import React, { useState, useEffect, useMemo, useRef } from "react";
import LoadingOverlay from "./LoadingOverlay";
import "./Startassessment.css";
import restart from "../assets/restart.png";
import Frame from "../assets/Frame.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBookOpenReader, faCopy, faDownload, faLink, faStar, faTriangleExclamation, faLocationDot, faSliders, faCircleExclamation, faBullseye, faCaretDown, faCaretUp, faCheck, faDotCircle, faArrowLeft, faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { useNavigate, useLocation } from "react-router-dom";
import infoIcon from "../assets/infoicon1.png";
import rightarrow from "../assets/rightarrow.png";
import { FaInfo, FaTimes, FaFileAlt, FaFolder } from "react-icons/fa";
import { paginateCanvasToPdf } from "./pdfBreakHelper";
import RichText from "./RichText";
import Toast from "./Toast";
import {
    pickInheritanceSession,
    orderHistoryGroups,
    formatHistoryAnswer,
    withAppointmentPrefix,
} from "./assessmentHistory";
import { API_BASE } from "../apiConfig";

// ---------------------------------------------------------------------------
// NETWORK / TIMEOUT UTILITIES
// ---------------------------------------------------------------------------

// All network calls in this file go through this wrapper so that a slow
// or dropped connection is detected quickly instead of hanging forever.
// A rejected request is then VERIFIED against the real network (see below)
// and only a CONFIRMED connection problem raises the "Network Issue" popup
// in the component (via globalNetworkErrorHandler, registered on mount).
// A merely slow API or a server-side error therefore never claims the
// therapist's internet is down.
const API_TIMEOUT_MS = 10000;

let globalNetworkErrorHandler = null;

// ---------------------------------------------------------------------------
// "NETWORK ISSUE" POPUP - connection problems ONLY
// ---------------------------------------------------------------------------
// Identical rule to start-assessment.js - keep both copies in sync.
//
// The popup used to be raised straight from the error type, which produced
// constant false positives:
//
//   1. AbortError -> network issue. That abort is OUR OWN 10s timeout, so any
//      merely SLOW API raised "Network Issue" while the connection was fine.
//   2. TypeError + navigator.onLine === false - the onLine flag is a guess
//      about the device interfaces, not proof this request failed for
//      connectivity reasons.
//
// A rejected request is now VERIFIED against the real network before any popup.
// The popup is an INTERNET problem popup and NOTHING else, so verification
// looks only at connectivity that can be lost independently of this app: the
// device's own offline flag, the API host, and SEVERAL always-up internet
// hosts (one CDN can be blocked or filtered on its own, so a single gstatic
// probe is not enough to call it an internet problem).
//
//   - navigator says every interface is down        -> internet issue.
//   - the API host answers (no-cors: any response
//     counts, CORS headers can never make it lie)   -> not an internet issue.
//   - ANY always-up internet host answers           -> not an internet issue.
//   - none of the above answers                     -> internet issue.
//
// A slow API, a 500, a CORS error, a PHP error or a down API server can never
// take every one of these probes down, so none of them can raise this popup.
const NETWORK_PROBE_TIMEOUT_MS = 5000;
// One popup per 5s window: a burst of failing requests must still produce ONE
// popup, not a stack of them.
const NETWORK_POPUP_COOLDOWN_MS = 5000;

let networkCheckInFlight = false;
let lastNetworkPopupAt = 0;

const probeOnce = (url, timeout, mode = "no-cors") => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);
    // Resolving in EITHER case (connected / refused) with the boolean keeps the
    // probe itself from ever throwing.
    return fetch(url, { method: "GET", cache: "no-store", mode, signal: controller.signal })
        .then(
            () => { clearTimeout(timeoutId); return true; },
            () => { clearTimeout(timeoutId); return false; }
        );
};

const INTERNET_PROBE_HOSTS = [
    "https://www.gstatic.com/generate_204",
    "https://connectivitycheck.gstatic.com/generate_204",
    "https://www.cloudflare.com/generate_204",
];

const confirmNetworkIssue = async () => {
    // The OS itself reports no connectivity - no probe can contradict that.
    if (typeof navigator !== "undefined" && navigator.onLine === false) return true;

    // Both probe sets run together: one round trip, one verdict.
    const [apiReachable, internetResults] = await Promise.all([
        probeOnce(`${API_BASE}/`, NETWORK_PROBE_TIMEOUT_MS, "no-cors"),
        Promise.all(
            INTERNET_PROBE_HOSTS.map((url) => probeOnce(url, NETWORK_PROBE_TIMEOUT_MS, "no-cors"))
        ),
    ]);

    if (apiReachable) return false;
    if (internetResults.some((reachable) => reachable)) return false;
    return true;
};

// Called from every failed request. The check is single-flight (concurrent
// failures share one probe) and the popup itself is rate limited.
const reportPossibleNetworkIssue = async () => {
    if (networkCheckInFlight || !globalNetworkErrorHandler) return;
    networkCheckInFlight = true;
    try {
        const isRealNetworkIssue = await confirmNetworkIssue();
        if (!isRealNetworkIssue) return;
        if (Date.now() - lastNetworkPopupAt < NETWORK_POPUP_COOLDOWN_MS) return;
        lastNetworkPopupAt = Date.now();
        if (globalNetworkErrorHandler) globalNetworkErrorHandler();
    } finally {
        networkCheckInFlight = false;
    }
};

// Only errors that mean "this request never completed" are worth verifying.
// Anything else (a bad JSON body, a bug in this file) is not a network
// question at all.
const isFetchConnectivityError = (error) =>
    Boolean(error) && (error.name === "AbortError" || error instanceof TypeError);

const fetchWithTimeout = async (url, options = {}, timeout = API_TIMEOUT_MS) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);
    try {
        const response = await fetch(url, { ...options, signal: controller.signal });
        clearTimeout(timeoutId);
        return response;
    } catch (error) {
        clearTimeout(timeoutId);
        if (isFetchConnectivityError(error)) reportPossibleNetworkIssue();
        throw error;
    }
};

const dispatchSessionExpired = () => {
    window.dispatchEvent(new CustomEvent("session-expired"));
};

const isTokenError = (json) => {
    if (!json || json.status !== false || !json.message) return false;
    const msg = json.message.toLowerCase();
    return (msg.includes("invalid") && msg.includes("token"))
        || (msg.includes("expired") && msg.includes("token"))
        || msg.includes("token already logged out")
        || msg.includes("token missing");
};

// ---------------------------------------------------------------------------
// GOAL-DOMAIN QUESTION DETECTION
// The Goal Domain question may be titled either "Domain of Goal" or
// "Domain of the New Goal". Both should trigger the goal-domain behaviour
// (checkbox, max 2, single-choice-only "Eating and Drinking").
// ---------------------------------------------------------------------------

const isGoalDomainQuestion = (q) => {
    const text = String(q?.question_text || q?.question || "").toLowerCase();
    const keyword = String(q?.keywords || "").toLowerCase();
    return keyword.includes("goal domain")
        || text.includes("domain of the new goal")
        || text.includes("domain of goal");
};

// ---------------------------------------------------------------------------
// API UTILITIES ? auto-save every answer using Review-specific APIs.
// Uses save_review_response.php and save_review_hine_hai.php (separate
// from the Initial pathway endpoints) so Review data is fully isolated.
// ---------------------------------------------------------------------------

const saveResponseAPI = async (sessionId, node, entity, entityId, answer = null, answers = null) => {
    if (!sessionId) { console.warn("Cannot save response: No Session ID"); return; }
    const token = localStorage.getItem("token");
    const url = `${API_BASE}/therapist_api/save_review_response.php`;
    const payload = { sessionId: parseInt(sessionId), node, entity, entityId: parseInt(entityId) };
    if (answers) { payload.answers = answers.map(id => parseInt(id)); } else { payload.answer = answer; }
    try {
        const response = await fetchWithTimeout(url, { method: "POST", headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` }, body: JSON.stringify(payload) });
        const json = await response.json();
        if (json.status) { console.log(`? Review Response Saved Node [${node}]:`, json.message); retryPendingResponses(); }
        else {
            console.error(`? Failed to save Review Response Node [${node}]:`, json.message);
            if (isTokenError(json)) { dispatchSessionExpired(); return; }
        }
    } catch (error) { console.error("Network error saving Review response:", error.message); queuePendingResponse(payload); }
};

const saveHineHaiAPI = async (sessionId, node, type, questionId, value, entries = []) => {
    if (!sessionId) { console.warn("Cannot save Review HINE/HAI: No Session ID"); return; }
    const token = localStorage.getItem("token");
    const url = `${API_BASE}/therapist_api/save_review_hine_hai.php`;
    const payload = { sessionId: parseInt(sessionId), node, type, questionId: parseInt(questionId), value };
    if (value === "Yes" && entries.length > 0) { payload.entries = entries; }
    try {
        const response = await fetchWithTimeout(url, { method: "POST", headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` }, body: JSON.stringify(payload) });
        const json = await response.json();
        if (json.status) { console.log(`? Review ${type.toUpperCase()} Saved Node [${node}]:`, json.message); }
        else {
            console.error(`? Failed to save Review ${type.toUpperCase()} Node [${node}]:`, json.message);
            if (isTokenError(json)) { dispatchSessionExpired(); return; }
        }
    } catch (error) { console.error("Network error saving Review HINE/HAI:", error.message); queuePendingResponse(payload); }
};

// ---------------------------------------------------------------------------
// NETWORK RESILIENCE ? queue failed requests & retry when possible
// ---------------------------------------------------------------------------

const PENDING_RESPONSES_KEY = "pendingResponses";

const queuePendingResponse = (payload) => {
    try {
        const pending = JSON.parse(localStorage.getItem(PENDING_RESPONSES_KEY) || "[]");
        pending.push({ payload, timestamp: Date.now() });
        localStorage.setItem(PENDING_RESPONSES_KEY, JSON.stringify(pending));
    } catch (e) { console.error("Failed to queue pending response:", e); }
};

const retryPendingResponses = async () => {
    let pending;
    try { pending = JSON.parse(localStorage.getItem(PENDING_RESPONSES_KEY) || "[]"); } catch { return; }
    if (!pending.length) return;
    const token = localStorage.getItem("token");
    if (!token) return;
    const remaining = [];
    for (const item of pending) {
        try {
            const response = await fetchWithTimeout(
                `${API_BASE}/therapist_api/save_response.php`,
                { method: "POST", headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` }, body: JSON.stringify(item.payload) }
            );
            const json = await response.json();
            if (json.status) console.log(`? Retried Node [${item.payload.node}]`);
            else {
                if (isTokenError(json)) { dispatchSessionExpired(); return; }
                remaining.push(item);
            }
        } catch { remaining.push(item); }
    }
    localStorage.setItem(PENDING_RESPONSES_KEY, JSON.stringify(remaining));
};

// ---------------------------------------------------------------------------
// HELPERS
// ---------------------------------------------------------------------------

const getQuestionType = (question) => {
    if (!question) return { isHINE: false, isHAI: false };
    if (question.type) {
        const t = question.type.toString().toUpperCase().trim();
        return { isHINE: t === "HINE", isHAI: t === "HAI" };
    }
    const instructionString = question.instruction || "";
    const result = { isHINE: false, isHAI: false };
    if (!instructionString) return result;
    if (instructionString.trim().startsWith("[")) {
        try {
            const parsed = JSON.parse(instructionString);
            if (Array.isArray(parsed)) {
                parsed.forEach(item => {
                    if (typeof item === "string") {
                        const lower = item.trim().toLowerCase();
                        if (lower === "hine") result.isHINE = true;
                        if (lower === "hai") result.isHAI = true;
                    }
                });
                return result;
            }
        } catch { /* fall through */ }
    }
    const parts = instructionString.split(",").map(s => s.trim().toLowerCase());
    if (parts.includes("hine")) result.isHINE = true;
    if (parts.includes("hai")) result.isHAI = true;
    return result;
};

const parseInstructions = (instructionString) => {
    const instructions = { isMulti: false, isVertical: false, isHINE: false, isHAI: false };
    if (!instructionString) return instructions;
    if (instructionString.trim().startsWith("[")) {
        try {
            const parsedArray = JSON.parse(instructionString);
            if (Array.isArray(parsedArray)) {
                parsedArray.forEach(item => {
                    if (typeof item === "string") {
                        const lowerItem = item.trim().toLowerCase();
                        if (lowerItem === "multi") instructions.isMulti = true;
                        if (lowerItem === "vertical") instructions.isVertical = true;
                        if (lowerItem === "hine") instructions.isHINE = true;
                        if (lowerItem === "hai") instructions.isHAI = true;
                    }
                });
                return instructions;
            }
        } catch { /* fall through */ }
    }
    const parts = instructionString.split(",").map(s => s.trim().toLowerCase());
    if (parts.includes("multi")) instructions.isMulti = true;
    if (parts.includes("vertical")) instructions.isVertical = true;
    if (parts.includes("hine")) instructions.isHINE = true;
    if (parts.includes("hai")) instructions.isHAI = true;
    return instructions;
};

const parseSubOptionLimit = (instruction) => {
    if (!instruction) return 1;
    const match = instruction.match(/select\s+(\d+)\s+options/i);
    if (match && match[1]) return parseInt(match[1]);
    return 1;
};

const instructionHasEnd = (instructionString) => {
    if (!instructionString) return false;
    if (instructionString.trim().startsWith("[")) {
        try {
            const parsed = JSON.parse(instructionString);
            if (Array.isArray(parsed)) {
                return parsed.some(
                    (item) => typeof item === "string" && item.trim().toLowerCase() === "end"
                );
            }
        } catch { /* fall through to comma-separated parse */ }
    }
    const parts = instructionString.split(",").map((s) => s.trim().toLowerCase());
    return parts.includes("end");
};

const hasEndInstructionDeep = (obj) => {
    if (!obj || typeof obj !== "object") return false;
    if (instructionHasEnd(obj.instruction)) return true;
    return [
        obj.options,
        obj.sub_options,
        obj.sub_questions,
        obj.questions,
        obj.groups,
    ].some((arr) => Array.isArray(arr) && arr.some(hasEndInstructionDeep));
};

// Given the current question and the user's answers, follow the *selected*
// option / sub-option chain and report whether any answered node carries the
// "End" instruction. Sub-questions are separate steps, so they are reached
// as their own question and checked by handleNext when it is their turn.
const selectedChainHasEnd = (question, answers, visited = new Set()) => {
    if (!question) return false;
    if (instructionHasEnd(question.instruction)) return true;
    const qNode = question.node_number;
    if (visited.has(qNode)) return false;
    visited.add(qNode);

    const ans = answers[qNode];
    if (!ans) return false;
    const selectedTexts = Array.isArray(ans) ? ans : [ans];

    const walk = (options, texts, pathTexts) => {
        for (const opt of options) {
            if (!texts.includes(opt.option_text)) continue;
            if (instructionHasEnd(opt.instruction)) return true;
            if (opt.sub_options && opt.sub_options.length > 0) {
                const nestedKey = [qNode, ...pathTexts, opt.option_text, "sub"].join("_");
                const nestedAns = answers[nestedKey];
                if (nestedAns) {
                    const nestedTexts = Array.isArray(nestedAns) ? nestedAns : [nestedAns];
                    if (walk(opt.sub_options, nestedTexts, [...pathTexts, opt.option_text])) return true;
                }
            }
        }
        return false;
    };

    return walk(question.options || [], selectedTexts, []);
};

// ---------------------------------------------------------------------------
// COMPONENT
// ---------------------------------------------------------------------------
export default function ReviewStartassessment() {
    const location = useLocation();
    const navigate = useNavigate();
    const urlParams = new URLSearchParams(location.search);
    const sessionId = urlParams.get("sessionId");
    // -- Carry-over from goalreview "Continue Pathway": the Review / Client /
    // Age answers are passed as query params so the first three questions are
    // pre-filled and the age-based Review pathway questions start. ----------
    const fromGoalReview = urlParams.get("fromGoalReview") === "1";
    const urlClient = urlParams.get("client");
    const urlAge = urlParams.get("age");
    const urlClientId = urlParams.get("clientId");
    // -- Review Smart Goal can be launched onto this page as well as
    // /start-assessment. ?appointment= tells us which review mode this is, so
    // the pathway, the session we create and the exit navigation all agree.
    const urlSmartGoal = urlParams.get("appointment") === "Review Smart Goal";
    const urlGoalSessionId = urlParams.get("goalSessionId") || urlParams.get("goalSession") || null;
    // -- Default the "Appointment" question to Review, as requested -----------
    // ...unless the caller explicitly asked for Review Smart Goal.
    const [session, setSession] = useState(urlSmartGoal ? "Review Smart Goal" : "Review");
    const isSmartGoalMode = session === "Review Smart Goal";
    // The value written to the server. Both review modes act on an existing
    // client, so they share the create/resume endpoints; the appointment type
    // is the only thing that keeps a Review Smart Goal session distinct from a
    // plain Review one, and it has to be correct on session create AND on
    // Save-for-Later (which rewrites the step-1 answer).
    const wireAppointmentType = isSmartGoalMode ? "review smart goal" : "review";
    const [client, setClient] = useState(fromGoalReview ? (urlClient || "") : "");
    const [ageRange, setAgeRange] = useState(fromGoalReview ? (urlAge || "") : "");
    const [fev, setfevo] = useState("");
    const [showInfantInfo, setShowInfantInfo] = useState(false);
    const [infantAnnouncement, setInfantAnnouncement] = useState("");
    const [pathwaysData, setPathwaysData] = useState([]);
    const [activePathway, setActivePathway] = useState(null);
    const [currentSessionId, setCurrentSessionId] = useState(null);
    const [isRestoringDraft, setIsRestoringDraft] = useState(false);
    const [toast, setToast] = useState(null);

    // -- Review clients (from get_review_pathway_details.php client mode) ?
    // real clients with their review sessions, age groups and client_id.
    const [reviewClients, setReviewClients] = useState([]);

    // -- Group History Panel state ----------------------------------------
    // historical data for the selected client (get_historical_pathway_data.php)
    const [groupHistory, setGroupHistory] = useState(null);
    const [historyGroupName, setHistoryGroupName] = useState(null);
    const [loadingGroupHistory, setLoadingGroupHistory] = useState(false);
    const [showHistoryFiles, setShowHistoryFiles] = useState(false);

    // -- NEW: Initial assessment data for the selected client (fetched once so
    // the review form can be pre-filled with the initial answers for HINE/HAI
    // and Current Skills / Comorbidities). -----------------------------------
    const [initialHistory, setInitialHistory] = useState(null);

    // -- NEW: Controls the "Goal Progress Update" screen shown after Next is
    // clicked for a Review session (matches the supplied design) -------------
    const [showReviewGoalScreen, setShowReviewGoalScreen] = useState(false);

    const targetNodeRef = useRef(null);
    const historyViewRef = useRef(null);

    const [answers, setAnswers] = useState({});
    const isMobile = window.innerWidth <= 768;

    const [hineData, setHineData] = useState([
        { id: Date.now(), timepoint: "", hineScore: 0, rightAsym: 0, leftAsym: 0 }
    ]);
    const [haiData, setHaiData] = useState([
        { id: Date.now(), timepoint: "", rightEahs: 0, leftEahs: 0, asymmetryIndex: 0, eachHandSumScore: 0 }
    ]);

    const [interventionsData, setInterventionsData] = useState([]);
    const [activeGoalTab, setActiveGoalTab] = useState(null);

    const [showLeftPanel, setShowLeftPanel] = useState(false);
    const [step, setStep] = useState(1);
    const [isGoalDetailStep, setIsGoalDetailStep] = useState(false);
    const [showGoalDomainPopup, setShowGoalDomainPopup] = useState(false);
    const [accordionOpen, setAccordionOpen] = useState({});
    const [isFavourite, setIsFavourite] = useState(false);
    const [openSections, setOpenSections] = useState({ demographics: false, background: false, comorbidities: false });

    // -- NEW: Share modal state ------------------------------------------------
    const [showShareModal, setShowShareModal] = useState(false);
    const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
    const [isDownloadingGroupData, setIsDownloadingGroupData] = useState(false);
    const summaryRef = useRef(null);
    const groupHistoryPrintRef = useRef(null);

    // -- NEW: Network issue popup state ----------------------------------------
    const [networkError, setNetworkError] = useState(false);

    const token = localStorage.getItem("token");
    const activeStepRef = useRef(null);

    // -- NEW: Register this component so any API call (including the ones in
    // saveResponseAPI / saveHineHaiAPI outside the component) can surface a
    // network-issue popup immediately when a request times out or fails ------
    useEffect(() => {
        globalNetworkErrorHandler = () => setNetworkError(true);
        return () => { globalNetworkErrorHandler = null; };
    }, []);

    // -- On a real browser refresh send the user back to start-assessment.
    // beforeunload only fires on a true page unload/reload, so React Router
    // navigation (e.g. goalreview "Continue Pathway") never triggers this. --
    useEffect(() => {
        if (sessionStorage.getItem("reviewAssessmentReloaded") === "1") {
            sessionStorage.removeItem("reviewAssessmentReloaded");
            navigate("/start-assessment");
            return;
        }
        const markReload = () => sessionStorage.setItem("reviewAssessmentReloaded", "1");
        window.addEventListener("beforeunload", markReload);
        return () => window.removeEventListener("beforeunload", markReload);
    }, [navigate]);

    // -- Persist state to localStorage ----------------------------------------
    useEffect(() => {
        const stateToSave = { session, client, ageRange, answers, hineData, haiData, currentSessionId, openSections, showReviewGoalScreen };
        localStorage.setItem("assessmentState", JSON.stringify(stateToSave));
    }, [session, client, ageRange, answers, hineData, haiData, currentSessionId, openSections, fev, showReviewGoalScreen]);

    // -- Restore state from localStorage (only when no URL sessionId) ---------
    useEffect(() => {
        const urlParams = new URLSearchParams(location.search);
        const urlSessionId = urlParams.get("sessionId");
        const fromGoalReview = urlParams.get("fromGoalReview") === "1";
        if (urlSessionId || fromGoalReview) {
            console.log("URL Session ID / fromGoalReview found ? skipping localStorage restore.");
            return;
        }
        const savedState = localStorage.getItem("assessmentState");
        if (savedState) {
            try {
                const parsed = JSON.parse(savedState);
                setSession(parsed.session || "Review");
                setClient(parsed.client || "");
                setAgeRange(parsed.ageRange || "");
                setIsFavourite(Boolean(parsed.is_favourite));
                setStep(1);
                setAnswers(parsed.answers || {});
                setHineData(parsed.hineData || [{ id: Date.now(), timepoint: "", hineScore: 0, rightAsym: 0, leftAsym: 0 }]);
                setHaiData(parsed.haiData || [{ id: Date.now(), timepoint: "", rightEahs: 0, leftEahs: 0, asymmetryIndex: 0, eachHandSumScore: 0 }]);
                setCurrentSessionId(parsed.currentSessionId || null);
                setOpenSections(parsed.openSections || { demographics: false, background: false, comorbidities: false });
                setShowReviewGoalScreen(Boolean(parsed.showReviewGoalScreen));
            } catch (e) { console.error("Error parsing saved state", e); }
        }
    }, [location.search]);

    // -- Carry-over from goalreview "Continue Pathway": pre-fill the three
    // step-1 answers (1. Review, 2. Client, 3. Age group) from the URL params
    // so the therapist doesn't re-answer them. Runs on mount before the async
    // draft restore; for a URL sessionId the draft restore then refines the
    // step (last_node) while keeping the carried-over client/age. --
    useEffect(() => {
        if (!fromGoalReview) return;
        setSession("Review");
        if (urlClient) setClient(urlClient);
        if (urlAge) setAgeRange(urlAge);
        setShowReviewGoalScreen(false);
        setOpenSections(prev => ({ ...prev, demographics: true }));
    }, [location.search, fromGoalReview, urlClient, urlAge]);

    // -- Fetch draft from API when sessionId is in URL ------------------------
    useEffect(() => {
        const urlParams = new URLSearchParams(location.search);
        const sessionId = urlParams.get("sessionId");
        if (!sessionId || !token) return;

        const fetchDraft = async () => {
            setIsRestoringDraft(true);
            try {
                const response = await fetchWithTimeout(
                    `${API_BASE}/therapist_api/get_draft_session_detail.php`,
                    {
                        method: "POST",
                        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
                        body: JSON.stringify({ sessionId: parseInt(sessionId) })
                    }
                );
                const json = await response.json();
                if (!json.status || !json.data) {
                    if (isTokenError(json)) { dispatchSessionExpired(); return; }
                    return;
                }

                const draftData = json.data;

                setCurrentSessionId(draftData.sessionId);

                // When arriving from goalreview "Continue Pathway", the
                // carried-over client/age params are authoritative (the draft's
                // age is the client's stored age_group, which can differ from
                // the review pathway's age group).
                const carryClient = fromGoalReview && urlClient ? urlClient : (draftData.client_name || "");
                const carryAge = fromGoalReview && urlAge ? urlAge : (draftData.age || "");
                setClient(carryClient);
                setAgeRange(carryAge);
                setfevo(draftData.is_favourite || "");
                setIsFavourite(Boolean(draftData.is_favourite));

                // Resolve the exact mode. The `=== "initial" ? ... : "Review"`
                // form this used collapsed every non-initial session to "Review",
                // so resuming a Review Smart Goal draft silently downgraded the
                // mode. get_draft_session_detail.php already returns the precise
                // appointment_key / appointment_label, so read those first and
                // keep the loose checks only as a fallback.
                let sessionType = "Initial";
                const modeKey = String(draftData.appointment_key || "").trim().toLowerCase();
                const modeLabel = String(draftData.appointment_label || "").trim();
                if (modeKey === "review_smart_goal" || /smart/i.test(modeLabel)) {
                    sessionType = "Review Smart Goal";
                } else if (modeKey === "review") {
                    sessionType = "Review";
                } else if (modeKey === "initial") {
                    sessionType = "Initial";
                } else if (draftData.appointment_type) {
                    const raw = String(draftData.appointment_type).toLowerCase();
                    sessionType = raw === "initial" ? "Initial"
                        : raw === "review smart goal" ? "Review Smart Goal"
                        : "Review";
                } else if (draftData.appt_id !== undefined) {
                    sessionType = draftData.appt_id === 1 ? "Initial" : "Review";
                }
                setSession(sessionType);

                // Resuming an existing REVIEW session keeps the step-1 static
                // questions visible (1. Review, 2. Client, 3. Age group) with
                // the carried-over values pre-filled. The last_node effect
                // below overrides the step when resuming a partially completed
                // review.
                if (sessionType === "Review" || sessionType === "Review Smart Goal") {
                    setShowReviewGoalScreen(false);
                }

                // -- When arriving from goalreview "Continue Pathway", skip
                // restoring saved pathway answers from the draft so that
                // comorbidities / current-skills data does NOT appear before
                // the user answers the 4th question. The pre-fill effect
                // will re-apply Initial data progressively as the user
                // advances through the review questions. --
                if (!fromGoalReview) {

                const newAnswers = {};
                const draftPathway = draftData.pathway || [];
                let restoredHineData = [];
                let restoredHaiData = [];

                const normaliseAnswer = (raw) => {
                    if (raw && typeof raw === "object" && !Array.isArray(raw)) {
                        return {
                            value: raw.value ?? null,
                            entries: Array.isArray(raw.entries) ? raw.entries : []
                        };
                    }
                    if (Array.isArray(raw) && raw.length > 0 && typeof raw[0] === "object") {
                        return { value: "Yes", entries: raw };
                    }
                    if (raw !== null && raw !== undefined && !Array.isArray(raw)) {
                        return { value: String(raw), entries: [] };
                    }
                    return { value: null, entries: [] };
                };

                const restoreSubOptions = (node, pathLabels, suboptions, parentInstruction = "") => {
                    if (!suboptions || suboptions.length === 0) return;

                    const instructions = parseInstructions(parentInstruction);
                    const isMulti = instructions.isMulti;

                    const selectedSubs = suboptions.filter(sub => sub.selected === true);
                    if (selectedSubs.length === 0) return;

                    const subKey = [node, ...pathLabels, "sub"].join("_");

                    if (isMulti) {
                        newAnswers[subKey] = selectedSubs.map(s => s.label);
                    } else {
                        if (selectedSubs.length === 1) {
                            newAnswers[subKey] = selectedSubs[0].label;
                        } else {
                            newAnswers[subKey] = selectedSubs.map(s => s.label);
                        }
                    }

                    selectedSubs.forEach(sub => {
                        restoreSubOptions(node, [...pathLabels, sub.label], sub.suboptions, sub.instruction || "");
                    });
                };

                draftPathway.forEach(p => {
                    const node = p.node;

                    const qTypeRaw = (p.type || "").toString().toUpperCase().trim();
                    const isHINEQuestion = qTypeRaw === "HINE";
                    const isHAIQuestion = qTypeRaw === "HAI";

                    const { value: answerValue, entries } = normaliseAnswer(p.answer);

                    if (isHINEQuestion) {
                        if (entries.length > 0) {
                            restoredHineData = entries.map((item, idx) => ({
                                id: idx,
                                timepoint: item.timepoint !== undefined ? item.timepoint : "",
                                hineScore: item.hine_score !== undefined ? item.hine_score : 0,
                                rightAsym: item.right_asymmetry !== undefined ? item.right_asymmetry : 0,
                                leftAsym: item.left_asymmetry !== undefined ? item.left_asymmetry : 0
                            }));
                        }
                        if (answerValue) newAnswers[node] = answerValue;
                        return;
                    }

                    if (isHAIQuestion) {
                        if (entries.length > 0) {
                            restoredHaiData = entries.map((item, idx) => ({
                                id: idx,
                                timepoint: item.timepoint !== undefined ? item.timepoint : "",
                                rightEahs: item.right_score !== undefined ? item.right_score : 0,
                                leftEahs: item.left_score !== undefined ? item.left_score : 0,
                                eachHandSumScore: item.sum !== undefined ? item.sum : 0,
                                asymmetryIndex: item.asymmetry !== undefined ? item.asymmetry : 0
                            }));
                        }
                        if (answerValue) newAnswers[node] = answerValue;
                        return;
                    }

                    if (entries.length > 0) {
                        const first = entries[0];
                        if (Object.prototype.hasOwnProperty.call(first, "hine_score")) {
                            restoredHineData = entries.map((item, idx) => ({
                                id: idx,
                                timepoint: item.timepoint !== undefined ? item.timepoint : "",
                                hineScore: item.hine_score !== undefined ? item.hine_score : 0,
                                rightAsym: item.right_asymmetry !== undefined ? item.right_asymmetry : 0,
                                leftAsym: item.left_asymmetry !== undefined ? item.left_asymmetry : 0
                            }));
                            if (answerValue) newAnswers[node] = answerValue;
                            return;
                        }
                        if (Object.prototype.hasOwnProperty.call(first, "right_score")) {
                            restoredHaiData = entries.map((item, idx) => ({
                                id: idx,
                                timepoint: item.timepoint !== undefined ? item.timepoint : "",
                                rightEahs: item.right_score !== undefined ? item.right_score : 0,
                                leftEahs: item.left_score !== undefined ? item.left_score : 0,
                                eachHandSumScore: item.sum !== undefined ? item.sum : 0,
                                asymmetryIndex: item.asymmetry !== undefined ? item.asymmetry : 0
                            }));
                            if (answerValue) newAnswers[node] = answerValue;
                            return;
                        }
                    }

                    if (p.options && Array.isArray(p.options)) {
                        const activeOptions = p.options.filter(opt =>
                            opt.selected === true ||
                            (opt.suboptions &&
                                Array.isArray(opt.suboptions) &&
                                opt.suboptions.some(sub => sub.selected === true))
                        );

                        // -- FIX: a single active option is always a radio-button (single
                        // choice) selection at this level, EXCEPT for the "domain of
                        // goal" question, which is the one genuinely multi (checkbox)
                        // question in the pathway. Previously this code stored the
                        // answer as an array whenever the API's `answer` field happened
                        // to be an array (e.g. ["Yes","Unilateral"], where "Unilateral"
                        // is really the answer for the sub-option, not a second main
                        // selection). That mismatched array vs string caused the main
                        // radio input (`selectedVal === opt.option_text`) to never match,
                        // so only the sub-option appeared selected even though the API
                        // returned "selected": true for the main option too. ----------
                        const goalDomainQuestion = isGoalDomainQuestion(p);

                        if (activeOptions.length === 1) {
                            const isAnswerArray = Array.isArray(p.answer);
                            newAnswers[node] = (goalDomainQuestion && isAnswerArray)
                                ? [activeOptions[0].label]
                                : activeOptions[0].label;

                            restoreSubOptions(node, [activeOptions[0].label], activeOptions[0].suboptions, activeOptions[0].instruction || "");
                        } else if (activeOptions.length > 1) {
                            newAnswers[node] = activeOptions.map(opt => opt.label);
                            activeOptions.forEach(opt =>
                                restoreSubOptions(node, [opt.label], opt.suboptions, opt.instruction || "")
                            );
                        }
                    }
                });

                setAnswers(newAnswers);
                if (restoredHineData.length > 0) setHineData(restoredHineData);
                if (restoredHaiData.length > 0) setHaiData(restoredHaiData);

                if (draftData.last_node) {
                    targetNodeRef.current = draftData.last_node;
                    console.log("?? Draft last_node stored:", draftData.last_node);
                }

                } // end if (!fromGoalReview)
            } catch (error) {
                console.error("Failed to fetch draft:", error.message);
            } finally {
                setIsRestoringDraft(false);
            }
        };

        fetchDraft();
    }, [location.search, token, fromGoalReview, urlClient, urlAge]);

    // -- Fetch pathway data ----------------------------------------------------
    // Scoped to THIS page's appointment type. /review-assessment only ever runs
    // Review or Review Smart Goal, and both the age-group dropdown below and the
    // pathway it hydrates must come from the Admin Module's rows for that exact
    // type - never from Initial's bands. get_pathway_details.php only narrows
    // when it is TOLD the appointment, so this body is what keeps the list
    // Admin-accurate (15 Initial / 9 Review / 2 RSG bands).
    useEffect(() => {
        const fetchPathways = async () => {
            try {
                const response = await fetchWithTimeout(
                    `${API_BASE}/therapist_api/get_pathway_details.php`,
                    {
                        method: "POST",
                        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
                        body: JSON.stringify({ appointment_type: wireAppointmentType })
                    }
                );
                const json = await response.json();
                if (json.status && json.data && Array.isArray(json.data)) {
                    setPathwaysData(json.data);
                } else if (json.status && json.pathways && Array.isArray(json.pathways)) {
                    setPathwaysData(json.pathways);
                } else {
                    if (isTokenError(json)) { dispatchSessionExpired(); return; }
                }
            } catch (error) { console.error("Fetch pathways failed:", error.message); }
        };
        if (token) fetchPathways();
    }, [token, wireAppointmentType]);

    // -- Fetch review clients (real client list with review sessions) ------
    useEffect(() => {
        const fetchReviewClients = async () => {
            if (!token) return;
            try {
                const response = await fetchWithTimeout(
                    `${API_BASE}/therapist_api/get_review_pathway_details.php`,
                    { method: "POST", headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` } }
                );
                const json = await response.json();
                if (json.status && Array.isArray(json.data)) setReviewClients(json.data);
                else if (isTokenError(json)) { dispatchSessionExpired(); return; }
            } catch (error) { console.error("Fetch review clients failed:", error.message); }
        };
        fetchReviewClients();
    }, [token]);

    // -- Map draft answers (ID ? text) once pathways are loaded ---------------
    useEffect(() => {
        if (pathwaysData.length === 0) return;
        let needsUpdate = false;
        const newAnswers = { ...answers };
        const nodeMap = {};
        pathwaysData.forEach(pData => {
            if (pData.pathways) {
                pData.pathways.forEach(p => {
                    if (p.groups) {
                        p.groups.forEach(g => {
                            const process = (qs) => {
                                qs.forEach(q => { if (q.options) nodeMap[q.node_number] = q.options; });
                            };
                            process(g.questions);
                        });
                    }
                });
            }
        });
        Object.keys(newAnswers).forEach(node => {
            const ans = newAnswers[node];
            if (Array.isArray(ans) && ans.length > 0 && typeof ans[0] === "number") {
                const options = nodeMap[node];
                if (options) {
                    const texts = ans.map(id => { const opt = options.find(o => o.option_id === id); return opt ? opt.option_text : id; }).filter(t => typeof t === "string");
                    if (texts.length > 0) { newAnswers[node] = texts; needsUpdate = true; }
                }
            } else if (typeof ans === "number") {
                const options = nodeMap[node];
                if (options) { const opt = options.find(o => o.option_id === ans); if (opt) { newAnswers[node] = opt.option_text; needsUpdate = true; } }
            }
        });
        if (needsUpdate) setAnswers(newAnswers);
        // answers is intentionally omitted: this only normalises legacy rows on
        // load, and re-running it on every answer change would rewrite state.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathwaysData]);

    // -- Set active pathway when ageRange changes ------------------------------
    useEffect(() => {
        if (ageRange && pathwaysData.length > 0) {
            const selectedAgeObj = pathwaysData.find(p => p.age_group === ageRange);
            if (selectedAgeObj && selectedAgeObj.pathways && selectedAgeObj.pathways.length > 0) {
                // Exactly one appointment type per mode: `review smart goal` for
                // Smart Goal, `review` otherwise. There is deliberately NO
                // cross-appointment fallback and no "whatever pathway this band
                // happens to have" fallback - appointment_type + age_group pick
                // one pathway or none, and none resolves to null (which is what
                // raises the missing-pathway popup).
                //
                // Keep this in sync with $pathwayTypes in
                // therapist_api/save_review_init.php / save_basic_details.php so
                // the clpa_id stored on the session matches what is rendered.
                const reviewPathway = selectedAgeObj.pathways.find(
                    p => (p.appointment_type || "").toLowerCase() === wireAppointmentType
                ) || null;
                setActivePathway(reviewPathway);
            } else {
                setActivePathway(null);
            }
        }
    }, [ageRange, pathwaysData, wireAppointmentType]);

    // -- NEW: Derived list of unique clients (from get_pathway_details.php),
    // each paired with the age group their record belongs to. Selecting a
    // client from the dropdown auto-fetches/derives their age group ---------
    const clientAgeOptions = useMemo(() => {
        const opts = [];
        const seen = new Set();
        reviewClients.forEach(c => {
            if (!c.client_name) return;
            // A Review Smart Goal session is a valid review session for this
            // page, so count both types. This used to be `=== "review"`, which
            // hid any client whose only review-style session was an RSG one.
            const reviewSessions = (c.sessions || []).filter(s => {
                const t = (s.appointment_type || s.type || "").toLowerCase();
                return t === "review" || t === "review smart goal";
            });
            if (reviewSessions.length === 0) return;
            const latest = reviewSessions[reviewSessions.length - 1];
            const name = c.client_name;
            if (!seen.has(name)) {
                seen.add(name);
                opts.push({
                    client_name: name,
                    client_id: c.client_id,
                    age_group: latest.age_group || "",
                    sessionId: latest.sessionId || null,
                    last_node: latest.last_node || null,
                });
            }
        });

        // -- Fallback for the carried-over client: when the client has no
        // review session yet (fresh Continue Pathway), they won't appear in
        // reviewClients. Add them so the pre-filled value displays in the
        // step-1 dropdown and save_review_init can use the carried client_id.
        if (fromGoalReview && urlClient && !seen.has(urlClient)) {
            opts.push({
                client_name: urlClient,
                client_id: urlClientId ? parseInt(urlClientId) : null,
                age_group: urlAge || "",
                sessionId: null,
                last_node: null,
            });
        }
        return opts;
    }, [reviewClients, fromGoalReview, urlClient, urlAge, urlClientId]);

    // client_id for the currently selected client (used by group history)
    const selectedClientId = useMemo(() => {
        const match = clientAgeOptions.find(o => o.client_name === client);
        return match ? match.client_id : null;
    }, [clientAgeOptions, client]);

    const handleClientSelect = (e) => {
        const selected = e.target.value;
        setClient(selected);
        const match = clientAgeOptions.find(o => o.client_name === selected);
        setAgeRange(match ? match.age_group : "");
    };

    // -- Group History Panel: fetch all prior Initial & Review sessions for
    // the selected client, then show the answers for the clicked group. -----
    const fetchGroupHistory = async (clientId, groupName) => {
        if (!clientId || !token) return;
        setLoadingGroupHistory(true);
        try {
            const response = await fetchWithTimeout(
                `${API_BASE}/therapist_api/get_historical_pathway_data.php`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
                    body: JSON.stringify({
                        client_id: parseInt(clientId),
                        group_name: groupName || "",
                    }),
                }
            );
            const json = await response.json();
            if (json.status) setGroupHistory(json);
            else if (isTokenError(json)) { dispatchSessionExpired(); return; }
        } catch (error) {
            console.error("Fetch group history failed:", error.message);
        } finally {
            setLoadingGroupHistory(false);
        }
    };

    const handleGroupHistoryClick = (groupName) => {
        const next = historyGroupName === groupName ? null : groupName;
        setHistoryGroupName(next);
        if (next && next !== historyGroupName) {
            const clientId = selectedClientId || (urlClientId ? parseInt(urlClientId) : null);
            fetchGroupHistory(clientId, next);
        }
    };

    // -- PREVIOUS DATA: "Previous Page" goes back one level WITHIN the view -
    // from an open group's tables to the group file bar. It must NOT call
    // handleBack: handleBack only moves the assessment step, so the click left
    // the therapist trapped in the history view on the same group - and on the
    // first review question it did nothing at all, because handleBack returns
    // early at step 2 to avoid dropping into the step-1 setup screen.
    const handleHistoryBack = () => {
        setHistoryGroupName(null);
    };

    // -- PREVIOUS DATA: "X" closes the view entirely and hands the assessment
    // area back to the questionnaire. Always available while the view is open,
    // including when only the group bar is showing, so it can never dead-end.
    const handleCloseHistory = () => {
        setHistoryGroupName(null);
        setShowHistoryFiles(false);
    };

    // -- NEW: Ordered group list for the file-icon history bar. Demographics
    // always comes first, followed by the remaining groups. Shared with
    // start-assessment.js so both pathways list them identically. ----------
    const orderedHistoryGroups = useMemo(
        () => orderHistoryGroups(initialHistory, activePathway?.groups),
        [initialHistory, activePathway]
    );

    // -- NEW: Toggle the file-icon "Previous Data" bar. When opening it, the
    // first group (Demographics) is auto-opened for immediate viewing. -------
    const handleHistoryFileClick = () => {
        const willShow = !showHistoryFiles;
        setShowHistoryFiles(willShow);
        if (willShow && orderedHistoryGroups.length > 0) {
            const firstGroup = orderedHistoryGroups[0].group_name;
            setHistoryGroupName(firstGroup);
            const clientId = selectedClientId || (urlClientId ? parseInt(urlClientId) : null);
            if (clientId) fetchGroupHistory(clientId, firstGroup);
        } else {
            setHistoryGroupName(null);
        }
    };

    // -- PREVIOUS DATA: true while the Previous Data view is on screen, whether
    // or not a group is open. The view replaces the question, so the assessment
    // footer (Back / Save for Later / Next) has to be hidden for the WHOLE view,
    // not only for an open group.
    const isHistoryPaneOpen = showHistoryFiles || historyGroupName;

    // -- NEW: Fetch the selected client's full assessment history once, so the
    // already-recorded Initial answers can be shown inline next to the review
    // questions for HINE/HAI and Current Skills / Comorbidities. -------------
    useEffect(() => {
        let cancelled = false;
        const clientId = selectedClientId || (urlClientId ? parseInt(urlClientId) : null);
        if (!clientId || !token) { setInitialHistory(null); return; }
        (async () => {
            try {
                const response = await fetchWithTimeout(
                    `${API_BASE}/therapist_api/get_historical_pathway_data.php`,
                    {
                        method: "POST",
                        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
                        body: JSON.stringify({ client_id: parseInt(clientId), group_name: "" }),
                    }
                );
                const json = await response.json();
                if (json.status && !cancelled) setInitialHistory(json);
                else if (!cancelled && isTokenError(json)) { dispatchSessionExpired(); }
            } catch (error) {
                console.error("Fetch initial history failed:", error.message);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedClientId, urlClientId, token]);

    // -- NEW: Map question text ? the answer carried over from the session we
    // inherit from, so review steps can show that data inline. HINE / HAI
    // answers carry { value, entries } where entries hold the score rows (only
    // when the inherited answer was "Yes").
    //
    // Data Continuity: a Review reads the client's LATEST session, minus the
    // session being answered and narrowed to its own age band.
    //
    // Review Smart Goal is different by definition: the therapist arrived from
    // a specific row of "Review Existing Goals", so ?goalSessionId names the
    // session that owns the goal being reviewed. That EXACT session is the
    // source - never a newer, unrelated one.
    const inheritSession = useMemo(() => {
        if (isSmartGoalMode && urlGoalSessionId) {
            // Exact session only - no fallback to "the latest session". A wrong
            // source here would pre-fill another session's history onto the RSG.
            // While the history payload is still loading this stays null and the
            // pre-fill effect simply waits for it.
            if (!Array.isArray(initialHistory?.data)) return null;
            const sessions = initialHistory.data;
            const wanted = String(urlGoalSessionId);
            return sessions.find(s => String(s?.session_id ?? s?.sessionId ?? "") === wanted) || null;
        }
        return pickInheritanceSession(initialHistory, currentSessionId, ageRange);
    }, [initialHistory, currentSessionId, ageRange, isSmartGoalMode, urlGoalSessionId]);

    const initialAnswerMap = useMemo(() => {
        const map = new Map();
        if (!inheritSession || !Array.isArray(inheritSession.pathway)) return map;
        inheritSession.pathway.forEach(q => {
            if (q.question) map.set(String(q.question).trim().toLowerCase(), q);
        });
        return map;
    }, [inheritSession]);

    // -- Keyword ? inherited answer map (fallback for HINE / HAI steps). --
    // The keyword alone is NOT unique: "Communication" is both a motor-skill
    // question (group 2) and a comorbidity (group 3). Every pathway uses the
    // same keyword for both, so the group index is part of the key. A plain
    // keyword key is kept as a fallback for questions with no usable node.
    const initialKeywordMap = useMemo(() => {
        const map = new Map();
        if (!inheritSession || !Array.isArray(inheritSession.pathway)) return map;
        inheritSession.pathway.forEach(q => {
            if (!q.keyword) return;
            const kw = String(q.keyword).trim().toLowerCase();
            const parts = String(q.node || "").split(".");
            if (parts.length > 1) map.set(parts[1] + "||" + kw, q);
            if (!map.has(kw)) map.set(kw, q);
        });
        return map;
    }, [inheritSession]);

    // -- Scroll the inline history into view when it opens (e.g. when the
    // History button in the left panel is clicked) -----------------------
    useEffect(() => {
        if (historyGroupName) {
            historyViewRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    }, [historyGroupName]);

    // -- Fetch interventions ---------------------------------------------------
    const fetchInterventions = async () => {
        if (!currentSessionId) return;

        setInterventionsData(null);
        setActiveGoalTab("");
        setAccordionOpen({});

        try {
            const response = await fetchWithTimeout(
                `${API_BASE}/therapist_api/get_test_intervention.php`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        session_id: parseInt(currentSessionId),
                    }),
                }
            );

            const json = await response.json();

            if (json.status) {
                setInterventionsData(json);
                const goalTabs = Object.keys(json.data || {}).map((key) => {
                    const items = json.data[key] || [];
                    const firstItem = items[0] || {};

                    const goalDomain = firstItem.goal_domain || "";
                    const goalSkill = firstItem.goal_skill || "";

                    const tabKey =
                        goalSkill && goalSkill !== "null"
                            ? goalSkill
                            : goalDomain;

                    const displayLabel =
                        goalSkill && goalSkill !== "null"
                            ? `${goalDomain} - ${goalSkill}`
                            : goalDomain;

                    return {
                        key,
                        tabKey,
                        goalDomain,
                        goalSkill,
                        displayLabel,
                    };
                });

                const uniqueGoalTabs = goalTabs.filter(
                    (tab, index, self) =>
                        index ===
                        self.findIndex((t) => t.tabKey === tab.tabKey)
                );

                if (uniqueGoalTabs.length > 0) {
                    const firstTab = uniqueGoalTabs[0];

                    setActiveGoalTab(firstTab.tabKey);

                    const firstTabInterventions =
                        json.data[firstTab.key] || [];

                    if (firstTabInterventions.length > 0) {
                        const initialOpen = {};
                        initialOpen[`int-${firstTabInterventions[0].id}`] = true;
                        if (firstTabInterventions.length > 1) {
                            initialOpen[`int-${firstTabInterventions[1].id}`] = true;
                        }
                        setAccordionOpen(initialOpen);
                    }
                }
            } else {
                if (isTokenError(json)) { dispatchSessionExpired(); return; }
            }
        } catch (error) {
            console.error(
                "Error fetching interventions:",
                error.message
            );
        }
    };

    // -- Derived: flat list of all questions ----------------------------------
    const allQuestionsFlat = useMemo(() => {
        const steps = [];
        if (activePathway && activePathway.groups) {
            activePathway.groups.forEach(group => {
                if (group.group_name.toLowerCase() === "demographics") return;

                // Recursively walk an arbitrarily deep chain of sub_options, and
                // whenever a sub_option (at any depth) carries its own sub_questions,
                // feed those sub_questions back into processOptions so they become
                // steps too (and so their own options/sub_options/sub_questions are
                // in turn walked the same way, to unlimited depth).
                function processSubOptionsForFlat(subOptions) {
                    if (!subOptions || subOptions.length === 0) return;
                    subOptions.forEach(subOpt => {
                        if (subOpt.sub_options && subOpt.sub_options.length > 0) {
                            processSubOptionsForFlat(subOpt.sub_options);
                        }
                        if (subOpt.sub_questions && subOpt.sub_questions.length > 0) {
                            processOptions(subOpt.sub_questions);
                        }
                    });
                }

                function processOptions(questions) {
                    questions.forEach(q => {
                        steps.push({ ...q, groupName: group.group_name, groupNode: group.node_number, groupInfo: group.group_info });
                        if (q.options && q.options.length > 0) {
                            q.options.forEach(opt => {
                                if (opt.sub_options && opt.sub_options.length > 0) {
                                    processSubOptionsForFlat(opt.sub_options);
                                }
                                if (opt.sub_questions && opt.sub_questions.length > 0) processOptions(opt.sub_questions);
                            });
                        }
                    });
                }

                processOptions(group.questions);
            });
        }
        return steps;
    }, [activePathway]);

    const groupNodeSet = useMemo(() => {
        const set = new Set();
        if (activePathway && activePathway.groups) activePathway.groups.forEach(g => set.add(g.node_number));
        return set;
    }, [activePathway]);

    const pathwayHasEndInstruction = useMemo(
        () => hasEndInstructionDeep(activePathway),
        [activePathway]
    );

    const visibleSteps = useMemo(() => {
        const selectedOptionNodes = new Set();

        // Walks an arbitrarily deep chain of sub_options belonging to question
        // `qNode`. `pathTexts` is the ordered list of selected option_text values
        // from the top-level option down to (but not including) the level being
        // processed here ? this mirrors the key format used by
        // handleChainSubOptionSelect / renderSubOptionsChain so the two stay in
        // sync no matter how deep the chain goes.
        function processSubOptionsChainForVisible(qNode, pathTexts, subOptions) {
            if (!subOptions || subOptions.length === 0) return;
            const chainKey = [qNode, ...pathTexts, "sub"].join("_");
            const chainAns = answers[chainKey];
            if (!chainAns) return;
            subOptions.forEach((subOpt) => {
                const isSelected = Array.isArray(chainAns) ? chainAns.includes(subOpt.option_text) : chainAns === subOpt.option_text;
                if (!isSelected) return;
                selectedOptionNodes.add(subOpt.node_number);
                if (subOpt.sub_options && subOpt.sub_options.length > 0) {
                    processSubOptionsChainForVisible(qNode, [...pathTexts, subOpt.option_text], subOpt.sub_options);
                }
                if (subOpt.sub_questions) processQuestions(subOpt.sub_questions);
            });
        }

        function processQuestions(questions) {
            if (!questions) return;
            questions.forEach((q) => {
                const ans = answers[q.node_number];
                if (!ans || !q.options) return;
                q.options.forEach((opt) => {
                    const isSelected = Array.isArray(ans) ? ans.includes(opt.option_text) : ans === opt.option_text;
                    if (!isSelected) return;
                    selectedOptionNodes.add(opt.node_number);
                    if (opt.sub_options && opt.sub_options.length > 0) {
                        processSubOptionsChainForVisible(q.node_number, [opt.option_text], opt.sub_options);
                    }
                    if (opt.sub_questions) processQuestions(opt.sub_questions);
                });
            });
        }

        processQuestions(allQuestionsFlat);

        return allQuestionsFlat.filter((q) => {
            if (groupNodeSet.has(q.parent_node)) return true;
            if (selectedOptionNodes.has(q.parent_node)) return true;
            return false;
        });
    }, [allQuestionsFlat, answers, groupNodeSet]);

    // -- NEW: Pre-fill the review questionnaire with the already-recorded
    // Initial assessment data ? auto-select the SAME option, carry over the
    // HINE/HAI score entries, and select sub-options too. Runs once per client.
    // After filling local state, auto-saves all pre-filled responses to the DB
    // via save_review_auto_responses.php so the data persists immediately.
    const prefilledClientRef = useRef(null);
    const prefilledDoneRef = useRef(false);
    useEffect(() => {
        const clientId = selectedClientId || (urlClientId ? parseInt(urlClientId) : null);
        if (prefilledClientRef.current !== clientId) {
            prefilledClientRef.current = clientId;
            prefilledDoneRef.current = false;
        }
        if (!clientId || !currentSessionId || step < 3 || initialHistory === null || visibleSteps.length === 0) return;
        if (prefilledDoneRef.current) return;

        const sourceSession = inheritSession;
        if (!sourceSession || !Array.isArray(sourceSession.pathway)) return;
        // Set only once a real source exists: with the flag consumed before this
        // guard, a source that arrived a moment later (history still loading, or
        // the exact ?goalSessionId row appearing late) could never pre-fill.
        prefilledDoneRef.current = true;

        const newAnswers = {};
        const hineRows = [];
        const haiRows = [];
        const autoSaveResponses = []; // collect responses for batch DB save

        // Walk the initial option tree and re-build the same chain keys that
        // renderSubOptionsChain / handleChainSubOptionSelect use, so the
        // selected sub-options show up inline on the review question.
        const fillChain = (qNode, initialOptions, ancestors) => {
            if (!Array.isArray(initialOptions)) return;
            initialOptions.forEach(opt => {
                if (!opt || !opt.selected) return;
                const children = Array.isArray(opt.suboptions) ? opt.suboptions : [];
                const selectedChildren = children.filter(c => c && c.selected);
                if (selectedChildren.length > 0) {
                    const subKey = [qNode, ...ancestors, opt.label, "sub"].join("_");
                    newAnswers[subKey] = selectedChildren.length === 1
                        ? (selectedChildren[0].answer ?? selectedChildren[0].label)
                        : selectedChildren.map(c => (c.answer ?? c.label));
                }
                selectedChildren.forEach(child => {
                    if (child.suboptions && child.suboptions.length > 0) {
                        fillChain(qNode, [child], [...ancestors, opt.label]);
                    }
                });
            });
        };

        // â”€â”€ Writing pre-filled SUB-options back to the review session â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
        //
        // fillChain() puts the selected sub-options into React state, so the
        // summary page can already display "Topography: Unilateral". Only the
        // TOP-LEVEL option was pushed onto autoSaveResponses, so a pre-filled
        // sub-option lived on screen and nowhere else: the review session stored
        // "Yes" with no child row, and /Interventions - which reads the session,
        // not the screen - came back with Topography: null and an empty
        // comorbidities list even though the therapist had answered correctly.
        //
        // get_historical_pathway_data.php labels its options with `node` but no
        // `option_id`, so the ids are indexed from the pathway definition itself
        // (allQuestionsFlat), which does carry them - that keeps the write
        // identical to what the click handler sends.
        const optionIdsByNode = {};
        const indexOptionTree = (opts) => {
            (opts || []).forEach(o => {
                if (!o) return;
                if (o.node_number && o.option_id != null) optionIdsByNode[String(o.node_number)] = o.option_id;
                if (o.sub_options && o.sub_options.length) indexOptionTree(o.sub_options);
            });
        };

        // One auto-save entry per selected sub-option, at its OWN node, exactly
        // as renderSubOptionsChain's click handler does. Multi-select levels
        // send `answers` (option ids) instead of a single `answer`.
        const saveSelectedSubOptions = (options) => {
            (options || []).forEach(o => {
                if (!o || !o.selected) return;
                const children = Array.isArray(o.suboptions) ? o.suboptions : [];
                const picked = children.filter(c => c && c.selected);
                if (picked.length === 0) return;

                const ids = picked.map(c => (c.node ? optionIdsByNode[String(c.node)] : null)).filter(id => id != null);
                if (ids.length === picked.length && ids.length > 0) {
                    if (ids.length === 1) {
                        autoSaveResponses.push({
                            node: String(picked[0].node),
                            entity: "option",
                            entityId: ids[0],
                            answer: String(picked[0].answer ?? picked[0].label ?? ""),
                        });
                    } else {
                        autoSaveResponses.push({
                            node: String(picked[0].node),
                            entity: "option",
                            entityId: ids[0],
                            answers: ids,
                        });
                    }
                }

                picked.forEach(child => {
                    if (child.suboptions && child.suboptions.length) saveSelectedSubOptions([child]);
                });
            });
        };

        allQuestionsFlat.forEach(q => {
            let match = q.question_text
                ? initialAnswerMap.get(String(q.question_text).trim().toLowerCase()) || null
                : null;
            if (!match && q.keywords) {
                const kw = String(q.keywords).trim().toLowerCase();
                const nodeGroup = String(q.node_number || "").split(".")[1] || "";
                if (nodeGroup) match = initialKeywordMap.get(nodeGroup + "||" + kw) || null;
                if (!match) match = initialKeywordMap.get(kw) || null;
            }
            if (!match) return;
            const grp = (q.groupName || "").toLowerCase();
            const isHineHai = match.type === "HINE" || match.type === "HAI";
            if (!isHineHai && !grp.includes("background") && !grp.includes("current skills") && !grp.includes("comorbid")) return;

            if (isHineHai) {
                if (answers[q.node_number] !== undefined) return;
                const value = match.answer?.value;
                if (value) newAnswers[q.node_number] = value;
                const entries = Array.isArray(match.answer?.entries) ? match.answer.entries : [];
                if (String(value || "").toLowerCase() === "yes" && entries.length > 0) {
                    if (match.type === "HINE") {
                        entries.forEach(e => hineRows.push({
                            id: Date.now() + Math.random(),
                            timepoint: e.timepoint || "",
                            hineScore: e.hine_score ?? 0,
                            rightAsym: e.right_asymmetry ?? 0,
                            leftAsym: e.left_asymmetry ?? 0,
                        }));
                    } else {
                        entries.forEach(e => haiRows.push({
                            id: Date.now() + Math.random(),
                            timepoint: e.timepoint || "",
                            rightEahs: e.right_score ?? 0,
                            leftEahs: e.left_score ?? 0,
                            eachHandSumScore: e.sum ?? 0,
                            asymmetryIndex: e.asymmetry ?? 0,
                        }));
                    }
                    // Collect for batch DB save
                    autoSaveResponses.push({
                        node: q.node_number,
                        entity: "question",
                        entityId: q.question_id || 0,
                        answer: value,
                        type: match.type.toLowerCase(),
                        entries: entries,
                    });
                } else {
                    // HINE/HAI with "No" ? still save it
                    autoSaveResponses.push({
                        node: q.node_number,
                        entity: "question",
                        entityId: q.question_id || 0,
                        answer: value || "No",
                        type: match.type.toLowerCase(),
                    });
                }
            } else {
                const ans = match.answer;
                if (ans !== null && ans !== undefined && ans !== "" && answers[q.node_number] === undefined) {
                    // The history API's `answer` for an answered option tree is
                    // the FLATTENED list of every selected label - the top-level
                    // option and its sub-options (e.g. ["Yes","Unilateral"]).
                    // Putting the whole array into the main answer renders the
                    // sub-option text twice ("Unilateral, Unilateral") and writes
                    // a mangled "Yes,Unilateral" string into the review session.
                    // Only top-level selected option(s) belong in the main answer;
                    // sub-options live in the `_sub` chain keys fillChain() sets.
                    let mainAnswer = ans;
                    const topLevelSelected = Array.isArray(match.options)
                        ? match.options.filter(o => o && o.selected).map(o => o.label)
                        : [];
                    if (topLevelSelected.length > 0) {
                        mainAnswer = topLevelSelected.length === 1 ? topLevelSelected[0] : topLevelSelected;
                    }
                    if (mainAnswer !== null && mainAnswer !== undefined && mainAnswer !== "") {
                        newAnswers[q.node_number] = mainAnswer;
                        // Collect for batch DB save
                        indexOptionTree(q.options);
                        const selectedOpt = (match.options || []).find(
                            o => String(o.label || "").toLowerCase() === String(mainAnswer || "").toLowerCase()
                        );
                        autoSaveResponses.push({
                            node: q.node_number,
                            entity: "option",
                            entityId: selectedOpt?.option_id || q.question_id || 0,
                            answer: String(mainAnswer),
                        });
                        // The sub-options fillChain() just put into state need the
                        // same treatment, otherwise the session keeps only "Yes".
                        saveSelectedSubOptions(match.options);
                    }
                }
                fillChain(q.node_number, match.options, []);
            }
        });

        setAnswers(prev => {
            const merged = { ...prev };
            let changed = false;
            Object.keys(newAnswers).forEach(k => {
                if (merged[k] === undefined) { merged[k] = newAnswers[k]; changed = true; }
            });
            return changed ? merged : prev;
        });
        const hineEmpty = hineData.length === 0 || (hineData.length === 1 && !hineData[0].timepoint && !hineData[0].hineScore);
        const haiEmpty = haiData.length === 0 || (haiData.length === 1 && !haiData[0].timepoint && !haiData[0].rightEahs);
        if (hineRows.length > 0 && hineEmpty) setHineData(hineRows);
        if (haiRows.length > 0 && haiEmpty) setHaiData(haiRows);

        // -- AUTO-SAVE pre-filled responses to DB --
        if (autoSaveResponses.length > 0) {
            const token = localStorage.getItem("token");
            fetchWithTimeout(
                `${API_BASE}/therapist_api/save_review_auto_responses.php`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
                    body: JSON.stringify({
                        sessionId: parseInt(currentSessionId),
                        responses: autoSaveResponses,
                    }),
                }
            )
            .then(res => res.json())
            .then(json => {
                if (json.status) console.log(`? Review: ${json.saved} pre-filled responses saved to DB`);
                else {
                    console.error("? Review auto-save failed:", json.message);
                    if (isTokenError(json)) { dispatchSessionExpired(); return; }
                }
            })
            .catch(err => console.error("Network error auto-saving pre-filled responses:", err.message));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentSessionId, step, allQuestionsFlat, visibleSteps, initialHistory, inheritSession, answers, hineData, haiData, selectedClientId, urlClientId]);

    // -- DRAFT RESTORE: calculate step from last_node once visibleSteps ready -
    useEffect(() => {
        const targetNode = targetNodeRef.current;
        if (!targetNode || visibleSteps.length === 0 || isRestoringDraft) return;

        console.log("?? Resolving draft last_node:", targetNode, "against", visibleSteps.length, "visible steps");

        const targetIndex = visibleSteps.findIndex(q => q.node_number === targetNode);

        if (targetIndex !== -1) {
            const calculatedStep = targetIndex + 2;
            setStep(calculatedStep);
            const targetQuestion = visibleSteps[targetIndex];
            if (targetQuestion && targetQuestion.groupName) {
                setOpenSections(prev => ({ ...prev, [targetQuestion.groupName]: true }));
            }
            console.log("? Draft restored to step:", calculatedStep, "node:", targetNode);
        } else {
            const lastIndex = visibleSteps.length - 1;
            if (lastIndex >= 0) {
                setStep(lastIndex + 2);
                const lastQ = visibleSteps[lastIndex];
                if (lastQ && lastQ.groupName) {
                    setOpenSections(prev => ({ ...prev, [lastQ.groupName]: true }));
                }
                console.warn("?? last_node not found in visibleSteps. Defaulted to last step:", lastIndex + 2);
            }
        }

        targetNodeRef.current = null;
    }, [visibleSteps, isRestoringDraft]);

    // -- When arriving from Goalreview "Continue Pathway", skip the step-1
    // basic-details form (those three answers were already captured) and show
    // a loading screen until the auto-advance below lands on the first
    // pathway question (the 4th question overall). The step-1 questions stay
    // reachable afterwards via the Demographics section in the left panel. --
    const [skipStep1, setSkipStep1] = useState(fromGoalReview);
    useEffect(() => {
        if (step > 1 && skipStep1) setSkipStep1(false);
    }, [step, skipStep1]);

    // -- Auto-advance to step 2 when coming from Goalreview "Continue Pathway" --
    // Skip the step-1 basic-details form since those answers (Review / Client /
    // Age) were already captured during the Initial assessment and carried over
    // via URL params. Create the review session if one doesn't exist yet, then
    // save the first 3 questions to the database and jump straight to the first
    // pathway question (the 4th question overall).
    const fromGoalReviewAutoAdvancing = useRef(false);
    useEffect(() => {
        if (!fromGoalReview) return;
        if (isRestoringDraft) return;
        if (!activePathway) return;
        if (step > 1) return;
        if (targetNodeRef.current) return;
        if (fromGoalReviewAutoAdvancing.current) return;

        fromGoalReviewAutoAdvancing.current = true;

        const saveFirst3AndAdvance = async () => {
            try {
                // Step A: Create the review session
                let sid = currentSessionId;
                if (!sid) {
                    const json = await handleSaveBasicDetails();
                    if (!json || !json.status) return;
                    sid = json.session_id || json.sessionId;
                }

                // Step B: Save first 3 questions (session type, client, age) to DB
                if (sid && client && ageRange) {
                    const token = localStorage.getItem("token");
                    const basicRes = await fetchWithTimeout(
                        `${API_BASE}/therapist_api/save_review_basic_details.php`,
                        {
                            method: "POST",
                            headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
                            body: JSON.stringify({
                                sessionId: parseInt(sid),
                                client_name: client,
                                age_group: ageRange,
                                appointment_type: session || "review"
                            })
                        }
                    );
                    const basicJson = await basicRes.json();
                    if (basicJson.status) {
                        console.log("? Review: First 3 questions saved to DB");
                    } else {
                        if (isTokenError(basicJson)) { dispatchSessionExpired(); return; }
                        console.error("? Failed to save review basic details:", basicJson.message);
                    }
                }

                setStep(2);
            } catch (err) {
                console.error("Error saving review basic details:", err.message);
            }
        };

        saveFirst3AndAdvance();
        // Step-1 answers (ageRange / client) and the session type are read once
        // when the draft restore finishes, which is what the listed deps track.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fromGoalReview, isRestoringDraft, activePathway, currentSessionId, step]);

    // -- Auto-open section when navigating normally ----------------------------
    useEffect(() => {
        if (targetNodeRef.current) return;
        if (step >= 2 && visibleSteps[step - 2]) {
            const currentQ = visibleSteps[step - 2];
            if (currentQ.groupName) {
                setOpenSections(prev => ({ ...prev, [currentQ.groupName]: true }));
            }
        }
    }, [step, visibleSteps]);

    // -- Auto-scroll to active step --------------------------------------------
    useEffect(() => {
        if (activeStepRef.current) {
            activeStepRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        }
    }, [step, visibleSteps]);

    // -- Trigger interventions fetch on final step -----------------------------
    // Keyed on step/session/questions only: fetchInterventions is a stable
    // wrapper and re-running on every `answers` change would refetch per click.
    useEffect(() => {
        const totalSteps = 1 + (visibleSteps ? visibleSteps.length : 0);
        if (step === totalSteps + 1 && currentSessionId) fetchInterventions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step, currentSessionId, visibleSteps, answers]);

    // -- Initialize accordion when interventions load --------------------------
    useEffect(() => {
        if (interventionsData && interventionsData.data && activeGoalTab && Object.keys(accordionOpen).length === 0) {
            const list = interventionsData.data[activeGoalTab];
            if (list && list.length > 0) {
                const initialOpen = {};
                initialOpen[`int-${list[0].id}`] = true;
                if (list.length > 1) {
                    initialOpen[`int-${list[1].id}`] = true;
                }
                setAccordionOpen(initialOpen);
            }
        }
        // accordionOpen is read as a guard, not tracked: including it would make
        // the effect self-cancelling (it sets accordionOpen itself).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [interventionsData, activeGoalTab]);

    // ---------------------------------------------------------------------------
    // ANSWER HELPERS
    // ---------------------------------------------------------------------------

    const clearDownstreamAnswers = (prevAnswers, currentNodeNumber) => {
        const newAnswers = { ...prevAnswers };
        const currentIndex = visibleSteps.findIndex(q => q.node_number === currentNodeNumber);
        if (currentIndex !== -1) {
            for (let i = currentIndex + 1; i < visibleSteps.length; i++) {
                const futureQ = visibleSteps[i];
                delete newAnswers[futureQ.node_number];
                delete newAnswers[`${futureQ.node_number}_sub`];
                Object.keys(prevAnswers).forEach(key => {
                    if (key.startsWith(`${futureQ.node_number}_`) && key !== `${futureQ.node_number}_sub`) delete newAnswers[key];
                });
            }
        }
        return newAnswers;
    };

    const EATING_AND_DRINKING_OPTION = "Eating and Drinking";

    const handleMainOptionSelect = (qNode, opt, isMulti = false, isGoalDomain = false) => {
        // -- "Eating and Drinking" can only be selected as a single choice in the
        // Goal Domain question: block (with a popup) any combination of it with
        // other domains. ---------------------------------------------------------
        if (isGoalDomain) {
            const currentSelections = Array.isArray(answers[qNode]) ? answers[qNode] : [];
            const isEatingDrinking = opt.option_text === EATING_AND_DRINKING_OPTION;
            if (!currentSelections.includes(opt.option_text)) {
                if ((isEatingDrinking && currentSelections.length > 0) ||
                    (!isEatingDrinking && currentSelections.includes(EATING_AND_DRINKING_OPTION))) {
                    setShowGoalDomainPopup(true);
                    return false;
                }
            }
        }
        setAnswers(prev => {
            let newAnswers = { ...prev };
            if (!isMulti) {
                Object.keys(prev).forEach(key => { if (key.startsWith(`${qNode}_`)) delete newAnswers[key]; });
                newAnswers[qNode] = opt.option_text;
            } else {
                // A multi-select question can hold a legacy single-string value
                // (e.g. saved while the question was still single-select). Treat
                // a string as a one-element selection so it is preserved when
                // the first additional option is ticked.
                const currentSelections = Array.isArray(prev[qNode])
                    ? [...prev[qNode]]
                    : (typeof prev[qNode] === "string" && prev[qNode] ? [prev[qNode]] : []);
                if (currentSelections.includes(opt.option_text)) {
                    newAnswers[qNode] = currentSelections.filter(s => s !== opt.option_text);
                    delete newAnswers[`${qNode}_${opt.option_text}_sub`];
                } else {
                    // -- CHANGE 1: Limit "domain of goal" checkboxes to max 2 --
                    if (isGoalDomain && currentSelections.length >= 2) {
                        return prev;
                    }
                    newAnswers[qNode] = [...currentSelections, opt.option_text];
                }
            }
            newAnswers = clearDownstreamAnswers(newAnswers, qNode);
            return newAnswers;
        });
        return true;
    };

    const handleSubOptionSelect = (qNode, parentOptText, subOpt, isMulti = false, limit = 1) => {
        setAnswers(prev => {
            let newAnswers = { ...prev };
            const key = `${qNode}_${parentOptText}_sub`;
            if (isMulti) {
                let currentSelections = Array.isArray(prev[key]) ? [...prev[key]] : [];
                if (currentSelections.includes(subOpt.option_text)) {
                    newAnswers[key] = currentSelections.filter(s => s !== subOpt.option_text);
                } else {
                    if (currentSelections.length < limit) newAnswers[key] = [...currentSelections, subOpt.option_text];
                }
            } else { newAnswers[key] = subOpt.option_text; }
            newAnswers = clearDownstreamAnswers(newAnswers, qNode);
            return newAnswers;
        });
    };

    // Generalised version of handleSubOptionSelect that works for a sub_option
    // chain of ANY depth. `pathTexts` is the ordered list of selected option_text
    // values from the top-level option down to (but not including) the level
    // currently being selected - this produces the exact same key as
    // handleSubOptionSelect for depth 1 (`${qNode}_${optText}_sub`), the exact
    // same key as the old depth-2 variant
    // (`${qNode}_${optText}_${subText}_sub`), and simply continues the same
    // pattern for depth 3, 4, ... with no limit.
    const handleChainSubOptionSelect = (qNode, pathTexts, subOpt, isMulti = false, limit = 1) => {
        setAnswers(prev => {
            let newAnswers = { ...prev };
            const key = [qNode, ...pathTexts, "sub"].join("_");
            if (isMulti) {
                let currentSelections = Array.isArray(prev[key]) ? [...prev[key]] : [];
                if (currentSelections.includes(subOpt.option_text)) {
                    newAnswers[key] = currentSelections.filter(s => s !== subOpt.option_text);
                } else {
                    if (currentSelections.length < limit) newAnswers[key] = [...currentSelections, subOpt.option_text];
                }
            } else {
                newAnswers[key] = subOpt.option_text;
            }
            newAnswers = clearDownstreamAnswers(newAnswers, qNode);
            return newAnswers;
        });
    };

    // ---------------------------------------------------------------------------
    // HINE HANDLERS
    // ---------------------------------------------------------------------------

    // Typing allows digits with ONE decimal point (e.g., 12.3)
    const sanitizeDecimalText = (value) => {
        let s = String(value).replace(/[^0-9.]/g, "");
        const dot = s.indexOf(".");
        if (dot !== -1) s = s.slice(0, dot + 1) + s.slice(dot + 1).replace(/\./g, "");
        return s.replace(/^0+(?=[0-9])/, "");
    };

    const handleHineChange = (index, field, value) => {
        const updated = [...hineData];
        if (["hineScore", "rightAsym", "leftAsym"].includes(field)) {
            updated[index][field] = sanitizeDecimalText(value);
        } else { updated[index][field] = value; }
        setHineData(updated);
        const hineQuestion = visibleSteps.find(q => getQuestionType(q).isHINE);
        if (hineQuestion && answers[hineQuestion.node_number] === "Yes") {
            saveHineHaiAPI(currentSessionId, hineQuestion.node_number, "hine", hineQuestion.question_id, "Yes",
                updated.map(row => ({ timepoint: row.timepoint, hine_score: Number(row.hineScore) || 0, right_asymmetry: Number(row.rightAsym) || 0, left_asymmetry: Number(row.leftAsym) || 0 })));
        }
    };
    // + / - steps ONLY the whole-number (left) part; decimal part stays unchanged (12.3 -> 13.3)
    const handleHineIncrement = (index, field) => { const cur = parseFloat(hineData[index]?.[field]) || 0; const intPart = Math.trunc(cur); const frac = Math.round((cur - intPart) * 100) / 100; handleHineChange(index, field, String(intPart + 1 + frac)); };
    const handleHineDecrement = (index, field) => { const cur = parseFloat(hineData[index]?.[field]) || 0; const intPart = Math.trunc(cur); if (intPart > 0) { const frac = Math.round((cur - intPart) * 100) / 100; handleHineChange(index, field, String(intPart - 1 + frac)); } };
    const addHineRow = () => {
        setHineData(prev => {
            const newRows = [...prev, { id: Date.now(), timepoint: "", hineScore: 0, rightAsym: 0, leftAsym: 0 }];
            const hineQuestion = visibleSteps.find(q => getQuestionType(q).isHINE);
            if (hineQuestion && answers[hineQuestion.node_number] === "Yes")
                saveHineHaiAPI(currentSessionId, hineQuestion.node_number, "hine", hineQuestion.question_id, "Yes",
                    newRows.map(row => ({ timepoint: row.timepoint, hine_score: Number(row.hineScore) || 0, right_asymmetry: Number(row.rightAsym) || 0, left_asymmetry: Number(row.leftAsym) || 0 })));
            return newRows;
        });
    };
    const removeHineRow = (index) => {
        setHineData(prev => {
            const newRows = prev.filter((_, i) => i !== index);
            const hineQuestion = visibleSteps.find(q => getQuestionType(q).isHINE);
            if (hineQuestion && answers[hineQuestion.node_number] === "Yes")
                saveHineHaiAPI(currentSessionId, hineQuestion.node_number, "hine", hineQuestion.question_id, "Yes",
                    newRows.map(row => ({ timepoint: row.timepoint, hine_score: Number(row.hineScore) || 0, right_asymmetry: Number(row.rightAsym) || 0, left_asymmetry: Number(row.leftAsym) || 0 })));
            return newRows;
        });
    };

    // ---------------------------------------------------------------------------
    // HAI HANDLERS
    // ---------------------------------------------------------------------------

    const handleHaiChange = (index, field, value) => {
        const updated = [...haiData];
        if (["rightEahs", "leftEahs", "asymmetryIndex", "eachHandSumScore"].includes(field)) {
            updated[index][field] = sanitizeDecimalText(value);
        } else { updated[index][field] = value; }
        setHaiData(updated);
        const haiQuestion = visibleSteps.find(q => getQuestionType(q).isHAI);
        if (haiQuestion && answers[haiQuestion.node_number] === "Yes") {
            saveHineHaiAPI(currentSessionId, haiQuestion.node_number, "hai", haiQuestion.question_id, "Yes",
                updated.map(row => ({ timepoint: row.timepoint, right_score: Number(row.rightEahs) || 0, left_score: Number(row.leftEahs) || 0, sum: Number(row.eachHandSumScore) || 0, asymmetry: Number(row.asymmetryIndex) || 0 })));
        }
    };
    // + / - steps ONLY the whole-number (left) part; decimal part stays unchanged (12.3 -> 13.3)
    const handleHaiIncrement = (index, field) => { const cur = parseFloat(haiData[index]?.[field]) || 0; const intPart = Math.trunc(cur); const frac = Math.round((cur - intPart) * 100) / 100; handleHaiChange(index, field, String(intPart + 1 + frac)); };
    const handleHaiDecrement = (index, field) => { const cur = parseFloat(haiData[index]?.[field]) || 0; const intPart = Math.trunc(cur); if (intPart > 0) { const frac = Math.round((cur - intPart) * 100) / 100; handleHaiChange(index, field, String(intPart - 1 + frac)); } };
    const addHaiRow = () => {
        setHaiData(prev => {
            const newRows = [...prev, { id: Date.now(), timepoint: "", rightEahs: 0, leftEahs: 0, asymmetryIndex: 0, eachHandSumScore: 0 }];
            const haiQuestion = visibleSteps.find(q => getQuestionType(q).isHAI);
            if (haiQuestion && answers[haiQuestion.node_number] === "Yes")
                saveHineHaiAPI(currentSessionId, haiQuestion.node_number, "hai", haiQuestion.question_id, "Yes",
                    newRows.map(row => ({ timepoint: row.timepoint, right_score: Number(row.rightEahs) || 0, left_score: Number(row.leftEahs) || 0, sum: Number(row.eachHandSumScore) || 0, asymmetry: Number(row.asymmetryIndex) || 0 })));
            return newRows;
        });
    };
    const removeHaiRow = (index) => {
        setHaiData(prev => {
            const newRows = prev.filter((_, i) => i !== index);
            const haiQuestion = visibleSteps.find(q => getQuestionType(q).isHAI);
            if (haiQuestion && answers[haiQuestion.node_number] === "Yes")
                saveHineHaiAPI(currentSessionId, haiQuestion.node_number, "hai", haiQuestion.question_id, "Yes",
                    newRows.map(row => ({ timepoint: row.timepoint, right_score: Number(row.rightEahs) || 0, left_score: Number(row.leftEahs) || 0, sum: Number(row.eachHandSumScore) || 0, asymmetry: Number(row.asymmetryIndex) || 0 })));
            return newRows;
        });
    };

    // Clear the visually-odd "0" when the user focuses a HINE / HAI counter,
    // and restore "0" on blur if they left the field empty.
    const clearZeroOnFocusHine = (e, index, field) => {
        if (["hineScore", "rightAsym", "leftAsym"].includes(field)) {
            const cur = hineData[index]?.[field];
            if (cur === 0 || cur === "0" || cur === "" || cur == null) {
                handleHineChange(index, field, "");
            }
        }
    };
    const restoreZeroOnBlurHine = (e, index, field) => {
        if (["hineScore", "rightAsym", "leftAsym"].includes(field) && (hineData[index]?.[field] === "" || hineData[index]?.[field] == null)) {
            handleHineChange(index, field, "0");
        }
    };
    // Same for HAI (Each-hand + Asymmetry) fields
    const clearZeroOnFocusHai = (e, index, field) => {
        if (["rightEahs", "leftEahs", "eachHandSumScore", "asymmetryIndex"].includes(field)) {
            const cur = haiData[index]?.[field];
            if (cur === 0 || cur === "0" || cur === "" || cur == null) {
                handleHaiChange(index, field, "");
            }
        }
    };
    const restoreZeroOnBlurHai = (e, index, field) => {
        if (["rightEahs", "leftEahs", "eachHandSumScore", "asymmetryIndex"].includes(field) && (haiData[index]?.[field] === "" || haiData[index]?.[field] == null)) {
            handleHaiChange(index, field, "0");
        }
    };

    // ---------------------------------------------------------------------------
    // MISC HELPERS
    // ---------------------------------------------------------------------------

    const getContinuousNumber = (targetQuestion) => {
        const targetIndex = visibleSteps.findIndex(q => q.node_number === targetQuestion.node_number);
        return 3 + targetIndex + 1;
    };

    const getSubOptionAnswerSummary = (q) => {
        const mainAns = answers[q.node_number];
        if (!mainAns) return "";
        const mainAnsArr = Array.isArray(mainAns) ? mainAns : [mainAns];
        let subDisplayArr = [];
        mainAnsArr.forEach(ansText => {
            const selectedOpt = q.options?.find(o => o.option_text === ansText);
            if (selectedOpt && selectedOpt.sub_options && selectedOpt.sub_options.length > 0) {
                const domainKey = `${q.node_number}_${ansText}_sub`;
                const subAns = answers[domainKey];
                if (subAns) { const subText = Array.isArray(subAns) ? subAns.join(", ") : subAns; if (subText) subDisplayArr.push(subText); }
                else { const stdKey = `${q.node_number}_sub`; const stdSubAns = answers[stdKey]; if (stdSubAns) { const subText = Array.isArray(stdSubAns) ? stdSubAns.join(", ") : stdSubAns; if (subText) subDisplayArr.push(subText); } }
            }
        });
        return subDisplayArr.join(", ");
    };

    const getGoalDomainPanelLabel = (q) => {
        if (!isGoalDomainQuestion(q)) return null;
        const skills = getSubOptionAnswerSummary(q);
        if (skills) return `Goals - ${skills}`;
        const mainAns = answers[q.node_number];
        if (mainAns) return Array.isArray(mainAns) ? mainAns.join(", ") : mainAns;
        return null;
    };

    const getAnswerDisplayText = (q) => {
        const mainAns = answers[q.node_number];
        if (mainAns === undefined || mainAns === null || mainAns === "") return "";
        const mainAnsArr = Array.isArray(mainAns) ? mainAns : [mainAns];
        const parts = [];
        mainAnsArr.forEach(ansText => {
            const selectedOpt = q.options?.find(o => o.option_text === ansText);
            const hasSubOptions = selectedOpt && selectedOpt.sub_options && selectedOpt.sub_options.length > 0;
            if (hasSubOptions) {
                const domainKey = `${q.node_number}_${ansText}_sub`;
                const subAns = answers[domainKey];
                const stdSubAns = answers[`${q.node_number}_sub`];
                const subText = subAns
                    ? (Array.isArray(subAns) ? subAns.join(", ") : subAns)
                    : (stdSubAns ? (Array.isArray(stdSubAns) ? stdSubAns.join(", ") : stdSubAns) : "");
                if (subText) parts.push(subText);
                else parts.push(ansText);
            } else {
                parts.push(ansText);
            }
        });
        // Guard: a part can be resolved both from a sub-option chain AND from
        // the raw main-answer element (older data held the flattened
        // ["Yes","Unilateral"] array / "Yes,Unilateral" string). Collapse so
        // the label never repeats.
        return Array.from(new Set(parts)).join(", ");
    };

    const handleSaveBasicDetails = async () => {
        if (!token || !session || !client || !ageRange) return null;
        try {
            const isReview = session === "Review";
            const url = isReview
                ? `${API_BASE}/therapist_api/save_review_init.php`
                : `${API_BASE}/therapist_api/save_basic_details.php`;
            const reviewClient = reviewClients.find(c => c.client_name === client);
            const clientId = selectedClientId
                || (reviewClient ? parseInt(reviewClient.client_id) : null)
                || (urlClientId ? parseInt(urlClientId) : null);
            // Both review modes create/resume a session for an EXISTING client.
            // The appointment type is what distinguishes them, so it has to be
            // sent as the real mode rather than a hardcoded "review" - otherwise
            // a Review Smart Goal session is created as a plain Review one.
            const body = isReview
                ? { client_id: clientId, age_group: ageRange, appointment_type: wireAppointmentType }
                : { appointment_type: session.toLowerCase(), client_name: client, age_group: ageRange };
            const response = await fetchWithTimeout(url, {
                method: "POST",
                headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
                body: JSON.stringify(body)
            });
            const json = await response.json();
            if (json.status) { const sid = json.session_id || json.sessionId; if (sid) setCurrentSessionId(sid); return json; }
            if (isTokenError(json)) { dispatchSessionExpired(); return null; }
            return null;
        } catch (error) { console.error("Save basic details failed:", error.message); return null; }
    };

    const handleSaveDraft = async () => {
        if (!currentSessionId) { console.warn("?? Cannot save draft: no sessionId"); return; }
        try {
            const currentQ = step >= 2 && !isGoalDetailStep && !showReviewGoalScreen ? visibleSteps[step - 2] : null;
            const lastNode = currentQ ? currentQ.node_number : (targetNodeRef.current || null);
            const response = await fetchWithTimeout(
                `${API_BASE}/therapist_api/save_draft_session.php`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
                    body: JSON.stringify({
                        sessionId: parseInt(currentSessionId),
                        // Save-for-Later rewrites the step-1 answer, so it has to
                        // carry the true mode or resuming the draft later
                        // downgrades Review Smart Goal to plain Review.
                        appointment_type: wireAppointmentType,
                        client_name: client,
                        age_group: ageRange,
                        last_node: lastNode
                    })
                }
            );
            const json = await response.json();
            if (json.status) console.log("? Draft Saved:", json.message);
            else {
                console.error("? Failed to save draft:", json.message);
                if (isTokenError(json)) { dispatchSessionExpired(); return; }
            }
        } catch (error) { console.error("Network error saving draft:", error.message); }
    };

    const handleKeywordClick = (targetStep) => { setIsGoalDetailStep(false); setShowReviewGoalScreen(false); setStep(targetStep); };

    const handleNext = () => {
        if (step === 1) {
            if (session === "Review") {
                // Resuming an existing review session (URL sessionId) must not
                // create a new session ? jump straight to the review questions.
                if (currentSessionId) {
                    setStep(2);
                } else if (isFormValid()) {
                    // From goalreview "Continue Pathway": create the review
                    // session and start the questions ? do NOT show the Goal
                    // Progress Update screen (goals were just reviewed there).
                    if (fromGoalReview) {
                        handleSaveBasicDetails().then((json) => { if (json && json.status) setStep(2); });
                    } else {
                        handleSaveBasicDetails().then(() => { setShowReviewGoalScreen(true); });
                    }
                }
                return;
            }
            if (isFormValid()) { handleSaveBasicDetails().then(() => { setStep(2); }); }
        } else {
            // If the selected option/sub-option chain of the current step carries
            // the "End" instruction, skip ahead straight to the summary page.
            if (step >= 2 && visibleSteps[step - 2]) {
                const currentQ = visibleSteps[step - 2];
                if (currentQ && selectedChainHasEnd(currentQ, answers)) {
                    setStep(1 + visibleSteps.length + 1);
                    return;
                }
            }
            if (isGoalDetailStep) { setIsGoalDetailStep(false); }
            else if (step >= 2 && visibleSteps[step - 2]) {
                const currentQ = visibleSteps[step - 2];
                const isGoalDomainQ = isGoalDomainQuestion(currentQ);
                const ans = answers[currentQ.node_number];
                if (isGoalDomainQ && Array.isArray(ans) && ans.length > 0) {
                    const hasSubOptions = currentQ.options.some(opt => ans.includes(opt.option_text) && opt.sub_options && opt.sub_options.length > 0);
                    if (hasSubOptions) { setIsGoalDetailStep(true); return; }
                }
            }
            const totalSteps = 1 + visibleSteps.length;
            if (step < totalSteps) { setStep(prev => prev + 1); }
            else if (step === totalSteps) { setStep(totalSteps + 1); }
            else if (step === totalSteps + 1) { navigate("/summarypage"); }
        }
    };

    const handleRestart = () => {
        localStorage.removeItem("assessmentState");
        localStorage.removeItem("reviewCarryOver");
        setSession("Review"); setClient(""); setAgeRange(""); setAnswers({});
        setHineData([{ id: Date.now(), timepoint: "", hineScore: 0, rightAsym: 0, leftAsym: 0 }]);
        setHaiData([{ id: Date.now(), timepoint: "", rightEahs: 0, leftEahs: 0, asymmetryIndex: 0, eachHandSumScore: 0 }]);
        setStep(1); setActivePathway(null); setIsGoalDetailStep(false);
        setOpenSections({ demographics: false, background: false, comorbidities: false });
        setCurrentSessionId(null); setInterventionsData([]); setActiveGoalTab(null);
        setShowReviewGoalScreen(false);
        targetNodeRef.current = null;
        navigate("/start-assessment");
    };

    // -- Fetch favourite status when sessionId is available -------------------
    useEffect(() => {
        if (!currentSessionId || !token) return;
        const fetchFavourite = async () => {
            try {
                const response = await fetchWithTimeout(
                    `${API_BASE}/therapist_api/pathway_favorites.php`,
                    {
                        method: "POST",
                        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
                        body: JSON.stringify({ sessionId: parseInt(currentSessionId), action: "status" })
                    }
                );
                const json = await response.json();
                if (json.status !== undefined) setIsFavourite(json.is_favourite === true);
                else if (isTokenError(json)) { dispatchSessionExpired(); return; }
            } catch (error) { console.error("Failed to fetch favourite status:", error.message); }
        };
        fetchFavourite();
    }, [currentSessionId, token]);

    const handleToggleFavourite = async () => {
        if (!currentSessionId) return;
        try {
            const response = await fetchWithTimeout(
                `${API_BASE}/therapist_api/pathway_favorites.php`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
                    body: JSON.stringify({ sessionId: parseInt(currentSessionId), action: "toggle" })
                }
            );
            const json = await response.json();
            if (json.status) {
                setIsFavourite(json.is_favourite === true);
                console.log(json.message);
            } else {
                if (isTokenError(json)) { dispatchSessionExpired(); return; }
            }
        } catch (error) {
            console.error("Failed to toggle favourite:", error.message);
        }
    };

    const handleBack = () => {
        // If the inline group history is open, Back returns to the question
        // that was showing before (the step is left unchanged).
        if (historyGroupName) { setHistoryGroupName(null); setShowHistoryFiles(false); return; }
        // If we're on the new "Goal Progress Update" screen, Back returns
        // to the basic-details form instead of decrementing the step
        if (showReviewGoalScreen) { setShowReviewGoalScreen(false); return; }
        if (isGoalDetailStep) { setIsGoalDetailStep(false); return; }
        if (step > 1) {
            // Review questions start at step 2. Back must not drop into the
            // step-1 Review setup screen, whose "Next" would create a NEW
            // review session and send the user back to Goal Progress Update.
            if (session === "Review" && step === 2) return;
            setStep(prev => prev - 1);
        }
    };

    const toggleSection = (key) => setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));

    // -- Format a historical answer for the Group History Panel ------------
    const isFormValid = () => {
        if (step === 1) return session && client.trim() !== "" && ageRange !== "";
        if (isGoalDetailStep) {
            const currentQ = visibleSteps[step - 2];
            if (!currentQ) return false;
            const mainAns = answers[currentQ.node_number];
            if (!mainAns || !Array.isArray(mainAns) || mainAns.length === 0) return false;
            for (const selectedOptText of mainAns) {
                const selectedOpt = currentQ.options?.find(o => o.option_text === selectedOptText);
                if (selectedOpt && selectedOpt.sub_options && selectedOpt.sub_options.length > 0) {
                    const subKey = `${currentQ.node_number}_${selectedOptText}_sub`;
                    if (!answers[subKey]) return false;
                }
            }
            return true;
        }
        const totalSteps = 1 + visibleSteps.length;
        if (step === totalSteps + 1) return true;
        const currentDynamicIndex = step - 2;
        const currentQ = visibleSteps[currentDynamicIndex];
        if (!currentQ) return false;
        const isGoalDomainQ = isGoalDomainQuestion(currentQ);
        if (isGoalDomainQ) { const ans = answers[currentQ.node_number]; return Array.isArray(ans) && ans.length > 0; }
        if (!currentQ.options || currentQ.options.length === 0) return true;
        const answer = answers[currentQ.node_number];
        if (!answer) return false;
        // Sub-options are collected inline on the same step, so the selected
        // option must also have its sub-option answered before Next.
        const instructions = parseInstructions(currentQ.instruction);
        if (instructions.isMulti) { const multiAns = answers[currentQ.node_number]; return Array.isArray(multiAns) && multiAns.length > 0; }
        if (!instructions.isMulti) {
            const selectedOpt = currentQ.options.find(o => o.option_text === answer);
            if (selectedOpt && selectedOpt.sub_options && selectedOpt.sub_options.length > 0) {
                const subKey = `${currentQ.node_number}_${selectedOpt.option_text}_sub`;
                const subAnswer = answers[subKey];
                if (Array.isArray(subAnswer)) { if (subAnswer.length === 0) return false; }
                else if (subAnswer === undefined || subAnswer === null || subAnswer === "") return false;
            }
        }
        return true;
    };

    // ---------------------------------------------------------------------------
    // DOWNLOAD & SHARE HELPERS
    // ---------------------------------------------------------------------------

    // Builds the structured session-comparison data for the currently open group.
    // Returns { sessions, sessionCount, labels, sections } used by both the
    // on-screen history table and the "Download as PDF" generation.
    const buildHistoryTableData = () => {
        if (!groupHistory || !Array.isArray(groupHistory.data) || groupHistory.data.length === 0) return null;

        const sessions = [...groupHistory.data].sort((a, b) => new Date(a.assessment_date) - new Date(b.assessment_date));
        const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        const ordinal = (n) => { const s = ["th", "st", "nd", "rd"]; const v = n % 100; return n + (s[(v - 20) % 10] || s[v] || s[0]); };
        const formatDate = (ds) => { if (!ds) return "-"; const d = new Date(ds); if (isNaN(d.getTime())) return ds; return `${ordinal(d.getDate())} ${months[d.getMonth()]} ${d.getFullYear()}`; };
        const joinBracketed = (vals) => vals.filter(v => v !== "" && v !== null && v !== undefined).map((v, i) => i === 0 ? v : `(${v})`).join(", ") || "-";
        const sessionQ = (s, type) => (Array.isArray(s.pathway) ? s.pathway : []).find(x => (x.type || "").trim().toUpperCase() === type);
        const anyQ = (type) => sessions.some(s => (Array.isArray(s.pathway) ? s.pathway : []).some(x => (x.type || "").trim().toUpperCase() === type));

        let reviewCount = 0;
        const labels = sessions.map((s, i) => {
            const isReview = (s.appointment_type || s.type || "").toLowerCase() !== "initial";
            if (isReview) reviewCount += 1;
            return {
                label: isReview ? `Review ${reviewCount}` : "Initial",
                date: formatDate(s.assessment_date),
                session: s,
            };
        }).reverse();

        const sections = [];

        if (historyGroupName === "Demographics") {
            sections.push({
                title: "Demographics",
                rows: [
                    { label: "Appointment", cell: s => s.appointment_type ? (s.appointment_type.charAt(0).toUpperCase() + s.appointment_type.slice(1)) : "-" },
                    { label: "Client", cell: s => s.client_name || groupHistory.client_name || "-" },
                    { label: "Age Group", cell: s => s.age_group || groupHistory.age_group || "-" }
                ]
            });
        } else {
            const seen = new Set();
            const qRows = [];
            sessions.forEach(s => {
                (Array.isArray(s.pathway) ? s.pathway : []).forEach(q => {
                    const type = (q.type || "").trim().toUpperCase();
                    if (type === "HINE" || type === "HAI") return;
                    const keyword = q.keyword || q.question || "";
                    const kLower = keyword.toLowerCase();
                    if (kLower.includes("hine") || kLower.includes("hai")) return;
                    if (!keyword || seen.has(keyword)) return;
                    seen.add(keyword);
                    qRows.push({
                        label: keyword,
                        cell: (ss) => {
                            const q2 = (Array.isArray(ss.pathway) ? ss.pathway : []).find(x => (x.keyword || x.question || "") === keyword);
                            return q2 ? formatHistoryAnswer(q2.answer) : "-";
                        }
                    });
                });
            });
            if (qRows.length > 0) sections.push({ title: historyGroupName, rows: qRows });
        }

        if (anyQ("HINE")) {
            sections.push({
                title: "HINE",
                rows: [
                    { label: "Status", cell: s => (sessionQ(s, "HINE")?.answer?.value) ? String(sessionQ(s, "HINE").answer.value) : "-" },
                    { label: "Time Point", cell: s => joinBracketed((sessionQ(s, "HINE")?.answer?.entries || []).map(e => e.timepoint)) },
                    { label: "HINE Score", cell: s => joinBracketed((sessionQ(s, "HINE")?.answer?.entries || []).map(e => e.hine_score)) },
                    { label: "Right Asym.", cell: s => joinBracketed((sessionQ(s, "HINE")?.answer?.entries || []).map(e => e.right_asymmetry)) },
                    { label: "Left Asym.", cell: s => joinBracketed((sessionQ(s, "HINE")?.answer?.entries || []).map(e => e.left_asymmetry)) }
                ]
            });
        }

        if (anyQ("HAI")) {
            sections.push({
                title: "HAI",
                rows: [
                    { label: "Status", cell: s => (sessionQ(s, "HAI")?.answer?.value) ? String(sessionQ(s, "HAI").answer.value) : "-" },
                    { label: "Time Point", cell: s => joinBracketed((sessionQ(s, "HAI")?.answer?.entries || []).map(e => e.timepoint)) },
                    { label: "Right Score", cell: s => joinBracketed((sessionQ(s, "HAI")?.answer?.entries || []).map(e => e.right_score)) },
                    { label: "Left Score", cell: s => joinBracketed((sessionQ(s, "HAI")?.answer?.entries || []).map(e => e.left_score)) },
                    { label: "Sum (EAHS)", cell: s => joinBracketed((sessionQ(s, "HAI")?.answer?.entries || []).map(e => e.sum)) },
                    { label: "Asymmetry", cell: s => joinBracketed((sessionQ(s, "HAI")?.answer?.entries || []).map(e => e.asymmetry)) }
                ]
            });
        }

        if (sections.length === 0) return null;

        return { sessions, sessionCount: sessions.length, labels, sections };
    };

    const openAllAccordions = () => {
        if (!interventionsData || !interventionsData.data) return () => { };
        const prevState = { ...accordionOpen };
        const allOpen = {};
        Object.keys(interventionsData.data).forEach(key => {
            (interventionsData.data[key] || []).forEach(intervention => {
                allOpen[`int-${intervention.id}`] = true;
            });
        });
        setAccordionOpen(allOpen);
        return () => setAccordionOpen(prevState);
    };

    const loadPdfLibraries = () => {
        return new Promise((resolve) => {
            const loadScript = (src) =>
                new Promise((res) => {
                    if (document.querySelector(`script[src="${src}"]`)) { res(); return; }
                    const s = document.createElement("script");
                    s.src = src;
                    s.onload = res;
                    document.head.appendChild(s);
                });

            Promise.all([
                loadScript("https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js"),
                loadScript("https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js"),
            ]).then(resolve);
        });
    };

    const captureSummaryAsPdfBlob = async () => {
        if (!summaryRef.current) throw new Error("Summary element not found");

        await loadPdfLibraries();

        await new Promise(r => setTimeout(r, 600));

        const element = summaryRef.current;
        const canvas = await window.html2canvas(element, {
            scale: 1.5,
            useCORS: true,
            allowTaint: true,
            backgroundColor: "#ffffff",
            scrollX: 0,
            scrollY: -window.scrollY,
            windowWidth: element.scrollWidth,
            windowHeight: element.scrollHeight,
        });

        const { jsPDF } = window.jspdf;
        const pdfWidth = 210;
        const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
        const pdf = new jsPDF({ orientation: pdfHeight > pdfWidth ? "portrait" : "landscape", unit: "mm", format: "a4" });

        const pages = paginateCanvasToPdf({
            canvas,
            element,
            pdfWidth,
            pageHeightMm: pdf.internal.pageSize.getHeight(),
        });

        pages.forEach((p, i) => {
            if (i > 0) pdf.addPage();
            pdf.addImage(p.sliceImgData, "JPEG", 0, 0, pdfWidth, p.sliceHeightMm);
        });

        return pdf.output("blob");
    };

    const handleShareMail = async () => {
        setShowShareModal(false);
        setIsGeneratingPdf(true);
        const restoreAccordions = openAllAccordions();

        try {
            const blob = await captureSummaryAsPdfBlob();

            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `assessment-summary-${client || "report"}-${new Date().toISOString().slice(0, 10)}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

            const subject = encodeURIComponent(`Assessment Summary ? ${client || "Client"}`);
            const body = encodeURIComponent(
                `Please find the assessment summary PDF attached.\n\nClient: ${client || "-"}\nAge Group: ${ageRange || "-"}\nSession Type: ${session || "-"}\n\nThe PDF has been downloaded to your device. Please attach it to this email before sending.`
            );
            window.location.href = `mailto:?subject=${subject}&body=${body}`;
        } catch (err) {
            console.error("Mail share failed:", err);
            alert("Failed to generate PDF for email. Please try again.");
        } finally {
            restoreAccordions();
            setIsGeneratingPdf(false);
        }
    };

    const handleShareWhatsApp = async () => {
        setShowShareModal(false);
        setIsGeneratingPdf(true);
        const restoreAccordions = openAllAccordions();

        try {
            const blob = await captureSummaryAsPdfBlob();

            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `assessment-summary-${client || "report"}-${new Date().toISOString().slice(0, 10)}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

            const message = encodeURIComponent(
                `Assessment Summary for ${client || "Client"}\nAge Group: ${ageRange || "-"} | Session: ${session || "-"}\n\nThe PDF summary has been downloaded to your device. Please share it via WhatsApp by attaching the file.`
            );
            window.open(`https://wa.me/?text=${message}`, "_blank");
        } catch (err) {
            console.error("WhatsApp share failed:", err);
            alert("Failed to generate PDF for WhatsApp. Please try again.");
        } finally {
            restoreAccordions();
            setIsGeneratingPdf(false);
        }
    };

    const handleSummaryConfirm = async () => {
        if (!currentSessionId) return;

        try {
            const response = await fetchWithTimeout(
                `${API_BASE}/therapist_api/review_summary_confirm.php`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        session_id: parseInt(currentSessionId),
                    }),
                }
            );

            const json = await response.json();

            if (json.status) {
                setToast({ message: "Summary Saved Successfully.", type: "success" });
                setTimeout(() => {
                    // A Review Smart Goal revises a goal that lives on the
                    // ORIGINATING session, so Interventions must be told which
                    // session holds the goal (goalSessionId) as well as which one
                    // holds this review. Without it "Revise Selected
                    // Intervention" would create a second goal on this review
                    // session instead of revising the existing one.
                    const params = new URLSearchParams({
                        sessionId: String(currentSessionId),
                        fromReview: "1",
                    });
                    if (isSmartGoalMode) {
                        params.set("smartGoal", "1");
                        const gsid = urlGoalSessionId || currentSessionId;
                        if (gsid) params.set("goalSessionId", String(gsid));
                    }
                    navigate(`/Interventions?${params.toString()}`);
                }, 1500);
            } else {
                if (isTokenError(json)) { dispatchSessionExpired(); return; }
                alert(json.message || "Failed to confirm summary");
            }
        } catch (error) {
            console.error(error);
            alert("Network error");
        }
    };

    // Download the currently open group's historical comparisons as a PDF.
    // Captures the off-screen printable node (group title + Client name - client
    // code + tables), paginates it onto A4 sheets with symmetric 12mm margins,
    // and saves it.
    const handleDownloadGroupData = async () => {
        setIsDownloadingGroupData(true);
        try {
            await loadPdfLibraries();
            await new Promise(r => setTimeout(r, 300));
            const node = groupHistoryPrintRef.current;
            if (!node) throw new Error("Printable node not found");

            const canvas = await window.html2canvas(node, {
                scale: 1.5,
                useCORS: true,
                allowTaint: true,
                backgroundColor: "#ffffff",
            });

            const { jsPDF } = window.jspdf;
            const pdfWidth = 210;
            const marginMm = 12;
            const contentWidthMm = pdfWidth - (marginMm * 2);
            const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
            const pdf = new jsPDF({
                orientation: pdfHeight > pdfWidth ? "portrait" : "landscape",
                unit: "mm",
                format: "a4",
            });

            const pages = paginateCanvasToPdf({
                canvas,
                element: node,
                pdfWidth,
                pageHeightMm: pdf.internal.pageSize.getHeight(),
            });

            pages.forEach((p, i) => {
                if (i > 0) pdf.addPage();
                const scaledHeight = (p.sliceHeightMm / pdfWidth) * contentWidthMm;
                const pageHeight = pdf.internal.pageSize.getHeight();
                const yOffset = marginMm + Math.max(0, (pageHeight - (marginMm * 2) - scaledHeight) / 2);
                pdf.addImage(p.sliceImgData, "JPEG", marginMm, yOffset, contentWidthMm, scaledHeight);
            });

            const safeName = (historyGroupName || "group").replace(/[^a-zA-Z0-9-]/g, "-");
            pdf.save(`previous-data-${safeName}-${new Date().toISOString().slice(0, 10)}.pdf`);
        } catch (error) {
            console.error("Group data PDF generation failed:", error);
            alert("Failed to generate PDF. Please try again.");
        } finally {
            setIsDownloadingGroupData(false);
        }
    };

    // ---------------------------------------------------------------------------
    // RENDER HELPERS
    // ---------------------------------------------------------------------------
    const DescriptionContent = ({ html, color = "#323C43" }) => (
        <div
            className="description-content"
            style={{
                fontSize: "14px",
                color: "#323C43",
                textAlign: "left",
                lineHeight: "1.8",
                margin: 0,
            }}
            dangerouslySetInnerHTML={{
                __html: html || "",
            }}
        />
    );
    const renderAttachments = (attachments) => {
        if (!attachments || attachments.length === 0) return null;
        const colorOrder = { purple: 1, grey: 2, yellow: 3, orange: 4, red: 5, cyan: 6, blue: 7, green: 8 };
        const sorted = [...attachments].sort((a, b) => (colorOrder[a.color] || 99) - (colorOrder[b.color] || 99));
        return (
            <div style={{ marginTop: "15px" }}>
                {sorted.map((att, idx) => (
                    <div key={idx} style={{ marginBottom: "10px" }}>
                        {att.color === "green" && (
                            <div>
                                {att.descriptions && att.descriptions.length > 0 ? (
                                    att.descriptions.map((desc, dIdx) => {
                                        const url = desc.link && desc.link.trim() !== "" ? (desc.link.startsWith("http") ? desc.link : "https://" + desc.link) : null;
                                        return (
                                            <div className="" key={dIdx}>
                                                <button className="fact-btn10" onClick={() => {
                                                    if (url) {
                                                        window.open(url, "_blank");
                                                    } else if (desc.description) {
                                                        window.open(desc.description, "_blank");
                                                    }
                                                }}>
                                                    <FontAwesomeIcon icon={faDownload} className="header-icon10" aria-hidden="true" /> {desc.title || att.title || "Resource Link"}
                                                </button>
                                            </div>
                                        );
                                    })
                                ) : att.file_path ? (
                                    <div>
                                        <button className="fact-btn10" onClick={() => {
                                            const fileUrl = att.file_path.startsWith("http") ? att.file_path : `${API_BASE}/` + att.file_path;
                                            window.open(fileUrl, "_blank");
}}>
                                                <FontAwesomeIcon icon={faDownload} className="header-icon10" aria-hidden="true" /> {att.title || "Resource Link"}
                                            </button>
                                    </div>
                                ) : null}
                            </div>
                        )}
                        {att.color === "blue" && att.file_path && (
                            <div style={{ marginTop: "10px", background: "#E6F7FC", borderRadius: "8px", padding: "10px" }}>
                                <a
                                    href={att.file_path.startsWith("http") ? att.file_path : `${API_BASE}/` + att.file_path}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="attachment-image-link"
                                    aria-label={att.title || "Open attachment image in new tab"}
                                >
                                    <img
                                        src={att.file_path.startsWith("http") ? att.file_path : `${API_BASE}/` + att.file_path}
                                        alt={att.title || "Attachment"}
                                        style={{ maxWidth: "100%", maxHeight: "400px", borderRadius: "8px", objectFit: "contain", border: "1px solid #e0e0e0", display: "block" }}
                                    />
                                </a>
                            </div>
                        )}
                        {att.color === "purple" && att.descriptions && att.descriptions.map((desc, dIdx) => (
                            <div className="info-box0" key={dIdx}>
                                {desc.title ? (
                                    <>
                                        <p style={{ fontSize: "14px", color: "#323C43", textAlign: "start", display: "flex", alignItems: "flex-start", gap: "8px" }}>
                                            <FontAwesomeIcon icon={faBookOpenReader} aria-hidden="true" style={{ marginTop: "5px" }} />
                                            <span style={{ fontWeight: "bold" }}>{desc.title}</span></p>
                                        <div
                                            style={{
                                                fontSize: "14px",
                                                color: "#323C43",
                                                textAlign: "left",
                                                paddingLeft: "22px",
                                                lineHeight: "1.8",
                                                whiteSpace: "pre-line",
                                                margin: 0,
                                            }}
                                        >
                                            <DescriptionContent
                                                html={desc.description}
                                                color="#323C43"
                                            />
                                        </div>                </>)
                                    :
                                    (<p style={{ fontSize: "14px", color: "#323C43", textAlign: "start", display: "flex", alignItems: "flex-start", gap: "8px" }}>
                                        <FontAwesomeIcon icon={faBookOpenReader} aria-hidden="true" style={{ marginTop: "5px" }} />
                                        <div style={{
                                            fontSize: "14px",
                                            color: "#323C43",
                                            textAlign: "start",
                                            display: "flex",
                                            alignItems: "flex-start",
                                            gap: "8px",
                                            lineHeight: "1.8",
                                            whiteSpace: "pre-line",
                                        }}>
                                            <DescriptionContent
                                                html={desc.description}
                                                color="#323C43"
                                            />
                                        </div>
                                    </p>)}
                            </div>
                        ))}
                        {att.color === "grey" && att.descriptions && att.descriptions.map((desc, dIdx) => (
                            <div className="info-box" key={dIdx}>
                                {desc.title ? (
                                    <>
                                        <p
                                            style={{
                                                fontSize: "14px",
                                                color: "#323C43",
                                                display: "flex",
                                                alignItems: "flex-start",
                                                gap: "8px",
                                            }}
                                        >
                                            <img
                                                src={infoIcon}
                                                alt="" aria-hidden="true"
                                                className="infoicon"
                                                style={{
                                                    marginTop: "5px",
                                                }}
                                            />

                                            <span style={{ fontWeight: "bold" }}>
                                                {desc.title}
                                            </span>
                                        </p>

                                        <div
                                            style={{
                                                fontSize: "14px",
                                                color: "#323C43",
                                                textAlign: "left",
                                                paddingLeft: "22px",
                                                lineHeight: "1.8",
                                                whiteSpace: "pre-line",
                                                margin: 0,
                                            }}
                                        >
                                            <DescriptionContent
                                                html={desc.description}
                                                color="#323C43"
                                            />
                                        </div>
                                    </>
                                ) : (
                                    <div
                                        style={{
                                            fontSize: "14px",
                                            color: "#323C43",
                                            textAlign: "start",
                                            display: "flex",
                                            alignItems: "flex-start",
                                            gap: "8px",
                                            lineHeight: "1.8",
                                            whiteSpace: "pre-line",
                                        }}
                                    >
                                        <img
                                            src={infoIcon}
                                            alt="" aria-hidden="true"
                                            className="infoicon"
                                            style={{
                                                marginTop: "5px",
                                                minWidth: "13px",
                                            }}
                                        />

                                        <DescriptionContent
                                            html={desc.description}
                                            color="#323C43"
                                        />
                                    </div>
                                )}
                            </div>
                        ))}
                        {att.color === "yellow" && att.descriptions && att.descriptions.map((desc, dIdx) => (
                            <div className="info-box1" key={dIdx}>
                                {desc.title ? (<><p style={{ fontSize: "14px", color: "#323C43", display: "flex", alignItems: "flex-start", gap: "8px" }}>
                                    <img src={rightarrow} alt="" aria-hidden="true" className="infoicon" style={{ width: "20px", height: "20px" }} /><span style={{ fontWeight: "bold" }}>{desc.title}</span></p>
                                    <div
                                        style={{
                                            paddingLeft: "27px",
                                            marginTop: "5px",
                                        }}
                                    >
                                        <DescriptionContent
                                            html={desc.description}
                                            color="#323C43"
                                        />
                                    </div>                  </>) : (<p style={{ fontSize: "14px", color: "#323C43", display: "flex", alignItems: "flex-start", gap: "8px" }}>
                                        <img src={rightarrow} alt="" aria-hidden="true" className="infoicon" style={{ width: "20px", height: "20px" }} />
                                        <div style={{
                                            fontSize: "14px",
                                            color: "#323C43",
                                            textAlign: "start",
                                            display: "flex",
                                            alignItems: "flex-start",
                                            gap: "8px",
                                            lineHeight: "1.8",
                                            whiteSpace: "pre-line",
                                        }}>
                                            <DescriptionContent
                                                html={desc.description}
                                                color="#323C43"
                                            />
                                        </div>
                                    </p>)}
                            </div>
                        ))}
                        {att.color === "orange" && att.descriptions && att.descriptions.map((desc, dIdx) => (
                            <div className="info-box2" key={dIdx}>
                                {desc.title ? (<>
                                    <p style={{ fontSize: "14px", color: "#323C43", display: "flex", alignItems: "flex-start", gap: "8px" }}>
                                        <FontAwesomeIcon icon={faCopy} aria-hidden="true" style={{ marginTop: "5px" }} />
                                        <span style={{ fontWeight: "bold" }}>{desc.title}</span></p>
                                    <div
                                        style={{
                                            paddingLeft: "25px",
                                            marginTop: "5px",
                                        }}
                                    >
                                        <DescriptionContent
                                            html={desc.description}
                                            color="#323C43"
                                        />
                                    </div>                  </>) : (<p style={{ fontSize: "14px", color: "#323C43", display: "flex", alignItems: "flex-start", gap: "8px" }}>
                                        <FontAwesomeIcon icon={faCopy} aria-hidden="true" style={{ marginTop: "5px" }} />
                                        <div style={{
                                            fontSize: "14px",
                                            color: "#323C43",
                                            textAlign: "start",
                                            display: "flex",
                                            alignItems: "flex-start",
                                            gap: "8px",
                                            lineHeight: "1.8",
                                            whiteSpace: "pre-line",
                                        }}>
                                            <DescriptionContent
                                                html={desc.description}
                                                color="#323C43"
                                            />
                                        </div>
                                    </p>)}
                            </div>
                        ))}
                        {att.color === "red" && att.descriptions && att.descriptions.map((desc, dIdx) => (
                            <div className="info-box3" key={dIdx}>
                                {desc.title ? (<>
                                    <p style={{ fontSize: "14px", color: "#323C43", display: "flex", alignItems: "flex-start", gap: "8px" }}>
                                        <FontAwesomeIcon icon={faTriangleExclamation} aria-hidden="true" style={{ marginTop: "5px" }} />
                                        <span style={{ fontWeight: "bold" }}>{desc.title}</span></p>
                                    <div
                                        style={{
                                            paddingLeft: "26px",
                                            marginTop: "5px",
                                        }}
                                    >
                                        <DescriptionContent
                                            html={desc.description}
                                            color="#323C43"
                                        />
                                    </div>                  </>) : (<p style={{ fontSize: "14px", color: "#323C43", display: "flex", alignItems: "flex-start", gap: "8px" }}>
                                        <FontAwesomeIcon icon={faTriangleExclamation} aria-hidden="true" style={{ marginTop: "5px" }} />
                                        <div style={{
                                            fontSize: "14px",
                                            color: "#323C43",
                                            textAlign: "start",
                                            display: "flex",
                                            alignItems: "flex-start",
                                            gap: "8px",
                                            lineHeight: "1.8",
                                            whiteSpace: "pre-line",
                                        }}>
                                            <DescriptionContent
                                                html={desc.description}
                                                color="#323C43"
                                            />
                                        </div>
                                    </p>)}
                            </div>
                        ))}
                        {att.color === "cyan" && att.descriptions && att.descriptions.map((desc, dIdx) => (
                            <div className="info-box4" key={dIdx}>
                                {desc.title ? (<>
                                    <p style={{ fontSize: "14px", color: "#323C43", display: "flex", alignItems: "flex-start", gap: "8px" }}>
                                        <FontAwesomeIcon icon={faLink} aria-hidden="true" style={{ marginTop: "5px" }} />
                                        <span style={{ fontWeight: "bold" }}>{desc.title}</span></p>
                                    <div
                                        style={{
                                            paddingLeft: "24px",
                                            marginTop: "5px",
                                        }}
                                    >
                                        <DescriptionContent
                                            html={desc.description}
                                            color="#323C43"
                                        />
                                    </div>                  </>) : (<p style={{ fontSize: "14px", color: "#323C43", display: "flex", alignItems: "flex-start", gap: "8px" }}>
                                        <FontAwesomeIcon icon={faLink} aria-hidden="true" style={{ marginTop: "5px" }} />
                                        <div style={{
                                            fontSize: "14px",
                                            color: "#323C43",
                                            textAlign: "start",
                                            display: "flex",
                                            alignItems: "flex-start",
                                            gap: "8px",
                                            lineHeight: "1.8",
                                            whiteSpace: "pre-line",
                                        }}>
                                            <DescriptionContent
                                                html={desc.description}
                                                color="#323C43"
                                            />
                                        </div>

                                    </p>)}
                            </div>
                        ))}
                    </div>
                ))}
            </div>
        );
    };

    // Recursively renders the "please select from the list below" sub-option
    // UI for an arbitrarily deep chain of sub_options belonging to question
    // `qNode`. `pathTexts` is the ordered list of selected option_text values
    // from the top-level option down to (but not including) the level being
    // rendered here. This reuses exactly the same markup/classNames as the
    // original hand-written sub-option / sub-sub-option blocks, so visually
    // nothing changes for the depths that already worked ? it just keeps
    // going for any additional depth instead of stopping.
    const renderSubOptionsChain = (qNode, pathTexts, subOptions, parentInstruction) => {
        if (!subOptions || subOptions.length === 0) return null;
        const subOptionLimit = parseSubOptionLimit(parentInstruction);
        const subInstructions = parseInstructions(parentInstruction);
        const isMultiSub = subOptionLimit > 1 || subInstructions.isMulti;
        const multiLimit = subOptionLimit > 1 ? subOptionLimit : null;
        const chainKey = [qNode, ...pathTexts, "sub"].join("_");
        const selectedValRaw = answers[chainKey];
        const selectedArr = Array.isArray(selectedValRaw) ? selectedValRaw : (selectedValRaw ? [selectedValRaw] : []);
        const selectedSubs = subOptions.filter(sub => selectedArr.includes(sub.option_text));
        const selectedSubAttachments = selectedSubs.reduce((acc, s) => acc.concat(s.attachments || []), []);

        return (
            <>
                {isMultiSub ? (
                    multiLimit ? (
                        <p className="question" style={{ marginBottom: "5px" }}>
                            Please select up to <strong>{multiLimit}</strong> options:
                            <span style={{ fontSize: "0.9em", color: "#666", marginLeft: "10px" }}>(Selected: {selectedArr.length}/{multiLimit})</span>
                        </p>
                    ) : (
                        <p className="question" style={{ marginBottom: "5px" }}>
                            Please select more than one option:
                            <span style={{ fontSize: "0.9em", color: "#666", marginLeft: "10px" }}>(Selected: {selectedArr.length})</span>
                        </p>
                    )
                ) : (
                    <p className="question">Please select from the list below:</p>
                )}
                <div className={`radio-group2 ${parentInstruction === "Vertical" ? "vertical" : ""}`}>
                    {subOptions.map(sub => {
                        const hasDeeper = sub.sub_options && sub.sub_options.length > 0;
                        if (isMultiSub) {
                            const isChecked = selectedArr.includes(sub.option_text);
                            const isDisabled = multiLimit !== null && !isChecked && selectedArr.length >= multiLimit;
                            return (
                                <div key={sub.option_id} style={{ marginBottom: hasDeeper ? "10px" : "0" }}>
                                    <label style={{ opacity: isDisabled ? 0.6 : 1, display: "block" }}>
                                        <input type="checkbox" name={chainKey} value={sub.option_text} checked={isChecked} disabled={isDisabled}
                                            onChange={() => {
                                                let currSel = Array.isArray(answers[chainKey]) ? [...answers[chainKey]] : [];
                                                let nextSel = isChecked ? currSel.filter(s => s !== sub.option_text) : [...currSel, sub.option_text];
                                                handleChainSubOptionSelect(qNode, pathTexts, sub, true, multiLimit ?? subOptions.length);
                                                let allSubIds = subOptions.filter(o => nextSel.includes(o.option_text)).map(o => o.option_id);
                                                const targetNode = sub.node_number || qNode;
                                                const parentIdForSave = sub.parent_option_id || sub.option_id;
                                                saveResponseAPI(currentSessionId, targetNode, "option", parentIdForSave, null, allSubIds.length === 0 ? [] : allSubIds);
                                            }} /> <RichText html={sub.option_text} />
                                    </label>
                                    {isChecked && hasDeeper && (
                                        <div style={{ marginLeft: "30px", marginTop: "10px", borderLeft: "2px solid #ddd", paddingLeft: "10px" }}>
                                            {renderSubOptionsChain(qNode, [...pathTexts, sub.option_text], sub.sub_options, sub.instruction || "")}
                                        </div>
                                    )}
                                </div>
                            );
                        }
                        const isSelectedRadio = selectedArr[0] === sub.option_text;
                        return (
                            <div key={sub.option_id} style={{ marginBottom: hasDeeper ? "10px" : "0" }}>
                                <label style={{ display: "block" }}>
                                    <input type="radio" name={chainKey} value={sub.option_text} checked={isSelectedRadio}
                                        onChange={() => {
                                            handleChainSubOptionSelect(qNode, pathTexts, sub, false, 1);
                                            if (!isSelectedRadio) saveResponseAPI(currentSessionId, sub.node_number, "option", sub.option_id, sub.option_text);
                                            else saveResponseAPI(currentSessionId, sub.node_number, "question", sub.option_id, "");
                                        }} /> <RichText html={sub.option_text} />
                                </label>
                                {isSelectedRadio && (
                                    <div style={{ marginLeft: "30px" }}>
                                        {hasDeeper && (
                                            <div style={{ borderLeft: "2px solid #ddd", paddingLeft: "10px", marginTop: "10px" }}>
                                                {renderSubOptionsChain(qNode, [...pathTexts, sub.option_text], sub.sub_options, sub.instruction || "")}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
                {renderAttachments(selectedSubAttachments)}
            </>
        );
    };

    // -- HINE UI ---------------------------------------------------------------
    const renderHINESection = (selectedVal) => {
        if (selectedVal !== "Yes") return null;
        return (
            <div className="hine-section">
                {/* First Row - Default Row */}
                <div className="hine-row-single">
                    <div className="hine-field time-field">
                        <label htmlFor="hine-timepoint-0">Timepoint</label>
                        <input
                            type="text"
                            id="hine-timepoint-0"
                            placeholder="Enter age in months"
                            value={hineData[0]?.timepoint || ""}
                            className="hine-input"
                            onChange={e => handleHineChange(0, "timepoint", e.target.value)}
                        />
                    </div>
                    <div className="hine-field">
                        <label htmlFor="hine-score-0">HINE Score</label>
                        <div className="hine-counter">
                            <button type="button" onClick={() => handleHineDecrement(0, "hineScore")} aria-label="Decrease HINE Score">-</button>
                            <input
                                type="text"
                                id="hine-score-0"
                                value={hineData[0]?.hineScore ?? ""}
                                onChange={e => handleHineChange(0, "hineScore", e.target.value)}
                                onFocus={e => clearZeroOnFocusHine(e, 0, "hineScore")}
                                onBlur={e => restoreZeroOnBlurHine(e, 0, "hineScore")}
                            inputMode="decimal"
                            />
                            <button type="button" onClick={() => handleHineIncrement(0, "hineScore")} aria-label="Increase HINE Score">+</button>
                        </div>
                    </div>
                    <div className="hine-field">
                        <label htmlFor="hine-right-asym-0">Right Asymmetries</label>
                        <div className="hine-counter">
                            <button type="button" onClick={() => handleHineDecrement(0, "rightAsym")} aria-label="Decrease Right Asymmetries">-</button>
                            <input
                                type="text"
                                id="hine-right-asym-0"
                                value={hineData[0]?.rightAsym ?? ""}
                                onChange={e => handleHineChange(0, "rightAsym", e.target.value)}
                                onFocus={e => clearZeroOnFocusHine(e, 0, "rightAsym")}
                                onBlur={e => restoreZeroOnBlurHine(e, 0, "rightAsym")}
                            inputMode="decimal"
                            />
                            <button type="button" onClick={() => handleHineIncrement(0, "rightAsym")} aria-label="Increase Right Asymmetries">+</button>
                        </div>
                    </div>
                    <div className="hine-field">
                        <label htmlFor="hine-left-asym-0">Left Asymmetries</label>
                        <div className="hine-counter">
                            <button type="button" onClick={() => handleHineDecrement(0, "leftAsym")} aria-label="Decrease Left Asymmetries">-</button>
                            <input
                                type="text"
                                id="hine-left-asym-0"
                                value={hineData[0]?.leftAsym ?? ""}
                                onChange={e => handleHineChange(0, "leftAsym", e.target.value)}
                                onFocus={e => clearZeroOnFocusHine(e, 0, "leftAsym")}
                                onBlur={e => restoreZeroOnBlurHine(e, 0, "leftAsym")}
                            inputMode="decimal"
                            />
                            <button type="button" onClick={() => handleHineIncrement(0, "leftAsym")} aria-label="Increase Left Asymmetries">+</button>
                        </div>
                    </div>
                </div>
                <div className="hine-sub">
                    <p className="hine-subtext">Please provide earlier HINE scores if any</p>
                    <div className="hine-actions">
                        <button type="button" className="add-btn" onClick={addHineRow} aria-label="Add HINE row">+</button>
                        {hineData.length > 1 && <button type="button" className="remove-btn" onClick={() => removeHineRow(hineData.length - 1)} aria-label="Remove last HINE row">-</button>}
                    </div>
                </div>
                {/* Dynamic Rows */}
                {hineData.slice(1).map((item, index) => {
                    const rowId = index + 1;
                    return (
                        <div key={item.id || index} className="hine-row-single">
                            <div className="hine-field time-field">
                                <label htmlFor={`hine-timepoint-${rowId}`}>Timepoint</label>
                                <input
                                    type="text"
                                    id={`hine-timepoint-${rowId}`}
                                    placeholder="Enter age in months"
                                    value={item.timepoint || ""}
                                    className="hine-input"
                                    onChange={e => handleHineChange(rowId, "timepoint", e.target.value)}
                                />
                            </div>
                            <div className="hine-field">
                                <label htmlFor={`hine-score-${rowId}`}>HINE Score</label>
                                <div className="hine-counter">
                                    <button type="button" onClick={() => handleHineDecrement(rowId, "hineScore")} aria-label="Decrease HINE Score">-</button>
                                    <input
                                        type="text"
                                        id={`hine-score-${rowId}`}
                                        value={item.hineScore ?? ""}
                                        onChange={e => handleHineChange(rowId, "hineScore", e.target.value)}
                                        onFocus={e => clearZeroOnFocusHine(e, rowId, "hineScore")}
                                        onBlur={e => restoreZeroOnBlurHine(e, rowId, "hineScore")}
                                    inputMode="decimal"
                                    />
                                    <button type="button" onClick={() => handleHineIncrement(rowId, "hineScore")} aria-label="Increase HINE Score">+</button>
                                </div>
                            </div>
                            <div className="hine-field">
                                <label htmlFor={`hine-right-asym-${rowId}`}>Right Asymmetries</label>
                                <div className="hine-counter">
                                    <button type="button" onClick={() => handleHineDecrement(rowId, "rightAsym")} aria-label="Decrease Right Asymmetries">-</button>
                                    <input
                                        type="text"
                                        id={`hine-right-asym-${rowId}`}
                                        value={item.rightAsym ?? ""}
                                        onChange={e => handleHineChange(rowId, "rightAsym", e.target.value)}
                                        onFocus={e => clearZeroOnFocusHine(e, rowId, "rightAsym")}
                                        onBlur={e => restoreZeroOnBlurHine(e, rowId, "rightAsym")}
                                    inputMode="decimal"
                                    />
                                    <button type="button" onClick={() => handleHineIncrement(rowId, "rightAsym")} aria-label="Increase Right Asymmetries">+</button>
                                </div>
                            </div>
                            <div className="hine-field">
                                <label htmlFor={`hine-left-asym-${rowId}`}>Left Asymmetries</label>
                                <div className="hine-counter">
                                    <button type="button" onClick={() => handleHineDecrement(rowId, "leftAsym")} aria-label="Decrease Left Asymmetries">-</button>
                                    <input
                                        type="text"
                                        id={`hine-left-asym-${rowId}`}
                                        value={item.leftAsym ?? ""}
                                        onChange={e => handleHineChange(rowId, "leftAsym", e.target.value)}
                                        onFocus={e => clearZeroOnFocusHine(e, rowId, "leftAsym")}
                                        onBlur={e => restoreZeroOnBlurHine(e, rowId, "leftAsym")}
                                    inputMode="decimal"
                                    />
                                    <button type="button" onClick={() => handleHineIncrement(rowId, "leftAsym")} aria-label="Increase Left Asymmetries">+</button>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    // -- HAI UI ----------------------------------------------------------------
    const renderHAISection = (selectedVal) => {
        if (selectedVal !== "Yes") return null;
        return (
            <div className="hai-section1">
                {/* First Row - Default Row */}
                <div className="hine-row-single">
                    <div className="hine-field time-field">
                        <label htmlFor="hai-timepoint-0">Timepoint</label>
                        <input
                            type="text"
                            id="hai-timepoint-0"
                            placeholder="Enter age in months"
                            value={haiData[0]?.timepoint || ""}
                            className="hine-input"
                            onChange={e => handleHaiChange(0, "timepoint", e.target.value)}
                        />
                    </div>
                    <div className="hine-field">
                        <label htmlFor="hai-right-eahs-0">Each hand score (Right)</label>
                        <div className="hine-counter">
                            <button type="button" onClick={() => handleHaiDecrement(0, "rightEahs")} aria-label="Decrease Each hand score Right">-</button>
                            <input
                                type="text"
                                id="hai-right-eahs-0"
                                value={haiData[0]?.rightEahs ?? ""}
                                onChange={e => handleHaiChange(0, "rightEahs", e.target.value)}
                                onFocus={e => clearZeroOnFocusHai(e, 0, "rightEahs")}
                                onBlur={e => restoreZeroOnBlurHai(e, 0, "rightEahs")}
                            inputMode="decimal"
                            />
                            <button type="button" onClick={() => handleHaiIncrement(0, "rightEahs")} aria-label="Increase Each hand score Right">+</button>
                        </div>
                    </div>
                    <div className="hine-field">
                        <label htmlFor="hai-left-eahs-0">Each hand score (Left)</label>
                        <div className="hine-counter">
                            <button type="button" onClick={() => handleHaiDecrement(0, "leftEahs")} aria-label="Decrease Each hand score Left">-</button>
                            <input
                                type="text"
                                id="hai-left-eahs-0"
                                value={haiData[0]?.leftEahs ?? ""}
                                onChange={e => handleHaiChange(0, "leftEahs", e.target.value)}
                                onFocus={e => clearZeroOnFocusHai(e, 0, "leftEahs")}
                                onBlur={e => restoreZeroOnBlurHai(e, 0, "leftEahs")}
                            inputMode="decimal"
                            />
                            <button type="button" onClick={() => handleHaiIncrement(0, "leftEahs")} aria-label="Increase Each hand score Left">+</button>
                        </div>
                    </div>
                    <div className="hine-field">
                        <label htmlFor="hai-sum-score-0">Each hand sum score</label>
                        <div className="hine-counter">
                            <button type="button" onClick={() => handleHaiDecrement(0, "eachHandSumScore")} aria-label="Decrease Each hand sum score">-</button>
                            <input
                                type="text"
                                id="hai-sum-score-0"
                                value={haiData[0]?.eachHandSumScore ?? ""}
                                onChange={e => handleHaiChange(0, "eachHandSumScore", e.target.value)}
                                onFocus={e => clearZeroOnFocusHai(e, 0, "eachHandSumScore")}
                                onBlur={e => restoreZeroOnBlurHai(e, 0, "eachHandSumScore")}
                            inputMode="decimal"
                            />
                            <button type="button" onClick={() => handleHaiIncrement(0, "eachHandSumScore")} aria-label="Increase Each hand sum score">+</button>
                        </div>
                    </div>
                    <div className="hine-field">
                        <label htmlFor="hai-asymmetry-0">Asymmetry (%) difference between hands</label>
                        <div className="hine-counter">
                            <button type="button" onClick={() => handleHaiDecrement(0, "asymmetryIndex")} aria-label="Decrease Asymmetry percentage">-</button>
                            <input
                                type="text"
                                id="hai-asymmetry-0"
                                value={haiData[0]?.asymmetryIndex ?? ""}
                                onChange={e => handleHaiChange(0, "asymmetryIndex", e.target.value)}
                                onFocus={e => clearZeroOnFocusHai(e, 0, "asymmetryIndex")}
                                onBlur={e => restoreZeroOnBlurHai(e, 0, "asymmetryIndex")}
                            inputMode="decimal"
                            />
                            <button type="button" onClick={() => handleHaiIncrement(0, "asymmetryIndex")} aria-label="Increase Asymmetry percentage">+</button>
                        </div>
                    </div>
                </div>
                <div className="hine-sub">
                    <p className="hine-subtext">Please provide earlier HAI scores if any</p>
                    <div className="hine-actions">
                        <button type="button" className="add-btn" onClick={addHaiRow} aria-label="Add HAI row">+</button>
                        {haiData.length > 1 && <button type="button" className="remove-btn" onClick={() => removeHaiRow(haiData.length - 1)} aria-label="Remove last HAI row">-</button>}
                    </div>
                </div>
                {/* Dynamic Rows */}
                {haiData.slice(1).map((item, index) => {
                    const rowId = index + 1;
                    return (
                        <div key={item.id || index} className="hine-row-single">
                            <div className="hine-field time-field">
                                <label htmlFor={`hai-timepoint-${rowId}`}>Timepoint</label>
                                <input
                                    type="text"
                                    id={`hai-timepoint-${rowId}`}
                                    placeholder="Enter age in months"
                                    value={item.timepoint || ""}
                                    className="hine-input"
                                    onChange={e => handleHaiChange(rowId, "timepoint", e.target.value)}
                                />
                            </div>
                            <div className="hine-field">
                                <label htmlFor={`hai-right-eahs-${rowId}`}>Each hand score (Right)</label>
                                <div className="hine-counter">
                                    <button type="button" onClick={() => handleHaiDecrement(rowId, "rightEahs")} aria-label="Decrease Each hand score Right">-</button>
                                    <input
                                        type="text"
                                        id={`hai-right-eahs-${rowId}`}
                                        value={item.rightEahs ?? ""}
                                        onChange={e => handleHaiChange(rowId, "rightEahs", e.target.value)}
                                        onFocus={e => clearZeroOnFocusHai(e, rowId, "rightEahs")}
                                        onBlur={e => restoreZeroOnBlurHai(e, rowId, "rightEahs")}
                                    inputMode="decimal"
                                    />
                                    <button type="button" onClick={() => handleHaiIncrement(rowId, "rightEahs")} aria-label="Increase Each hand score Right">+</button>
                                </div>
                            </div>
                            <div className="hine-field">
                                <label htmlFor={`hai-left-eahs-${rowId}`}>Each hand score (Left)</label>
                                <div className="hine-counter">
                                    <button type="button" onClick={() => handleHaiDecrement(rowId, "leftEahs")} aria-label="Decrease Each hand score Left">-</button>
                                    <input
                                        type="text"
                                        id={`hai-left-eahs-${rowId}`}
                                        value={item.leftEahs ?? ""}
                                        onChange={e => handleHaiChange(rowId, "leftEahs", e.target.value)}
                                        onFocus={e => clearZeroOnFocusHai(e, rowId, "leftEahs")}
                                        onBlur={e => restoreZeroOnBlurHai(e, rowId, "leftEahs")}
                                    inputMode="decimal"
                                    />
                                    <button type="button" onClick={() => handleHaiIncrement(rowId, "leftEahs")} aria-label="Increase Each hand score Left">+</button>
                                </div>
                            </div>
                            <div className="hine-field">
                                <label htmlFor={`hai-sum-score-${rowId}`}>Each hand sum score</label>
                                <div className="hine-counter">
                                    <button type="button" onClick={() => handleHaiDecrement(rowId, "eachHandSumScore")} aria-label="Decrease Each hand sum score">-</button>
                                    <input
                                        type="text"
                                        id={`hai-sum-score-${rowId}`}
                                        value={item.eachHandSumScore ?? ""}
                                        onChange={e => handleHaiChange(rowId, "eachHandSumScore", e.target.value)}
                                        onFocus={e => clearZeroOnFocusHai(e, rowId, "eachHandSumScore")}
                                        onBlur={e => restoreZeroOnBlurHai(e, rowId, "eachHandSumScore")}
                                    inputMode="decimal"
                                    />
                                    <button type="button" onClick={() => handleHaiIncrement(rowId, "eachHandSumScore")} aria-label="Increase Each hand sum score">+</button>
                                </div>
                            </div>
                            <div className="hine-field">
                                <label htmlFor={`hai-asymmetry-${rowId}`}>Asymmetry (%) difference between hands</label>
                                <div className="hine-counter">
                                    <button type="button" onClick={() => handleHaiDecrement(rowId, "asymmetryIndex")} aria-label="Decrease Asymmetry percentage">-</button>
                                    <input
                                        type="text"
                                        id={`hai-asymmetry-${rowId}`}
                                        value={item.asymmetryIndex ?? ""}
                                        onChange={e => handleHaiChange(rowId, "asymmetryIndex", e.target.value)}
                                        onFocus={e => clearZeroOnFocusHai(e, rowId, "asymmetryIndex")}
                                        onBlur={e => restoreZeroOnBlurHai(e, rowId, "asymmetryIndex")}
                                    inputMode="decimal"
                                    />
                                    <button type="button" onClick={() => handleHaiIncrement(rowId, "asymmetryIndex")} aria-label="Increase Asymmetry percentage">+</button>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    // ---------------------------------------------------------------------------
    // RENDER CURRENT DYNAMIC QUESTION
    // ---------------------------------------------------------------------------

    const renderCurrentQuestion = () => {
        if (step < 2 || isGoalDetailStep) return null;
        if (step > 1 + visibleSteps.length) return null;

        const currentQ = visibleSteps[step - 2];
        if (!currentQ) return null;

        const prevQ = visibleSteps[step - 3];
        const isNewGroup = !prevQ || prevQ.groupNode !== currentQ.groupNode;
        const demographicsInfoText = "Understanding the infant's family context, supports, environment, and available resources is essential when selecting interventions, as these factors directly influence what is realistic, feasible, and sustainable. Effective intervention planning must integrate best-available evidence with family-centred practice and shared decision-making to ensure the chosen approach fits the infant's goals and everyday life.";
        let groupInfoText = null;
        if (isNewGroup) {
            groupInfoText = currentQ.groupName === "Demographics" ? demographicsInfoText : currentQ.groupInfo;
        }

        const continuousNumber = getContinuousNumber(currentQ);
        const instructions = parseInstructions(currentQ.instruction);
        const { isHINE, isHAI } = getQuestionType(currentQ);
        const isGoalDomain = isGoalDomainQuestion(currentQ);
        const selectedVal = answers[currentQ.node_number];

        let selectedOptionsList = [];
        if (selectedVal && currentQ.options) {
            if (Array.isArray(selectedVal)) {
                selectedOptionsList = currentQ.options.filter(o => selectedVal.includes(o.option_text));
            } else {
                const opt = currentQ.options.find(o => o.option_text === selectedVal);
                if (opt) selectedOptionsList = [opt];
            }
        }

        const hasOptions = currentQ.options && currentQ.options.length > 0;

        // -- For goal domain question: compute current selected count --
        const goalDomainSelectedCount = isGoalDomain
            ? (Array.isArray(selectedVal) ? selectedVal.length : 0)
            : 0;

        return (
            <>
        {/* Group heading carries the appointment type so a Review group is
            never read as the Initial group of the same name. This component
            only ever runs a Review, hence the hard-coded "review". */}
        {isNewGroup && currentQ.groupName && (
          <h2 className="assessment-group-heading">
            {withAppointmentPrefix(currentQ.groupName, "review")}
          </h2>
        )}
        {groupInfoText && (
          <div className="question-cardtop" style={{ borderLeft: "5px solid #288537", marginBottom: "0px", padding: "8px 12px" }}>
            <RichText html={groupInfoText} as="p" style={{ fontWeight: "bold", fontSize: "16px", color: "#288537", margin: 0 }} />
          </div>
        )}
        <div className="question-card">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px" }}>
            <p className="question">{continuousNumber}. <RichText html={currentQ.question_text} /></p>
            <div style={{ flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "center", gap: "2px" }}>
              <span className="previous-data-label" style={{ fontSize: "11px", fontWeight: "bold", color: "#1F7A26", letterSpacing: "0.5px", lineHeight: 1.2 }}>Previous Data</span>
              <button className="group-history-file-btn" onClick={handleHistoryFileClick} title="View previous data" aria-label="View previous data" aria-expanded={showHistoryFiles}>
                <FaFolder aria-hidden="true" />
              </button>
            </div>
          </div>

                    {instructions.isMulti && !isGoalDomain && (
                        <p style={{ fontSize: "16px", marginBottom: "15px", fontWeight: 500, paddingLeft: "3px" }}>
                            Please select more than one option
                        </p>
                    )}

                    {/* -- CHANGE 1b: Show max 2 hint for goal domain question -- */}
                    {/* {isGoalDomain && (
            <p style={{ fontSize: "16px", marginBottom: "15px", fontWeight: 500, paddingLeft: "3px" }}>
              Please select up to 2 options ({goalDomainSelectedCount}/2 selected)
            </p>
          )} */}

                    {hasOptions && (
                        <div className={`radio-group ${instructions.isVertical ? "vertical" : ""} ${isGoalDomain ? "goal-domain-grid" : ""}`}>
                            {currentQ.options.map(opt => {
                                if (isGoalDomain) {
                                    const isSelected = Array.isArray(selectedVal) && selectedVal.includes(opt.option_text);
                                    const isDisabled = !isSelected && goalDomainSelectedCount >= 2;
                                    return (
                                        <label key={opt.option_id} style={{ opacity: isDisabled ? 0.5 : 1 }}>
                                            <input type="checkbox" name={currentQ.node_number} value={opt.option_text} checked={isSelected}
                                                disabled={isDisabled}
                                                onChange={() => {
                                                    const allowed = handleMainOptionSelect(currentQ.node_number, opt, true, true);
                                                    if (!allowed) return;
                                                    let curr = Array.isArray(answers[currentQ.node_number]) ? [...answers[currentQ.node_number]] : [];
                                                    let next = isSelected ? curr.filter(s => s !== opt.option_text) : [...curr, opt.option_text];
                                                    let allIds = currentQ.options.filter(o => next.includes(o.option_text)).map(o => o.option_id);
                                                    saveResponseAPI(currentSessionId, currentQ.node_number, "option", currentQ.question_id, null, allIds.length === 0 ? [] : allIds);
                                                }} /> <RichText html={opt.option_text} />
                                        </label>
                                    );
                                }

                                if (instructions.isMulti) {
                                    const multiVals = Array.isArray(selectedVal)
                                        ? selectedVal
                                        : (selectedVal !== undefined && selectedVal !== null && selectedVal !== ""
                                            ? [selectedVal]
                                            : []);
                                    const isSelected = multiVals.includes(opt.option_text);
                                    return (
                                        <label key={opt.option_id}>
                                            <input type="checkbox" name={currentQ.node_number} value={opt.option_text} checked={isSelected}
                                                onChange={() => {
                                                    handleMainOptionSelect(currentQ.node_number, opt, true);
                                                    let curr = Array.isArray(answers[currentQ.node_number])
                                                        ? [...answers[currentQ.node_number]]
                                                        : (answers[currentQ.node_number] ? [answers[currentQ.node_number]] : []);
                                                    let next = isSelected ? curr.filter(s => s !== opt.option_text) : [...curr, opt.option_text];
                                                    let allIds = currentQ.options.filter(o => next.includes(o.option_text)).map(o => o.option_id);
                                                    saveResponseAPI(currentSessionId, currentQ.node_number, "option", currentQ.question_id, null, allIds.length === 0 ? [] : allIds);
                                                }} /> <RichText html={opt.option_text} />
                                        </label>
                                    );
                                }

                                if (isHINE || isHAI) {
                                    const type = isHINE ? "hine" : "hai";
                                    const isSelected = selectedVal === opt.option_text;
                                    return (
                                        <label key={opt.option_id}>
                                            <input type="radio" name={currentQ.node_number} value={opt.option_text} checked={isSelected}
                                                onChange={() => {
                                                    handleMainOptionSelect(currentQ.node_number, opt, false);
                                                    if (!isSelected) {
                                                        saveResponseAPI(currentSessionId, currentQ.node_number, "option", opt.option_id, opt.option_text);
                                                        saveHineHaiAPI(
                                                            currentSessionId, currentQ.node_number, type, currentQ.question_id, opt.option_text,
                                                            opt.option_text === "Yes"
                                                                ? (isHINE
                                                                    ? hineData.map(r => ({ timepoint: r.timepoint, hine_score: r.hineScore, right_asymmetry: r.rightAsym, left_asymmetry: r.leftAsym }))
                                                                    : haiData.map(r => ({ timepoint: r.timepoint, right_score: r.rightEahs, left_score: r.leftEahs, sum: r.eachHandSumScore, asymmetry: r.asymmetryIndex })))
                                                                : []
                                                        );
                                                    } else {
                                                        saveResponseAPI(currentSessionId, currentQ.node_number, "question", opt.option_id, "");
                                                    }
                                                }} /> <RichText html={opt.option_text} />
                                        </label>
                                    );
                                }

                                const isSelected = selectedVal === opt.option_text;
                                return (
                                    <label key={opt.option_id}>
                                        <input type="radio" name={currentQ.node_number} value={opt.option_text} checked={isSelected}
                                            onChange={() => {
                                                handleMainOptionSelect(currentQ.node_number, opt, false);
                                                if (!isSelected) {
                                                    saveResponseAPI(currentSessionId, currentQ.node_number, "option", opt.option_id, opt.option_text);
                                                } else {
                                                    saveResponseAPI(currentSessionId, currentQ.node_number, "question", opt.option_id, "");
                                                }
                                            }} /> <RichText html={opt.option_text} />
                                    </label>
                                );
                            })}
                        </div>
                    )}

                    {!instructions.isMulti && !isGoalDomain && selectedOptionsList.length > 0 && (
                        <div style={{ marginTop: "15px" }}>
                            {selectedOptionsList.map(selectedOpt => {
                                if (!selectedOpt.sub_options || selectedOpt.sub_options.length === 0) return null;
                                return (
                                    <div key={selectedOpt.option_id}>
                                        {renderSubOptionsChain(currentQ.node_number, [selectedOpt.option_text], selectedOpt.sub_options, selectedOpt.instruction || "")}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {isHINE && renderHINESection(selectedVal)}
                    {isHAI && renderHAISection(selectedVal)}

                    {selectedOptionsList.map(opt => renderAttachments(opt.attachments))}
                    {renderAttachments(currentQ.attachments)}
                </div>
            </>
        );
    };

    // ---------------------------------------------------------------------------
    // LAYOUT SUB-RENDER HELPERS
    // ---------------------------------------------------------------------------
    // Kept as functions inside the component so they can read every state value
    // and handler with no prop drilling. Breaks the large JSX below into small,
    // named pieces without changing any behavior.

    // -- Small primitive shared by every summary row in the left panel --------
    const renderSummaryItem = ({ label, value, num, onClick }) => (
        <li
            key={label}
            style={{ color: "#2a2b2a", position: "relative", zIndex: 1, cursor: "pointer" }}
            onClick={onClick}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
            role="button"
            tabIndex="0"
            aria-label={onClick ? `${label}: ${value || 'not set'}` : `${label}: ${value || 'not set'}`}
        >
            <span className="step-number" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: "24px", height: "24px", background: "#fff", borderRadius: "50%", fontSize: "14px", margin: "5px" }}>{num}</span>
            <div className="content-row" style={{ display: "flex", alignItems: "center", flexWrap: "wrap", minWidth: 0 }}>
                <span className="keyword-text">{label}</span>
                <span style={{ color: "var(--cpa-gray-2, #626A6F)", backgroundColor: "white", border: "none", borderRadius: "0%", fontWeight: "bold", marginLeft: "4px", marginRight: "4px" }} aria-hidden="true">-</span>
                <p style={{ fontWeight: "normal" }}><RichText html={value || ""} /></p>
            </div>
        </li>
    );

    // -- Left-panel "Demographics" block --------------------------------------
    // `connector` renders the trailing green connector for the question steps;
    // `withRef` attaches the active-step scroll ref to the circle.
    const renderDemographicsPanel = ({ connector, withRef }) => (
        <div className="panel-block expanded" tabIndex="0">
            <div className={`circle ${step > 1 ? "done" : ""}`} ref={withRef ? activeStepRef : null}>
                {step > 1 ? <FontAwesomeIcon icon={faCheck} /> : "I"}
            </div>
            <h3 className="section-title" onClick={() => toggleSection("demographics")} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleSection("demographics"); } }} role="button" tabIndex="0" aria-expanded={openSections.demographics}>
                Demographics <span className="arrow">{openSections.demographics ? <FontAwesomeIcon icon={faCaretDown} /> : <FontAwesomeIcon icon={faCaretUp}/>}</span>
            </h3>
            {openSections.demographics && (
                <div style={{ position: "relative" }}>
                    <div style={{ position: "absolute", left: "16px", top: "19px", bottom: "10px", width: "3px", background: "var(--cpa-primary, #247A3D)", zIndex: 0 }} aria-hidden="true"></div>
                    <ul className="sub-list1" tabIndex="0">
                    {[
                        { label: "Appointment", value: session, num: 1 },
                        { label: "Client", value: client, num: 2 },
                        { label: "Age", value: ageRange, num: 3 }
                    ].map(item => renderSummaryItem({ ...item, onClick: () => handleKeywordClick(1) }))}
                    </ul>
                </div>
            )}
            {connector && step > 1 && <div className="connector11 done"></div>}
        </div>
    );

    // -- Left-panel group (II, III, ...) blocks with their summary questions --
    const renderPathwayGroupPanels = () =>
        activePathway?.groups?.map((group, gIdx) => {
            if (group.group_name.toLowerCase() === "demographics") return null;
            const groupQuestions = visibleSteps.filter(q => q.groupNode === group.node_number);
            const firstQIdx = visibleSteps.findIndex(q => q.groupNode === group.node_number);
            const lastQIdx = visibleSteps.findLastIndex(q => q.groupNode === group.node_number);
            const isGroupActive = firstQIdx !== -1 && (step - 2 >= firstQIdx && step - 2 <= lastQIdx);
            const hasGroupAnswers = groupQuestions.some(q => answers[q.node_number]);
            const isGroupDone = lastQIdx !== -1 && (step - 2 > lastQIdx);
            if (!isGroupActive && !hasGroupAnswers) return null;
            const isLastGroup = gIdx === activePathway.groups.length - 1;
            const totalSteps = 1 + visibleSteps.length;
            const isSummaryPageActive = step === totalSteps + 1;
            const romanNumerals = ["II", "III", "IV", "V", "VI", "VII", "VIII", "IX", "X"];
            return (
                <div key={group.node_number} className="panel-block expanded" tabIndex="0">
                    <div className={`circle ${isGroupDone || isSummaryPageActive ? "done" : ""}`} ref={(isGroupDone || isSummaryPageActive) ? activeStepRef : null}>
                        {(isGroupDone || isSummaryPageActive) ? <FontAwesomeIcon icon={faCheck} /> : (romanNumerals[gIdx] || <FontAwesomeIcon icon={faDotCircle} />)}
                    </div>
                    <h3 className="section-title" onClick={() => toggleSection(group.group_name)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleSection(group.group_name); } }} role="button" tabIndex="0" aria-expanded={openSections[group.group_name]}>
                        {group.group_name} <span className="arrow">{openSections[group.group_name] ? <FontAwesomeIcon icon={faCaretDown} /> : <FontAwesomeIcon icon={faCaretUp}/>}</span>
                    </h3>
                    {openSections[group.group_name] && (
                        <div style={{ position: "relative" }}>
                            <div style={{ position: "absolute", left: "16px", top: "29px", bottom: "5px", width: "2.5px", background: "var(--cpa-primary, #247A3D)", zIndex: 0, margin: "5px" }} aria-hidden="true"></div>
                            <ul className="sub-list1">
                            {groupQuestions.map(q => {
                                const dynIdx = visibleSteps.findIndex(dq => dq.node_number === q.node_number);
                                if (dynIdx === -1) return null;
                                const qStep = 2 + dynIdx;
                                const isQActive = step === qStep;
                                const continuousNumber = getContinuousNumber(q);
                                const mainAns = answers[q.node_number];
                                let displayText = getAnswerDisplayText(q);
                                const isAnswered = mainAns !== undefined;
                                const isNavigable = isQActive || isAnswered;
                                return (
                                    <li key={q.node_number} ref={isQActive ? activeStepRef : null}
                                        style={{ color: isQActive ? "#323C43" : (isAnswered ? "#080808" : "var(--cpa-gray-2, #626A6F)"), cursor: isNavigable ? "pointer" : "not-allowed", fontWeight: "bold", position: "relative", zIndex: 1 }}
                                        onClick={isNavigable ? () => handleKeywordClick(qStep) : null}
                                        onKeyDown={isNavigable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleKeywordClick(qStep); } } : null}
                                        tabIndex="0" role="button" aria-label={isNavigable ? `Go to ${getGoalDomainPanelLabel(q) || q.keywords || "question"} ${continuousNumber}` : undefined}>
                                        <span className="step-number" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: "24px", height: "24px", background: "#fbfcfc", borderRadius: "50%", fontSize: "14px", margin: "9px 3px 4px 10px" }}>{continuousNumber}</span>
                                        <div className="content-row" style={{ display: "flex", alignItems: "center", flexWrap: "wrap", minWidth: 0 }} tabIndex="0">
                                            <span className="keyword-text" >{getGoalDomainPanelLabel(q) || q.keywords || "Keywords"}</span>
                                            <span style={{ color: "var(--cpa-gray-2, #626A6F)", backgroundColor: "#fbfcfc", border: "none", fontWeight: "bold", marginLeft: "4px", marginRight: "4px" }} aria-hidden="true">-</span>
                                            <p style={{ fontWeight: "normal" }}><RichText html={displayText || ""} /></p>
                                        </div>
                                    </li>
                                );
                            })}
                            {isLastGroup && !pathwayHasEndInstruction && (
                                <li style={{ color: isSummaryPageActive ? "#323C43" : "var(--cpa-gray-2, #626A6F)", cursor: "pointer", fontWeight: "bold", position: "relative", zIndex: 1, marginTop: "10px" }}
                                    onClick={() => setStep(totalSteps + 1)}
                                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setStep(totalSteps + 1); } }}
                                    role="button" tabIndex="0" aria-label="Go to suggested interventions">
                                    <span className="step-number" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: "24px", height: "24px", background: "#fff", borderRadius: "50%", fontSize: "14px", margin: "10px" }}>{visibleSteps.length + 4}</span>
                                    <div className="content-row" style={{ display: "flex", alignItems: "center", flexWrap: "wrap", minWidth: 0 }}>
                                        <span className="keyword-text">Suggested Interventions</span>
                                        <p style={{ fontWeight: "normal" }}>{isSummaryPageActive ? "    " : ""}</p>
                                    </div>
                                </li>
                            )}
                            </ul>
                        </div>
                    )}
                    {(isGroupDone || isSummaryPageActive) && <div className="connector11 done"></div>}
                </div>
            );
        });

    // -- Left-panel "Infant & caregiver context" block (step 1) ---------------
    const renderInfantContextPanel = () => (
        <div className="final-section">
            <div className={`infant-panel ${showInfantInfo ? "expanded" : ""}`}>
                <button
                    type="button"
                    className="infant-header"
                    onClick={() => {
                        const next = !showInfantInfo;
                        setShowInfantInfo(next);
                        setInfantAnnouncement(
                            next
                                ? "Infant and caregiver context expanded. Understanding the infant, family and their context, including available supports and resources, is essential to selecting realistic, feasible and sustainable interventions. Effective intervention planning must integrate best-available evidence with family-centred practice and shared decision-making to ensure the chosen approach fits the infant's goals and everyday life."
                                : "Infant and caregiver context collapsed."
                        );
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            const next = !showInfantInfo;
                            setShowInfantInfo(next);
                            setInfantAnnouncement(
                                next
                                    ? "Infant and caregiver context expanded. Understanding the infant, family and their context, including available supports and resources, is essential to selecting realistic, feasible and sustainable interventions. Effective intervention planning must integrate best-available evidence with family-centred practice and shared decision-making to ensure the chosen approach fits the infant's goals and everyday life."
                                    : "Infant and caregiver context collapsed."
                            );
                        }
                    }}
                    aria-expanded={showInfantInfo}
                    aria-controls="infant-context-content-review"
                    aria-label={`Infant and caregiver context, ${showInfantInfo ? "expanded" : "collapsed"}. Press Enter or Space to toggle details.`}
                >
                    <span className="infant-info-btn" aria-hidden="true">
                        <FaInfo size={14} aria-hidden="true" />
                    </span>
                    <div className="infant-heading">
                        <span className="heading-line" aria-hidden="true"></span>
                        <span className="heading-text">
                            Infant and
                            <br />
                            caregiver
                            <br />
                            context
                        </span>
                    </div>
                </button>
                {showInfantInfo && (
                    <div
                        id="infant-context-content-review"
                        className="infant-content"
                        role="region"
                        aria-label="Infant and caregiver context details"
                        tabIndex={0}
                    >
                        <p>
                            Understanding the infant, family and their context, including available
                            supports and resources, is essential to selecting realistic, feasible
                            and sustainable interventions. Effective intervention planning must
                            integrate best-available evidence with family-centred practice and
                            shared decision-making to ensure the chosen approach fits the infant's
                            goals and everyday life.
                        </p>
                    </div>
                )}
                <span className="sr-only" role="status" aria-live="polite" aria-atomic="true">
                    {infantAnnouncement}
                </span>
            </div>
        </div>
    );

    // -- Left panel used inside the normal (non-review-goal) branch -----------
    const renderNormalLeftPanel = () => (
        <>
            {step === 1 && renderInfantContextPanel()}
            {step !== 1 && renderDemographicsPanel({ connector: true, withRef: true })}
            {step !== 1 && renderPathwayGroupPanels()}
        </>
    );

    // -- Step 1 basic-details form (client + age group select) ---------------
    const renderStep1Form = () => (
        <>
            <div className="question-card">
                <p className="question">1. Review the existing goal of the client in this session</p>
                <div className="radio-group">
                    {["Initial", "Review"].map((s) => (
                        <label key={s}>
                            <input
                                type="radio"
                                name="session"
                                value={s}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    if (value === "Initial") {
                                        navigate("/start-assessment?appointment=Initial");
                                    } else {
                                        setSession(value);
                                    }
                                }}
                                checked={session === s}
                            />
                            {s}
                        </label>
                    ))}
                </div>
            </div>

            <div className="question-card">
                <p className="question">2. Client - please select infant's client.</p>
                <div className="age-dropdown-container">
                    <label hidden htmlFor="client-select" className="age-label">Client</label>
                    <select
                        id="client-select"
                        name="client"
                        className="age-select-dropdown"
                        value={client}
                        onChange={handleClientSelect}
                        aria-required="true"
                    >
                        <option value="" disabled>
                            {clientAgeOptions.length === 0 ? "Loading clients..." : "Select Client"}
                        </option>
                        {clientAgeOptions.map((opt, idx) => (
                            <option key={idx} value={opt.client_name}>
                                {opt.client_name}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="question-card">
                <p className="question">3. Age group - What is the child's current corrected age?</p>
                <div className="age-dropdown-container">
                    <label hidden htmlFor="age-group-select" className="age-label">Age Group</label>
                    <select
                        id="age-group-select"
                        name="ageGroup"
                        className="age-select-dropdown"
                        value={ageRange}
                        onChange={(e) => setAgeRange(e.target.value)}
                        aria-required="true"
                    >
                        <option value="" disabled>
                            {client ? "Fetching age group..." : "Select a client first"}
                        </option>
                        {ageRange && !pathwaysData.some(p => p.age_group === ageRange) && (
                            <option key="carried-age" value={ageRange}>{ageRange}</option>
                        )}
                        {pathwaysData.map((ageObj, idx) => (
                            <option key={idx} value={ageObj.age_group}>
                                {ageObj.age_group}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
        </>
    );

    // -- Goal-detail step block (domain sub-options) --------------------------
    const renderGoalDetailStep = () => {
        if (!(step >= 2 && isGoalDetailStep && visibleSteps[step - 2])) return null;
        return (
            <div className="question-card">
                <p className="question">Select the specific option from the list that supports the caregiver-led goal. Some domains do not have a list to select from ÃƒÂ¯Ã‚Â¿Ã‚Â½ if so, no further selection is needed for that domain. Your selected domain(s) and option will determine the tailored information provided at the end of the pathway.</p>
                <div style={{ display: "flex", flexDirection: "column", gap: "15px" }}>
                    {visibleSteps[step - 2].options.map(opt => {
                        const isSelected = Array.isArray(answers[visibleSteps[step - 2].node_number]) && answers[visibleSteps[step - 2].node_number].includes(opt.option_text);
                        if (!isSelected) return null;
                        const subOpts = opt.sub_options || [];
                        const domainAnswerKey = `${visibleSteps[step - 2].node_number}_${opt.option_text}_sub`;
                        const currentSelections = answers[domainAnswerKey] || [];
                        return (
                            <div key={opt.option_id} style={{ border: "1px solid #fff", borderRadius: "8px", padding: "15px", backgroundColor: "#fff" }}>
                                <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: isMobile ? "15px" : "0px" }}>
                                    <div style={{ width: isMobile ? "100%" : "30%", fontWeight: "bold", display: "flex", alignItems: "start", gap: "10px" }}>
                                        <label style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: "5px" }}>
                                            <input type="checkbox" checked={isSelected} onChange={() => {
                                                const qNode = visibleSteps[step - 2].node_number;
                                                const allowed = handleMainOptionSelect(qNode, opt, true, true);
                                                if (!allowed) return;
                                                let curr = Array.isArray(answers[qNode]) ? [...answers[qNode]] : [];
                                                let next = isSelected ? curr.filter(s => s !== opt.option_text) : [...curr, opt.option_text];
                                                let allIds = visibleSteps[step - 2].options.filter(o => next.includes(o.option_text)).map(o => o.option_id);
                                                saveResponseAPI(currentSessionId, qNode, "option", visibleSteps[step - 2].question_id, null, allIds.length === 0 ? [] : allIds);
                                            }} /> <RichText html={opt.option_text} />
                                        </label>
                                    </div>
                                    <div style={{ width: isMobile ? "100%" : "70%" }}>
                                        {subOpts.length > 0 && (
                                            <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                                                {subOpts.map(sub => {
                                                    const isSubSelected = currentSelections.includes(sub.option_text);
                                                    return (
                                                        <label key={sub.option_id} style={{ display: "flex", alignItems: "flex-start", gap: "8px", cursor: "pointer" }}>
                                                            <input type="radio" value={sub.option_text} checked={isSubSelected}
                                                                onChange={() => {
                                                                    const qNode = visibleSteps[step - 2].node_number;
                                                                    handleSubOptionSelect(qNode, opt.option_text, sub, false, 1);
                                                                    if (!isSubSelected) saveResponseAPI(currentSessionId, sub.node_number, "option", sub.option_id, sub.option_text);
                                                                    else saveResponseAPI(currentSessionId, sub.node_number, "question", sub.option_id, "");
                                                                }} />
                                                            <span style={{ lineHeight: "1.4" }}><RichText html={sub.option_text} /></span>
                                                        </label>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    // -- Right-content footer navigation bar ----------------------------------
    const renderReviewFooterButtons = () => (
        <div className="footer-buttons">
            <button className="backbutton" onClick={handleBack} disabled={step === 1 && !isGoalDetailStep}><FontAwesomeIcon icon={faArrowLeft} /> Back</button>
            <button className="save-btn" onClick={async () => { await handleSaveDraft(); navigate("/continue-assessment"); }}>Save for Later</button>
            <button className={`footer-btn next-btn ${isFormValid() ? "active" : ""}`} disabled={!isFormValid()} onClick={handleNext}>
                {step === 1 && session === "Review" ? "Next " : "Next "}<FontAwesomeIcon icon={faArrowRight} />
            </button>
        </div>
    );

    // ---------------------------------------------------------------------------
    // MAIN RENDER
    // ---------------------------------------------------------------------------

    return (
        <>
            {isRestoringDraft && (
                <div role="status" aria-live="polite" style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", background: "rgba(255,255,255,0.8)", zIndex: 9999, display: "flex", justifyContent: "center", alignItems: "center", fontSize: "20px", fontWeight: "bold" }}>
                    Restoring Assessment...
                </div>
            )}

            {/* -- Coming from Goalreview "Continue Pathway": don't flash the
                 step-1 basic-details form. Show a loading screen until the
                 auto-advance lands on the first pathway question (the 4th
                 question overall). -- */}
            {skipStep1 && step === 1 && !isRestoringDraft && (
                <div role="status" aria-live="polite" style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", background: "#fff", zIndex: 9998, display: "flex", justifyContent: "center", alignItems: "center", fontSize: "20px", fontWeight: "bold", color: "var(--cpa-primary, #247A3D)" }}>
                    Loading assessment...
                </div>
            )}

            {/* -- PDF Generation Overlay ------------------------------------------- */}
            {isGeneratingPdf && <LoadingOverlay />}

            {/* -- Share Modal ------------------------------------------------------ */}
            {showShareModal && (
                <div
                    role="dialog"
                    aria-modal="true"
                    aria-label="Share assessment summary"
                    style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", background: "rgba(0,0,0,0.45)", zIndex: 10001, display: "flex", justifyContent: "center", alignItems: "center" }}
                    onClick={() => setShowShareModal(false)}
                >
                    <div
                        style={{ background: "#fff", borderRadius: "16px", padding: "32px 28px", minWidth: "300px", boxShadow: "0 8px 40px rgba(0,0,0,0.18)", display: "flex", flexDirection: "column", gap: "16px", alignItems: "stretch" }}
                        onClick={e => e.stopPropagation()}
                    >
                        <h3 style={{ margin: 0, fontSize: "18px", fontWeight: "bold", color: "#222", textAlign: "center" }}>Share Assessment Summary</h3>
                        <p style={{ margin: 0, fontSize: "13px", color: "#666", textAlign: "center" }}>The summary will be converted to PDF and shared via your chosen app.</p>

                        <button
                            onClick={handleShareMail}
                            style={{ display: "flex", alignItems: "center", gap: "14px", padding: "14px 20px", border: "1.5px solid #e0e0e0", borderRadius: "10px", background: "#f7f7f7", cursor: "pointer", fontSize: "15px", fontWeight: "600", color: "#333", transition: "background 0.2s" }}
                            onMouseEnter={e => e.currentTarget.style.background = "#eaf4ea"}
                            onMouseLeave={e => e.currentTarget.style.background = "#f7f7f7"}
                        >
                            <span style={{ fontSize: "26px" }}>??</span>
                            Share via Mail
                        </button>

                        <button
                            onClick={handleShareWhatsApp}
                            style={{ display: "flex", alignItems: "center", gap: "14px", padding: "14px 20px", border: "1.5px solid #e0e0e0", borderRadius: "10px", background: "#f7f7f7", cursor: "pointer", fontSize: "15px", fontWeight: "600", color: "#333", transition: "background 0.2s" }}
                            onMouseEnter={e => e.currentTarget.style.background = "#eaf4ea"}
                            onMouseLeave={e => e.currentTarget.style.background = "#f7f7f7"}
                        >
                            <span style={{ fontSize: "26px" }}>??</span>
                            Share via WhatsApp
                        </button>

                        <button
                            onClick={() => setShowShareModal(false)}
                            style={{ marginTop: "4px", background: "none", border: "none", color: "var(--cpa-gray-2, #626A6F)", cursor: "pointer", fontSize: "14px", textAlign: "center" }}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}

            {/* -- Group History now renders inline inside the right-content
                 area (see "INLINE Group History" above), so the old slide-in
                 overlay has been removed. ----------------------------------- */}

            {/* -- Network Issue Popup ---------------------------------------------- */}
            {networkError && (
                <div
                    role="alert"
                    style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", background: "rgba(0,0,0,0.45)", zIndex: 10002, display: "flex", justifyContent: "center", alignItems: "center" }}
                >
                    <div
                        style={{ background: "#fff", borderRadius: "16px", padding: "32px 28px", minWidth: "300px", maxWidth: "360px", boxShadow: "0 8px 40px rgba(0,0,0,0.18)", textAlign: "center" }}
                    >
                        <h3 style={{ margin: 0, fontSize: "18px", fontWeight: "bold", color: "#c0392b" }}>Network Issue</h3>
                        <p style={{ margin: "12px 0 20px", fontSize: "14px", color: "#555", lineHeight: 1.5 }}>
                            We're having trouble reaching the server. Please check your internet connection and try again.
                        </p>
                        <button
                            onClick={() => setNetworkError(false)}
                            aria-label="Dismiss network error"
                            style={{ padding: "10px 28px", borderRadius: "8px", border: "none", background: "var(--cpa-primary, #247A3D)", color: "#fff", fontWeight: "600", fontSize: "14px", cursor: "pointer" }}
                        >
                            OK
                        </button>
                    </div>
                </div>
            )}

            {showGoalDomainPopup && (
                <div
                    style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", background: "rgba(0,0,0,0.45)", zIndex: 10002, display: "flex", justifyContent: "center", alignItems: "center" }}
                >
                    <div
                        style={{ background: "#fff", borderRadius: "16px", padding: "32px 28px", minWidth: "300px", maxWidth: "360px", boxShadow: "0 8px 40px rgba(0,0,0,0.18)", textAlign: "center" }}
                    >
                        <h3 style={{ margin: 0, fontSize: "18px", fontWeight: "bold", color: "#c0392b" }}>Selection Not Allowed</h3>
                        <p style={{ margin: "12px 0 20px", fontSize: "14px", color: "#555", lineHeight: 1.5 }}>
                            Eating and Drinking domain can only be selected as a single choice.
                        </p>
                        <button
                            onClick={() => setShowGoalDomainPopup(false)}
                            aria-label="Close selection not allowed popup"
                            style={{ padding: "10px 28px", borderRadius: "8px", border: "none", background: "var(--cpa-primary, #247A3D)", color: "#fff", fontWeight: "600", fontSize: "14px", cursor: "pointer" }}
                        >
                            OK
                        </button>
                    </div>
                </div>
            )}

            <div className="assessment1">
                <div className="header-bar1">
                    <img src={Frame} alt="" className="frame-icon" onClick={() => setShowLeftPanel(prev => !prev)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setShowLeftPanel(prev => !prev); } }} role="button" tabIndex="0" aria-label="Toggle navigation panel" aria-expanded={showLeftPanel} />
                    <h1>
                        <span className="sr-only">Review Assessment: </span>
                        Background and Assessment Information
                    </h1>
                    <div className="header-actions">
                        <button className="header-btn restart-btn" onClick={handleRestart} aria-label="Restart Assessment">
                            <img src={restart} alt="" aria-hidden="true" className="header-icon" />
                            <h4 aria-hidden="true">Restart Assessment</h4>
                        </button>
                        {(step >= 2 || showReviewGoalScreen) && (
                            <button
                                className="header-btn fav-btn"
                                onClick={handleToggleFavourite}
                                aria-label={isFavourite ? "Remove from favourites" : "Add to favourites"}
                                aria-pressed={isFavourite}
                            >
                                <FontAwesomeIcon
                                    icon={faStar}
                                    className="star-icon1"
                                    aria-hidden="true"
                                    style={{
                                        color: isFavourite ? "var(--cpa-yellow-bright, #FFDD00)" : "#fff",
                                    }}
                                />
                                <h4 aria-hidden="true">Favourite</h4>
                            </button>
                        )}
                    </div>
                </div>

                <div className="assessment-container">
                    {showReviewGoalScreen ? (
                        <>
                            {/* -- LEFT PANEL (Review / Goal Progress screen) ------------- */}
                            <div className={`left-panel ${showLeftPanel ? "show-panel" : "hide-panel"}`}>
                                {renderDemographicsPanel({ connector: false, withRef: false })}
                            </div>

                            {/* -- RIGHT CONTENT (Review / Goal Progress screen) ---------- */}
                            <div className="right-content">
                                <div className="right-content-over" tabIndex="0" aria-label="Scrollable content area">
                                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "360px", height: "100%", padding: "40px 20px" }}>
                                        <div style={{ background: "#ffffff", border: "1px solid #e0e0e0", borderRadius: "8px", padding: "28px 32px", maxWidth: "760px", boxShadow: "0 1px 4px rgba(0,0,0,0.08)" }}>
                                            <p style={{ color: "#288537", fontWeight: "bold", fontSize: "24px", textAlign: "center", lineHeight: "1.8", margin: 0 }}>
                                                Review pathways use background and assessment information gathered to date, alongside goal progress, to provide tailored guidance.
                                            </p>
                                            <p style={{ color: "#626A6F", fontWeight: "normal", fontSize: "14px", textAlign: "center", lineHeight: "1.8", margin: "18px 0 0" }}>
                                                Please click below to update goal progress.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div className="footer-buttons">
                                    <button className="backbutton" onClick={handleBack}><FontAwesomeIcon icon={faArrowLeft} /> Back</button>
                                    <button
                                        className="footer-btn next-btn active"
                                        onClick={() => navigate(`/goalreview?sessionId=${currentSessionId || sessionId}`)}
                                    >
                                        Goal Progress Update
                                    </button>
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                            {/* -- LEFT PANEL ----------------------------------------------- */}
                            <div className={`left-panel ${showLeftPanel ? "show-panel" : "hide-panel"}`} style={isHistoryPaneOpen ? { display: "none" } : undefined}>
                                {renderNormalLeftPanel()}
                            </div>

                            {/* -- RIGHT CONTENT --------------------------------------------- */}
                            <div className="right-content" style={isHistoryPaneOpen ? { width: "100%" } : undefined}>
                                <div className="right-content-over" tabIndex="0" aria-label="Scrollable content area">
                                    {/* -- TOP: Previous Page + Client History + Download PDF row --
                                        Rendered for the WHOLE view, not just for an open group,
                                        so an open-but-empty Previous Data always has a way out. */}
                                    {isHistoryPaneOpen && (
                                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px", background: "#fff", border: "1px solid #ececec", borderRadius: "8px", padding: "5px 18px", margin: "5px 0", flexWrap: "wrap" }}>
                                            {/* CONDITION: only once a group is open - with just the
                                                group bar there is no previous page inside Previous
                                                Data, and "X" is the way out of the view. */}
                                            {historyGroupName && (
                                                <button className="backbutton" onClick={handleHistoryBack} style={{ marginTop: 0, marginBottom: 0 }}><FontAwesomeIcon icon={faArrowLeft} /> Previous Page</button>
                                            )}
                                            <div style={{ textAlign: "center", flex: "1", minWidth: "0", margin: "0 8px" }}>
                                                <h3 style={{ margin: 0, fontSize: "clamp(16px, 4vw, 21px)", fontWeight: "bold", color: "#1F7A26" }}>
                                                    {historyGroupName ? "Client History" : "Previous Data"}
                                                </h3>
                                                {groupHistory?.client_name && (
                                                    <p style={{ margin: "4px 0 0", fontSize: "clamp(13px, 3vw, 15px)", color: "#333", wordBreak: "break-word" }}>
                                                        <span style={{ fontWeight: "bold" }}>{groupHistory.client_name}</span>
                                                        {groupHistory?.client_code ? ` - ${groupHistory.client_code}` : ""}
                                                    </p>
                                                )}
                                            </div>
                                            <div style={{ display: "flex", alignItems: "center", gap: "8px", flexShrink: 0 }}>
                                                {/* CONDITION: the PDF is built from the open group's
                                                    tables, so it is only offered once a group with
                                                    data is on screen. */}
                                                {historyGroupName && (
                                                    <button
                                                        className="group-history-download-btn"
                                                        onClick={handleDownloadGroupData}
                                                        disabled={isDownloadingGroupData || !groupHistory || !groupHistory.data?.length}
                                                        title="Download  PDF"
                                                        aria-label="Download  PDF"
                                                        style={{ background: "#fff", border: "1.5px solid #1F7A26", color: "#1F7A26", fontFamily: "inherit", fontSize: "14px", fontWeight: "bold", padding: "8px 14px", borderRadius: "6px", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "8px", flexShrink: 0 }}
                                                    >
                                                        <FontAwesomeIcon icon={faDownload} aria-hidden="true" />
                                                        {isDownloadingGroupData ? "Generating..." : "Download as PDF"}
                                                    </button>
                                                )}
                                                {/* CONDITION: "X" closes the whole Previous Data
                                                    view, so it is always available while the
                                                    view is open. */}
                                                <button
                                                    onClick={handleCloseHistory}
                                                    aria-label="Close history view"
                                                    title="Close Previous Data"
                                                    style={{ background: "none", border: "none", cursor: "pointer", color: "#666", fontSize: "18px" }}
                                                >
                                                     <FaTimes aria-hidden="true" />
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    {/* -- Group names (files) bar -- */}
                                    {showHistoryFiles && (
                                        <div className="previous-data-wrapper" style={{ marginTop: 0 }}>
                                            <div className="previous-steps">
                                                {orderedHistoryGroups.map((group, gIdx) => {
                                                    const isHistoryOpen = historyGroupName === group.group_name;
                                                    const stepColors = ["active-purple", "active-gray", "active-orange", "active-red", "active-blue"];
                                                    return (
                                                        <div
                                                            key={group.node_number || gIdx}
                                                            className={`step-item ${stepColors[gIdx % stepColors.length] || "active-purple"}`}
                                                            role="button"
                                                            tabIndex={0}
                                                            aria-pressed={isHistoryOpen}
                                                            style={{ cursor: "pointer", borderRadius: "8px", border: isHistoryOpen ? "1px solid var(--cpa-primary, #247A3D)" : "1px solid transparent", background: isHistoryOpen ? "#f0f0f0" : "transparent", transition: "all 0.2s ease" }}
                                                            onClick={() => handleGroupHistoryClick(group.group_name)}
                                                            onKeyDown={(e) => {
                                                                if (e.key === "Enter" || e.key === " ") {
                                                                    e.preventDefault();
                                                                    handleGroupHistoryClick(group.group_name);
                                                                }
                                                            }}
                                                            title={`View previous data for ${group.group_name}`}
                                                        >
                                                            <FaFileAlt className="step-icon" aria-hidden="true" />
                                                            <span style={{ color: isHistoryOpen ? "#1F7A26" : "#111" }}>{group.group_name}</span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}

                                    {/* -- INLINE Group History (shown in the right content) -- */}
                                    {historyGroupName && (
                                        <div ref={historyViewRef} className="group-history-inline" style={{ background: "#fff", borderTop: "1px solid #ececec", padding: "4px 18px 18px", minHeight: "300px" }}>
                                            <div style={{ paddingTop: "14px" }}>
                                                {loadingGroupHistory && (
                                                    <p style={{ color: "var(--cpa-gray-2, #626A6F)", fontSize: "14px", textAlign: "center", padding: "40px 0" }} role="status" aria-live="polite">Loading previous answers...</p>
                                                )}

                                                {!loadingGroupHistory && groupHistory && Array.isArray(groupHistory.data) && groupHistory.data.length === 0 && historyGroupName === "Demographics" && (
                                                    <div style={{ padding: "12px 16px" }}>
                                                        <p style={{ margin: "0 0 10px", fontSize: "clamp(13px, 3.5vw, 15px)", color: "#323C43", wordBreak: "break-word" }}>
                                                            <span style={{ fontWeight: 700 }}>Appointment</span>
                                                            <span> - {groupHistory.appointment_type ? (groupHistory.appointment_type.charAt(0).toUpperCase() + groupHistory.appointment_type.slice(1)) : "-"}</span>
                                                        </p>
                                                        <p style={{ margin: "0 0 10px", fontSize: "clamp(13px, 3.5vw, 15px)", color: "#323C43", wordBreak: "break-word" }}>
                                                            <span style={{ fontWeight: 700 }}>Client</span>
                                                            <span> - {groupHistory.client_name || "-"}</span>
                                                        </p>
                                                        <p style={{ margin: "0 0 10px", fontSize: "clamp(13px, 3.5vw, 15px)", color: "#323C43", wordBreak: "break-word" }}>
                                                            <span style={{ fontWeight: 700 }}>Client ID</span>
                                                            <span> - {groupHistory?.client_id || selectedClientId || urlClientId || "-"}</span>
                                                        </p>
                                                        <p style={{ margin: "0 0 0px", fontSize: "clamp(13px, 3.5vw, 15px)", color: "#323C43", wordBreak: "break-word" }}>
                                                            <span style={{ fontWeight: 700 }}>Age group</span>
                                                            <span> - {groupHistory.age_group || "-"}</span>
                                                        </p>
                                                    </div>
                                                )}
                                                {!loadingGroupHistory && groupHistory && Array.isArray(groupHistory.data) && groupHistory.data.length === 0 && historyGroupName !== "Demographics" && (
                                                    <p style={{ color: "var(--cpa-gray-2, #626A6F)", fontSize: "14px", textAlign: "center", padding: "40px 0" }}>No previous data found for this group.</p>
                                                )}

{!loadingGroupHistory && groupHistory && Array.isArray(groupHistory.data) && groupHistory.data.length > 0 && (() => {
                                                    const sessions = [...groupHistory.data].sort((a, b) => new Date(a.assessment_date) - new Date(b.assessment_date));
                                                    const shades = ["#A0C7E8", "#BED8EE", "#D3E4F5", "#E7F0FA"];
                                                    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
                                                    const ordinal = (n) => {
                                                        const s = ["th", "st", "nd", "rd"];
                                                        const v = n % 100;
                                                        return n + (s[(v - 20) % 10] || s[v] || s[0]);
                                                    };
                                                    const formatDate = (ds) => {
                                                        if (!ds) return "-";
                                                        const d = new Date(ds);
                                                        if (isNaN(d.getTime())) return ds;
                                                        return `${ordinal(d.getDate())} ${months[d.getMonth()]} ${d.getFullYear()}`;
                                                    };
                                                    let reviewCount = 0;
                                                    const labeled = sessions.map((s, i) => {
                                                        const isReview = (s.appointment_type || s.type || "").toLowerCase() !== "initial";
                                                        if (isReview) reviewCount += 1;
                                                        return { session: s, label: isReview ? `Review ${reviewCount}` : "Initial", date: formatDate(s.assessment_date) };
                                                    });
                                                    const colMeta = labeled.reverse().map((c, i) => ({
                                                        ...c,
                                                        shade: shades[i % shades.length]
                                                    }));
                                                    const rowHeadStyle = { background: "#0063A5", color: "#fff", fontWeight: "bold", textAlign: "left", padding: "8px 10px", verticalAlign: "top", whiteSpace: "nowrap" };
                                                    const renderTable = (title, rows) => (
                                                        <div style={{ marginBottom: "24px", overflowX: "auto", WebkitOverflowScrolling: "touch", borderRadius: "8px", border: "1px solid #d9e2ec" }}>
                                                            <table style={{ borderCollapse: "collapse", fontSize: "13px", minWidth: "max-content", width: "auto", background: "#fff" }}>
                                                                <thead>
                                                                    <tr>
                                                                        <th scope="colgroup" colSpan={colMeta.length + 1} style={{ background: "#0063A5", color: "#fff", textAlign: "center", padding: "10px 12px", fontWeight: "bold", fontSize: "14px", border: "1px solid #d9e2ec" }}>{title}</th>
                                                                    </tr>
                                                                    <tr>
                                                                        <th scope="col" style={{ background: "#0063A5", color: "#fff", textAlign: "left", padding: "8px 10px", fontWeight: "bold", whiteSpace: "nowrap", border: "1px solid #d9e2ec" }}>Assessment date</th>
                                                                        {colMeta.map((c, ci) => (
                                                                            <th key={ci} scope="col" style={{ background: c.shade, color: "#1A1A1A", textAlign: "center", padding: "8px 10px", whiteSpace: "nowrap", border: "1px solid #d9e2ec" }}>
                                                                                <div>{c.date}</div>
                                                                                <div style={{ fontSize: "11px", opacity: 0.85, fontWeight: "normal" }}>[{c.label}]</div>
                                                                            </th>
                                                                        ))}
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {rows.map((row, r) => (
                                                                        <tr key={r}>
                                                                            <td role="rowheader" style={{ ...rowHeadStyle, border: "1px solid #d9e2ec" }}>{row.label}</td>
                                                                            {colMeta.map((c, ci) => (
                                                                                <td key={ci} style={{ background: c.shade, color: "#1A1A1A", verticalAlign: "top", wordBreak: "break-word", padding: "8px 10px", border: "1px solid #d9e2ec" }}>{row.cell(c.session) || "-"}</td>
                                                                            ))}
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    );

                                                    const joinBracketed = (vals) => vals.filter(v => v !== "" && v !== null && v !== undefined).map((v, i) => i === 0 ? v : `(${v})`).join(", ") || "-";
                                                    const sessionQ = (s, type) => (Array.isArray(s.pathway) ? s.pathway : []).find(x => (x.type || "").trim().toUpperCase() === type);
                                                    const anyQ = (type) => sessions.some(s => (Array.isArray(s.pathway) ? s.pathway : []).some(x => (x.type || "").trim().toUpperCase() === type));

                                                    const tables = [];

                                                    if (historyGroupName === "Demographics") {
                                                        tables.push({
                                                            title: "Demographics",
                                                            rows: [
                                                                { label: "Appointment", cell: s => s.appointment_type ? (s.appointment_type.charAt(0).toUpperCase() + s.appointment_type.slice(1)) : "-" },
                                                                { label: "Client", cell: s => s.client_name || groupHistory.client_name || "-" },
                                                                { label: "Client ID", cell: () => groupHistory?.client_id || selectedClientId || urlClientId || "-" },
                                                                { label: "Age Group", cell: s => s.age_group || groupHistory.age_group || "-" }
                                                            ]
                                                        });
                                                    } else {
                                                        const seen = new Set();
                                                        const qRows = [];
                                                        sessions.forEach(s => {
                                                            (Array.isArray(s.pathway) ? s.pathway : []).forEach(q => {
                                                                const type = (q.type || "").trim().toUpperCase();
                                                                if (type === "HINE" || type === "HAI") return;
                                                                const rawKeyword = q.keyword || q.question || "";
                                                                if (!rawKeyword) return;
                                                                const kLower = rawKeyword.toLowerCase().trim();
                                                                if (kLower.includes("hine") || kLower.includes("hai")) return;
                                                                const keyword = (kLower === "neuroimaging results" || kLower === "neuroimaging") ? "Neuroimaging" : rawKeyword;
                                                                if (seen.has(keyword)) return;
                                                                seen.add(keyword);
                                                                qRows.push({
                                                                    label: keyword,
                                                                    cell: (ss) => {
                                                                        const q2 = (Array.isArray(ss.pathway) ? ss.pathway : []).find((x) => {
                                                                            const xRaw = x.keyword || x.question || "";
                                                                            const xLower = xRaw.toLowerCase().trim();
                                                                            const xKw = (xLower === "neuroimaging results" || xLower === "neuroimaging") ? "Neuroimaging" : xRaw;
                                                                            return xKw === keyword;
                                                                        });
                                                                        return q2 ? formatHistoryAnswer(q2.answer) : "-";
                                                                    }
                                                                });
                                                            });
                                                        });
                                                        if (qRows.length > 0) tables.push({ title: historyGroupName, rows: qRows });
                                                    }

                                                    if (anyQ("HINE")) {
                                                        tables.push({
                                                            title: "HINE",
                                                            rows: [
                                                                { label: "Status", cell: s => (sessionQ(s, "HINE")?.answer?.value) ? String(sessionQ(s, "HINE").answer.value) : "-" },
                                                                { label: "Time Point", cell: s => joinBracketed((sessionQ(s, "HINE")?.answer?.entries || []).map(e => e.timepoint)) },
                                                                { label: "HINE Score", cell: s => joinBracketed((sessionQ(s, "HINE")?.answer?.entries || []).map(e => e.hine_score)) },
                                                                { label: "Right Asym.", cell: s => joinBracketed((sessionQ(s, "HINE")?.answer?.entries || []).map(e => e.right_asymmetry)) },
                                                                { label: "Left Asym.", cell: s => joinBracketed((sessionQ(s, "HINE")?.answer?.entries || []).map(e => e.left_asymmetry)) }
                                                            ]
                                                        });
                                                    }

                                                    if (anyQ("HAI")) {
                                                        tables.push({
                                                            title: "HAI",
                                                            rows: [
                                                                { label: "Status", cell: s => (sessionQ(s, "HAI")?.answer?.value) ? String(sessionQ(s, "HAI").answer.value) : "-" },
                                                                { label: "Time Point", cell: s => joinBracketed((sessionQ(s, "HAI")?.answer?.entries || []).map(e => e.timepoint)) },
                                                                { label: "Right Score", cell: s => joinBracketed((sessionQ(s, "HAI")?.answer?.entries || []).map(e => e.right_score)) },
                                                                { label: "Left Score", cell: s => joinBracketed((sessionQ(s, "HAI")?.answer?.entries || []).map(e => e.left_score)) },
                                                                { label: "Sum (EAHS)", cell: s => joinBracketed((sessionQ(s, "HAI")?.answer?.entries || []).map(e => e.sum)) },
                                                                { label: "Asymmetry", cell: s => joinBracketed((sessionQ(s, "HAI")?.answer?.entries || []).map(e => e.asymmetry)) }
                                                            ]
                                                        });
                                                    }

                                                    if (tables.length === 0) {
                                                        return (
                                                            <p style={{ color: "var(--cpa-gray-2, #626A6F)", fontSize: "14px", textAlign: "center", padding: "40px 0" }}>No previous data found for this group.</p>
                                                        );
                                                    }

                                                    return tables.map((t, ti) => <div key={ti}>{renderTable(t.title, t.rows)}</div>);
                                                })()}
                                            </div>
                                        </div>
                                    )}

                                    {/* The questionnaire is hidden for the whole Previous Data
                                        view, not just for an open group: the group bar alone
                                        already replaces the question on screen. */}
                                    {!isHistoryPaneOpen && (
                                        <>
                                            {step === 1 && renderStep1Form()}

                                            {renderCurrentQuestion()}

                                            {step >= 2 && isGoalDetailStep && visibleSteps[step - 2] && renderGoalDetailStep()}

                                            {step === (1 + visibleSteps.length + 1) && !isGoalDetailStep && (
                                                <>
                                                    <div className="summ-container1" ref={summaryRef}>
                                                        <div className="sum-cont1">

                                                            {/* Section 1 - Row 1: Client name (centered, cpa color) */}
                                                            <div className="summ-client-row">
                                                                {interventionsData?.session?.client_name || client}
                                                            </div>

                                                            {/* Section 1 - Row 2: Metadata pills */}
                                                            <div className="summ-meta-row">
                                                                <span className="summ-meta-pill summ-meta-plain">
                                                                    <span className="summ-meta-label" style={{ color: "#247A33", fontSize: "16px" }}>Assessment Date:</span>&nbsp;
                                                                    <span className="summ-meta-value" style={{ color: "#247A33" }}>
                                                                        {(() => {
                                                                            const code = String(interventionsData?.session?.client_code || "");
                                                                            const digits = code.replace(/\D/g, "").slice(0, 8);
                                                                            if (digits.length === 8) {
                                                                                const dd = digits.slice(0, 2);
                                                                                const mm = digits.slice(2, 4);
                                                                                const yyyy = digits.slice(4, 8);
                                                                                const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
                                                                                const mIdx = parseInt(mm, 10) - 1;
                                                                                return `${parseInt(dd, 10)} ${months[mIdx]} ${yyyy}`;
                                                                            }
                                                                            return "";
                                                                        })()}
                                                                    </span>
                                                                </span>
                                                                <span className="summ-meta-pill summ-meta-green">
                                                                    <span className="summ-meta-label" style={{ color: "#626A6F" }}>Client ID:</span>&nbsp;
                                                                    <span className="summ-meta-value" style={{ color: "#247A33" }}>{interventionsData?.session?.client_code || ""}</span>
                                                                </span>
                                                                <span className="summ-meta-pill summ-meta-green">
                                                                    <span className="summ-meta-label" style={{ color: "#626A6F" }}>Age Group:</span>&nbsp;
                                                                    <span className="summ-meta-value" style={{ color: "#247A33" }}>{interventionsData?.session?.age_group || ageRange}</span>
                                                                </span>
                                                            </div>

                                                            {/* Section 1 - Row 3: Section title */}
                                                            <h3 className="summ-section-title">Interpreting Medical History and Combined Assessment Findings</h3>

                                                            {/* Section 1 - Row 4: Topography + Severity */}
                                                            <div className="summ-findings-block">
                                                                <div className="summ-finding-row">
                                                                    <span className="icon-circle1"><FontAwesomeIcon icon={faLocationDot} aria-hidden="true" /></span>
                                                                    <strong className="summ-finding-label">Topography:&nbsp;</strong>
                                                                    <span className="summ-value-pill">
                                                                        {(() => {
                                                                            const q = visibleSteps.find(v => v.question_text.toLowerCase().includes("topography"));
                                                                            if (!q) return "";
                                                                            const ans = answers[q.node_number];
                                                                            if (!ans) return "";
                                                                            const ansArray = Array.isArray(ans) ? ans : [ans];
                                                                            if (ansArray.length === 1) {
                                                                                const selectedOpt = q.options?.find(o => o.option_text === ansArray[0]);
                                                                                if (selectedOpt && selectedOpt.sub_options && selectedOpt.sub_options.length > 0) {
                                                                                    const subAns = answers[`${q.node_number}_${ansArray[0]}_sub`];
                                                                                    if (subAns) return Array.isArray(subAns) ? subAns.join(", ") : subAns;
                                                                                }
                                                                                return ansArray[0];
                                                                            }
                                                                            return ansArray.join(", ");
                                                                        })()}
                                                                    </span>
                                                                </div>

                                                                <div className="summ-finding-row">
                                                                    <span className="icon-circle1"><FontAwesomeIcon icon={faSliders} aria-hidden="true" /></span>
                                                                    <strong className="summ-finding-label">Severity:&nbsp;</strong>
                                                                    <span className="summ-value-pill">
                                                                        {(() => {
                                                                            const q = visibleSteps.find(v => v.question_text.toLowerCase().includes("severity"));
                                                                            const ans = q ? answers[q.node_number] : "";
                                                                            return Array.isArray(ans) ? ans.join(", ") : (ans || "");
                                                                        })()}
                                                                    </span>
                                                                </div>
                                                            </div>

                                                            {/* Section 1 - Row 5: Early indicators of comorbidities */}
                                                            <div className="summ-finding-row">
                                                                <span className="icon-circle1"><FontAwesomeIcon icon={faCircleExclamation} aria-hidden="true" /></span>
                                                                <strong className="summ-finding-label">Early indicators of comorbidities:&nbsp;</strong>
                                                                <span className="summ-value-pill">
                                                                    {(() => {
                                                                        const comorbidityGroup = activePathway?.groups?.find(g => g.group_name.toLowerCase().includes("comorbidities"));
                                                                        if (!comorbidityGroup) return "";
                                                                        let keywordNames = [];
                                                                        (comorbidityGroup.questions || []).forEach(q => {
                                                                            const ans = answers[q.node_number];
                                                                            if (ans && ans.toString().toLowerCase() === "yes") keywordNames.push(q.keywords || q.question_text);
                                                                        });
                                                                        return keywordNames.length > 0 ? keywordNames.join(", ") : "";
                                                                    })()}
                                                                </span>
                                                            </div>

                                                            {/* Section 1 - Row 5.5: Goal Limiting Factors */}
                                                            <div className="summ-finding-row">
                                                                <span className="icon-circle1"><FontAwesomeIcon icon={faBullseye} aria-hidden="true" /></span>
                                                                <strong className="summ-finding-label">Goal Limiting Factors:&nbsp;</strong>
                                                                <span className="summ-value-pill">
                                                                    {(() => {
                                                                        const factors = interventionsData?.session?.goal_limiting_factors || [];
                                                                        return factors.join(", ");
                                                                    })()}
                                                                </span>
                                                            </div>

                                                            {/* Section 1 - Row 6: Goals */}
                                                            <div className="summ-finding-row">
                                                                <span className="icon-circle1"><FontAwesomeIcon icon={faBullseye} aria-hidden="true" /></span>
                                                                <strong className="summ-finding-label">Goals:&nbsp;</strong>
                                                                <span className="summ-value-pill">
                                                                    {(() => {
                                                                        const map = interventionsData?.data || {};
                                                                        const tabs = Object.keys(map).map((key) => {
                                                                            const items = map[key] || [];
                                                                            const first = items[0] || {};
                                                                            const domain = first.goal_domain || key || "";
                                                                            const skill = first.goal_skill || "";
                                                                            return skill && skill !== "null" ? `${domain} - ${skill}` : domain;
                                                                        });
                                                                        return [...new Set(tabs)].join(", ");
                                                                    })()}
                                                                </span>
                                                            </div>

                                                            {/* Section 2 - Row 1: Info heading */}
                                                            <h4 className="summ-info-header">
                                                                You are about to receive suggested interventions based on your responses throughout this pathway.
                                                            </h4>

                                                            {/* Section 2 - Row 2: Info text */}
                                                            <p className="summ-info-text">
                                                                Review your answers now before confirming
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <div className="summary-footer">
                                                        <button className="backbutton" onClick={handleBack} disabled={step === 1 && !isGoalDetailStep}><FontAwesomeIcon icon={faArrowLeft} /> Back</button>

                                                        <button className=" summ-save-btn" onClick={handleSummaryConfirm}>
                                                            Save and View Intervention
                                                        </button>
                                                    </div>
                                                </>
                                            )}
                                        </>
                                    )}
                                </div>

                                {/* Back / Save for Later / Next act on the live
                                    questionnaire - moving the step or saving a
                                    half-finished review - so none of them are
                                    reachable while the therapist is reading a
                                    past session's answers. Hidden for the whole
                                    Previous Data view (group bar included), not
                                    just for an open group: `showHistoryFiles`
                                    alone still replaces the question with the
                                    history, so the footer has to go with it. The
                                    panel's own "Previous Page" and "X" are the
                                    way in and out. */}
                                {!isHistoryPaneOpen && step !== (1 + visibleSteps.length + 1) && renderReviewFooterButtons()}
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* -- Off-screen printable node for "Download as PDF" (group data). --
                 Captured by html2canvas in handleDownloadGroupData, paginated
                 with paginateCanvasToPdf, and saved as an A4 PDF. Positioned off
                 screen (position: fixed; left: -9999px) so it never disturbs the
                 on-screen layout. ------------------------------------------- */}
            {(() => {
                const printData = buildHistoryTableData();
                if (!printData) return null;
                const navyStyle = { background: "#0063A5", color: "#fff", fontWeight: "bold", padding: "6px 8px", verticalAlign: "top", whiteSpace: "nowrap", border: "1px solid #d9e2ec" };
                const tintRow = ["#A0C7E8", "#BED8EE", "#D3E4F5", "#E7F0FA"];
                return (
                    <div
                        ref={groupHistoryPrintRef}
                        style={{ position: "fixed", left: "-9999px", top: 0, width: "820px", background: "#ffffff", padding: "20px", fontFamily: "Arial, Helvetica, sans-serif", fontSize: "13px", zIndex: -1 }}
                        aria-hidden="true"
                    >
                        <h2 style={{ margin: "0 0 10px", fontSize: "20px", color: "#1F7A26", fontWeight: "bold" }}>{historyGroupName}</h2>
                        <p style={{ margin: "0 0 16px", fontSize: "14px", color: "#1A1A1A" }}>
                            <strong>{groupHistory?.client_name || client || "-"}</strong>
                            {groupHistory?.client_code ? ` - ${groupHistory.client_code}` : ""}
                        </p>
                        {printData.sections.map((section, si) => (
                            <div key={si} style={{ marginBottom: "22px", overflowX: "auto" }}>
                                <table style={{ borderCollapse: "collapse", width: "100%", fontSize: "12px", background: "#fff" }}>
                                    <thead>
                                        <tr>
                                            <th scope="colgroup" colSpan={printData.sessionCount + 1} style={{ ...navyStyle, textAlign: "center", fontSize: "13px" }}>{section.title}</th>
                                        </tr>
                                        <tr>
                                            <th scope="col" style={{ ...navyStyle, textAlign: "left" }}>Assessment date</th>
                                            {printData.labels.map((l, li) => (
                                                <th key={li} scope="col" style={{ background: tintRow[li % tintRow.length], color: "#1A1A1A", textAlign: "center", padding: "6px 8px", whiteSpace: "nowrap", border: "1px solid #d9e2ec" }}>
                                                    <div>{l.date}</div>
                                                    <div style={{ fontSize: "10px", opacity: 0.85, fontWeight: "normal" }}>[{l.label}]</div>
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {section.rows.map((row, ri) => (
                                            <tr key={ri}>
                                                <td role="rowheader" style={{ ...navyStyle, textAlign: "left" }}>{row.label}</td>
                                                {printData.labels.map((c, ci) => (
                                                    <td key={ci} style={{ background: tintRow[ci % tintRow.length], color: "#1A1A1A", verticalAlign: "top", wordBreak: "break-word", padding: "6px 8px", border: "1px solid #d9e2ec" }}>{row.cell(c.session) || "-"}</td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ))}
                    </div>
                );
            })()}

            {toast && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={() => setToast(null)}
                />
            )}
        </>
    );
}

